// config-class.ts
//
// Purpose: Config class with all static configuration data, sample loading, and Config-dependent utilities
//
// This module:
// - Defines the Config class with musical scales, keys, rhythms, waveform presets, and effect data
// - Provides Config-dependent utility functions (getDrumWave, drawNoiseSpectrum, getPulseWidthRatio, etc.)
// - Provides waveform math utilities (centerWave, centerAndNormalizeWave, performIntegral)

/*!
Copyright (c) 2012-2022 John Nesky and contributing authors

Permission is hereby granted, free of charge, to any person obtaining a copy of
this software and associated documentation files (the "Software"), to deal in
the Software without restriction, including without limitation the rights to
use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies
of the Software, and to permit persons to whom the Software is furnished to do
so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

import { EffectType, EnvelopeComputeIndex, EnvelopeType, GranularEnvelopeType } from "./enums";
import { InstrumentType } from "./instrument-registry";
import type {
	Algorithm,
	AutomationTarget,
	ChipNoise,
	ChipWave,
	Chord,
	Dictionary,
	DictionaryArray,
	Envelope,
	Feedback,
	Key,
	Modulator,
	OperatorFrequency,
	OperatorWave,
	Rhythm,
	Scale,
	Transition,
	Unison,
	Vibrato,
	VibratoType,
} from "./types";
import { centerAndNormalizeWave, centerWave, rawChipToIntegrated, toNameMap } from "./utils";
export class Config {
	// Params for post-processing compressor
	public static thresholdVal: number = -10;
	public static kneeVal: number = 40;
	public static ratioVal: number = 12;
	public static attackVal: number = 0;
	public static releaseVal: number = 0.25;

	public static willReloadForCustomSamples: boolean = false;

	public static jsonFormat: string = "slarmoosbox";
	// public static thurmboxImportUrl: string = "https://file.garden/ZMQ0Om5nmTe-x2hq/PandoraArchive%20Samples/";

	public static readonly scales: DictionaryArray<Scale> = toNameMap([
		//   C     Db      D     Eb      E      F     F#      G     Ab      A     Bb      B      C
		{
			name: "Free",
			realName: "chromatic",
			flags: [true, true, true, true, true, true, true, true, true, true, true, true],
		}, // Free
		{
			name: "Major",
			realName: "ionian",
			flags: [true, false, true, false, true, true, false, true, false, true, false, true],
		}, // Major
		{
			name: "Minor",
			realName: "aeolian",
			flags: [true, false, true, true, false, true, false, true, true, false, true, false],
		}, // Minor
		{
			name: "Mixolydian",
			realName: "mixolydian",
			flags: [true, false, true, false, true, true, false, true, false, true, true, false],
		}, // Mixolydian
		{
			name: "Lydian",
			realName: "lydian",
			flags: [true, false, true, false, true, false, true, true, false, true, false, true],
		}, // Lydian
		{
			name: "Dorian",
			realName: "dorian",
			flags: [true, false, true, true, false, true, false, true, false, true, true, false],
		}, // Dorian
		{
			name: "Phrygian",
			realName: "phrygian",
			flags: [true, true, false, true, false, true, false, true, true, false, true, false],
		}, // Phrygian
		{
			name: "Locrian",
			realName: "locrian",
			flags: [true, true, false, true, false, true, true, false, true, false, true, false],
		}, // Locrian
		{
			name: "Lydian Dominant",
			realName: "lydian dominant",
			flags: [true, false, true, false, true, false, true, true, false, true, true, false],
		}, // Lydian Dominant
		{
			name: "Phrygian Dominant",
			realName: "phrygian dominant",
			flags: [true, true, false, false, true, true, false, true, true, false, true, false],
		}, // Phrygian Dominant
		{
			name: "Harmonic Major",
			realName: "harmonic major",
			flags: [true, false, true, false, true, true, false, true, true, false, false, true],
		}, // Harmonic Major
		{
			name: "Harmonic Minor",
			realName: "harmonic minor",
			flags: [true, false, true, true, false, true, false, true, true, false, false, true],
		}, // Harmonic Minor
		{
			name: "Melodic Minor",
			realName: "melodic minor",
			flags: [true, false, true, true, false, true, false, true, false, true, false, true],
		}, // Melodic Minor
		{
			name: "Blues",
			realName: "blues major",
			flags: [true, false, true, true, true, false, false, true, false, true, false, false],
		}, // Blues Major
		{
			name: "Blues Minor",
			realName: "blues",
			flags: [true, false, false, true, false, true, true, true, false, false, true, false],
		}, // Blues
		{
			name: "Altered",
			realName: "altered",
			flags: [true, true, false, true, true, false, true, false, true, false, true, false],
		}, // Altered
		{
			name: "Pentatonic Major",
			realName: "major pentatonic",
			flags: [true, false, true, false, true, false, false, true, false, true, false, false],
		}, // Major Pentatonic
		{
			name: "Pentatonic Minor",
			realName: "minor pentatonic",
			flags: [true, false, false, true, false, true, false, true, false, false, true, false],
		}, // Minor Pentatonic
		{
			name: "Whole Tone",
			realName: "whole tone",
			flags: [true, false, true, false, true, false, true, false, true, false, true, false],
		}, // Whole Tone
		{
			name: "Octatonic",
			realName: "octatonic",
			flags: [true, false, true, true, false, true, true, false, true, true, false, true],
		}, // Octatonic
		{
			name: "Hexatonic",
			realName: "hexatonic",
			flags: [true, false, false, true, true, false, false, true, true, false, false, true],
		}, // Hexatonic
		// Legacy scale entries: kept for backward compat with old URL-encoded songs.
		// Removing would break songs whose URL encodes these scale indices.
		{
			name: "No Dabbing (MB)",
			realName: "no dabbing",
			flags: [true, true, false, true, true, true, true, true, true, false, true, false],
		},
		// todbox
		{
			name: "Jacked Toad (TB)",
			realName: "jacked toad",
			flags: [true, false, true, true, false, true, true, true, true, false, true, true],
		},
		{
			name: "Test Scale (TB)",
			realName: "**t",
			flags: [true, true, false, false, false, true, true, false, false, true, true, false],
		},
		// JukeBox
		{
			name: "Test Scale (TB)",
			realName: "**t",
			flags: [true, true, false, false, false, true, true, false, false, true, true, false],
		},

		// crashes, but not because of the lack of a root note
		// { name: "Empty", realName: "empty", flags: [false, false, false, false, false, false, false, false, false, false, false, false] }, // Custom? considering allowing this one to be be completely configurable

		{
			name: "Custom",
			realName: "custom",
			flags: [true, false, false, true, false, false, false, false, false, true, true, false],
		}, // Custom? considering allowing this one to be be completely configurable
	]);
	public static readonly keys: DictionaryArray<Key> = toNameMap([
		{ name: "C", isWhiteKey: true, basePitch: 12 }, // C0 has index 12 on the MIDI scale. C7 is 96, and C9 is 120. C10 is barely in the audible range.
		{ name: "C♯", isWhiteKey: false, basePitch: 13 },
		{ name: "D", isWhiteKey: true, basePitch: 14 },
		{ name: "D♯", isWhiteKey: false, basePitch: 15 },
		{ name: "E", isWhiteKey: true, basePitch: 16 },
		{ name: "F", isWhiteKey: true, basePitch: 17 },
		{ name: "F♯", isWhiteKey: false, basePitch: 18 },
		{ name: "G", isWhiteKey: true, basePitch: 19 },
		{ name: "G♯", isWhiteKey: false, basePitch: 20 },
		{ name: "A", isWhiteKey: true, basePitch: 21 },
		{ name: "A♯", isWhiteKey: false, basePitch: 22 },
		{ name: "B", isWhiteKey: true, basePitch: 23 },
		// { name: "C+", isWhiteKey: false, basePitch: 24 },
		// taken from todbox, called "B#" for some reason lol
		// { name: "G- (actually F#-)", isWhiteKey: false, basePitch: 6 },
		// { name: "C-", isWhiteKey: true, basePitch: 0 },
		// brucebox
		// g- isn't actually g-???
		// { name: "oh no (F-)", isWhiteKey: true, basePitch: 5 },
		// shitbox
	]);
	public static readonly blackKeyNameParents: ReadonlyArray<number> = [
		-1, 1, -1, 1, -1, 1, -1, -1, 1, -1, 1, -1,
	];
	public static readonly tempoMin: number = 1;
	public static readonly tempoMax: number = 2000; // slarmoo 500
	public static readonly octaveMin: number = -8; // slarmoo -2
	public static readonly octaveMax: number = 8; // slarmoo 2
	public static readonly echoDelayRange: number = 24;
	public static readonly echoDelayStepTicks: number = 4;
	public static readonly echoSustainRange: number = 8;
	public static readonly echoShelfHz: number = 4000.0; // The cutoff freq of the shelf filter that is used to decay echoes.
	public static readonly echoShelfGain: number = 2.0 ** -0.5;
	public static readonly reverbShelfHz: number = 8000.0; // The cutoff freq of the shelf filter that is used to decay reverb.
	public static readonly reverbShelfGain: number = 2.0 ** -1.5;
	public static readonly reverbRange: number = 32;
	public static readonly reverbDelayBufferSize: number = 16384;
	public static readonly reverbDelayBufferMask: number = Config.reverbDelayBufferSize - 1;
	public static readonly defaultSampleRate: number = 44100;
	public static readonly minSampleRate: number = 8000;
	public static readonly maxSampleRate: number = 96000;
	public static readonly phaserMixRange: number = 32;
	public static readonly phaserFeedbackRange: number = 32;
	public static readonly phaserFreqRange: number = 32;
	public static readonly phaserMinFreq: number = 8.0;
	public static readonly phaserMaxFreq: number = 20000.0;
	public static readonly phaserMinStages: number = 0;
	public static readonly phaserMaxStages: number = 32;
	public static readonly beatsPerBarMin: number = 1;
	public static readonly beatsPerBarMax: number = 64;
	public static readonly barCountMin: number = 1;
	public static readonly barCountMax: number = 4096; // slarmoo: 1024
	public static readonly instrumentCountMin: number = 1;
	public static readonly layeredInstrumentCountMax: number = 10;
	public static readonly patternInstrumentCountMax: number = 10;
	public static readonly partsPerBeat: number = 24;
	public static readonly ticksPerPart: number = 2;
	public static readonly ticksPerArpeggio: number = 3;
	public static readonly arpeggioPatterns: ReadonlyArray<ReadonlyArray<number>> = [
		[0],
		[0, 1],
		[0, 1, 2, 1],
		[0, 1, 2, 3],
		[0, 1, 2, 3, 4],
		[0, 1, 2, 3, 4, 5],
		[0, 1, 2, 3, 4, 5, 6],
		[0, 1, 2, 3, 4, 5, 6, 7],
	];
	public static readonly rhythms: DictionaryArray<Rhythm> = toNameMap([
		// ÷1, ÷2, ÷16, and ÷48 are taken from DinoBox
		// { name: "÷1", stepsPerBeat: 1, /*ticksPerArpeggio: 3, arpeggioPatterns: [[0], [0, 0, 1, 1], [0, 1, 2, 1]],*/ roundUpThresholds: null},
		// { name: "÷2", stepsPerBeat: 2, /*ticksPerArpeggio: 3, arpeggioPatterns: [[0], [0, 0, 1, 1], [0, 1, 2, 1]],*/ roundUpThresholds: null},
		{
			name: "÷3 (triplets)",
			stepsPerBeat: 3,
			/*ticksPerArpeggio: 4, arpeggioPatterns: [[0], [0, 0, 1, 1], [0, 1, 2, 1], [0, 1, 2, 3]]*/ roundUpThresholds:
				[/*0*/ 5, /*8*/ 12, /*16*/ 18 /*24*/],
		},
		{
			name: "÷4 (standard)",
			stepsPerBeat: 4,
			/*ticksPerArpeggio: 3, arpeggioPatterns: [[0], [0, 0, 1, 1], [0, 1, 2, 1], [0, 1, 2, 3]]*/ roundUpThresholds:
				[/*0*/ 3, /*6*/ 9, /*12*/ 17, /*18*/ 21 /*24*/],
		},
		{
			name: "÷6",
			stepsPerBeat: 6,
			/*ticksPerArpeggio: 4, arpeggioPatterns: [[0], [0, 1], [0, 1, 2, 1], [0, 1, 2, 3]]*/ roundUpThresholds:
				null,
		},
		{
			name: "÷8",
			stepsPerBeat: 8,
			/*ticksPerArpeggio: 3, arpeggioPatterns: [[0], [0, 1], [0, 1, 2, 1], [0, 1, 2, 3]]*/ roundUpThresholds:
				null,
		},
		{
			name: "÷12",
			stepsPerBeat: 12,
			/*ticksPerArpeggio: 3, arpeggioPatterns: [[0], [0, 1], [0, 1, 2, 1]]*/ roundUpThresholds:
				null,
		},
		// { name: "÷16", stepsPerBeat: 16, /*ticksPerArpeggio: 3, arpeggioPatterns: [[0], [0, 1], [0, 1, 2, 1]],*/ roundUpThresholds: null},
		{
			name: "freehand (÷24)",
			stepsPerBeat: 24,
			/*ticksPerArpeggio: 3, arpeggioPatterns: [[0], [0, 1], [0, 1, 2, 1], [0, 1, 2, 3]]*/ roundUpThresholds:
				null,
		},
		// { name: "absolute freedom (÷48)",stepsPerBeat: 48, /*ticksPerArpeggio: 3, arpeggioPatterns: [[0], [0, 1],[0, 1, 2, 1]],*/ roundUpThresholds: null},
	]);

	public static readonly instrumentTypeNames: ReadonlyArray<string> = [
		"chip",
		"FM",
		"noise",
		"spectrum",
		"drumset",
		"harmonics",
		"PWM",
		"Picked String",
		"supersaw",
		"custom chip",
		"mod",
		"FM6op",
	];
	public static readonly instrumentTypeHasSpecialInterval: ReadonlyArray<boolean> = [
		true,
		true,
		false,
		false,
		false,
		true,
		false,
		false,
		false,
		false,
		false,
	];
	public static readonly chipBaseExpression: number = 0.03375; // Doubled by unison feature, but affected by expression adjustments per unison setting and wave shape. Custom chip is multiplied by 0.05 in instrumentState.updateWaves
	public static readonly fmBaseExpression: number = 0.03;
	public static readonly opl3BaseExpression: number = 0.03;
	public static readonly noiseBaseExpression: number = 0.19;
	public static readonly spectrumBaseExpression: number = 0.3; // Spectrum can be in pitch or noise channels, the expression is doubled for noise.
	public static readonly drumsetBaseExpression: number = 0.45; // Drums tend to be loud but brief!
	public static readonly harmonicsBaseExpression: number = 0.025;
	public static readonly pwmBaseExpression: number = 0.04725; // It's actually closer to half of this, the synthesized pulse amplitude range is only .5 to -.5, but also note that the fundamental sine partial amplitude of a square wave is 4/π times the measured square wave amplitude.
	public static readonly supersawBaseExpression: number = 0.061425; // It's actually closer to half of this, the synthesized sawtooth amplitude range is only .5 to -.5.
	public static readonly pickedStringBaseExpression: number = 0.025; // Same as harmonics.
	public static readonly distortionBaseVolume: number = 0.011; // Distortion is not affected by pitchDamping, which otherwise approximately halves expression for notes around the middle of the range.
	public static readonly bitcrusherBaseVolume: number = 0.01; // Also not affected by pitchDamping, used when bit crushing is maxed out (aka "1-bit" output).
	public static readonly granularOutputLoudnessCompensation: number = 0.5; // compensate for multiple grains playing at once
	public static rawChipWaves: DictionaryArray<ChipWave> = toNameMap([
		{
			name: "rounded",
			expression: 0.94,
			samples: centerWave([
				0.0, 0.2, 0.4, 0.5, 0.6, 0.7, 0.8, 0.85, 0.9, 0.95, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 0.95, 0.9, 0.85, 0.8, 0.7, 0.6, 0.5,
				0.4, 0.2, 0.0, -0.2, -0.4, -0.5, -0.6, -0.7, -0.8, -0.85, -0.9, -0.95, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -0.95, -0.9, -0.85, -0.8,
				-0.7, -0.6, -0.5, -0.4, -0.2,
			]),
		},
		{
			name: "triangle",
			expression: 1.0,
			samples: centerWave([
				1.0 / 15.0,
				3.0 / 15.0,
				5.0 / 15.0,
				7.0 / 15.0,
				9.0 / 15.0,
				11.0 / 15.0,
				13.0 / 15.0,
				15.0 / 15.0,
				15.0 / 15.0,
				13.0 / 15.0,
				11.0 / 15.0,
				9.0 / 15.0,
				7.0 / 15.0,
				5.0 / 15.0,
				3.0 / 15.0,
				1.0 / 15.0,
				-1.0 / 15.0,
				-3.0 / 15.0,
				-5.0 / 15.0,
				-7.0 / 15.0,
				-9.0 / 15.0,
				-11.0 / 15.0,
				-13.0 / 15.0,
				-15.0 / 15.0,
				-15.0 / 15.0,
				-13.0 / 15.0,
				-11.0 / 15.0,
				-9.0 / 15.0,
				-7.0 / 15.0,
				-5.0 / 15.0,
				-3.0 / 15.0,
				-1.0 / 15.0,
			]),
		},
		{ name: "square", expression: 0.5, samples: centerWave([1.0, -1.0]) },
		{ name: "1/4 pulse", expression: 0.5, samples: centerWave([1.0, -1.0, -1.0, -1.0]) },
		{
			name: "1/8 pulse",
			expression: 0.5,
			samples: centerWave([1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0]),
		},
		{
			name: "sawtooth",
			expression: 0.65,
			samples: centerWave([
				1.0 / 31.0,
				3.0 / 31.0,
				5.0 / 31.0,
				7.0 / 31.0,
				9.0 / 31.0,
				11.0 / 31.0,
				13.0 / 31.0,
				15.0 / 31.0,
				17.0 / 31.0,
				19.0 / 31.0,
				21.0 / 31.0,
				23.0 / 31.0,
				25.0 / 31.0,
				27.0 / 31.0,
				29.0 / 31.0,
				31.0 / 31.0,
				-31.0 / 31.0,
				-29.0 / 31.0,
				-27.0 / 31.0,
				-25.0 / 31.0,
				-23.0 / 31.0,
				-21.0 / 31.0,
				-19.0 / 31.0,
				-17.0 / 31.0,
				-15.0 / 31.0,
				-13.0 / 31.0,
				-11.0 / 31.0,
				-9.0 / 31.0,
				-7.0 / 31.0,
				-5.0 / 31.0,
				-3.0 / 31.0,
				-1.0 / 31.0,
			]),
		},
		{
			name: "double saw",
			expression: 0.5,
			samples: centerWave([
				0.0, -0.2, -0.4, -0.6, -0.8, -1.0, 1.0, -0.8, -0.6, -0.4, -0.2, 1.0, 0.8, 0.6, 0.4,
				0.2,
			]),
		},
		{
			name: "double pulse",
			expression: 0.4,
			samples: centerWave([
				1.0, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0, -1.0, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0, -1.0,
				-1.0,
			]),
		},
		{ name: "spiky", expression: 0.4, samples: centerWave([1.0, -1.0, 1.0, -1.0, 1.0, 0.0]) },
		{
			name: "sine",
			expression: 0.88,
			samples: centerAndNormalizeWave([
				8.0, 9.0, 11.0, 12.0, 13.0, 14.0, 15.0, 15.0, 15.0, 15.0, 14.0, 14.0, 13.0, 11.0,
				10.0, 9.0, 7.0, 6.0, 4.0, 3.0, 2.0, 1.0, 0.0, 0.0, 0.0, 0.0, 1.0, 1.0, 2.0, 4.0,
				5.0, 6.0,
			]),
		},
		{
			name: "flute",
			expression: 0.8,
			samples: centerAndNormalizeWave([
				3.0, 4.0, 6.0, 8.0, 10.0, 11.0, 13.0, 14.0, 15.0, 15.0, 14.0, 13.0, 11.0, 8.0, 5.0,
				3.0,
			]),
		},
		{
			name: "harp",
			expression: 0.8,
			samples: centerAndNormalizeWave([
				0.0, 3.0, 3.0, 3.0, 4.0, 5.0, 5.0, 6.0, 7.0, 8.0, 9.0, 11.0, 11.0, 13.0, 13.0, 15.0,
				15.0, 14.0, 12.0, 11.0, 10.0, 9.0, 8.0, 7.0, 7.0, 5.0, 4.0, 3.0, 2.0, 1.0, 0.0, 0.0,
			]),
		},
		{
			name: "sharp clarinet",
			expression: 0.38,
			samples: centerAndNormalizeWave([
				0.0, 0.0, 0.0, 1.0, 1.0, 8.0, 8.0, 9.0, 9.0, 9.0, 8.0, 8.0, 8.0, 8.0, 8.0, 9.0, 9.0,
				7.0, 9.0, 9.0, 10.0, 4.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
			]),
		},
		{
			name: "soft clarinet",
			expression: 0.45,
			samples: centerAndNormalizeWave([
				0.0, 1.0, 5.0, 8.0, 9.0, 9.0, 9.0, 9.0, 9.0, 9.0, 9.0, 11.0, 11.0, 12.0, 13.0, 12.0,
				10.0, 9.0, 7.0, 6.0, 4.0, 3.0, 3.0, 3.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
			]),
		},
		{
			name: "alto sax",
			expression: 0.3,
			samples: centerAndNormalizeWave([
				5.0, 5.0, 6.0, 4.0, 3.0, 6.0, 8.0, 7.0, 2.0, 1.0, 5.0, 6.0, 5.0, 4.0, 5.0, 7.0, 9.0,
				11.0, 13.0, 14.0, 14.0, 14.0, 14.0, 13.0, 10.0, 8.0, 7.0, 7.0, 4.0, 3.0, 4.0, 2.0,
			]),
		},
		{
			name: "bassoon",
			expression: 0.35,
			samples: centerAndNormalizeWave([
				9.0, 9.0, 7.0, 6.0, 5.0, 4.0, 4.0, 4.0, 4.0, 5.0, 7.0, 8.0, 9.0, 10.0, 11.0, 13.0,
				13.0, 11.0, 10.0, 9.0, 7.0, 6.0, 4.0, 2.0, 1.0, 1.0, 1.0, 2.0, 2.0, 5.0, 11.0, 14.0,
			]),
		},
		{
			name: "trumpet",
			expression: 0.22,
			samples: centerAndNormalizeWave([
				10.0, 11.0, 8.0, 6.0, 5.0, 5.0, 5.0, 6.0, 7.0, 7.0, 7.0, 7.0, 6.0, 6.0, 7.0, 7.0,
				7.0, 7.0, 7.0, 6.0, 6.0, 6.0, 6.0, 6.0, 6.0, 6.0, 6.0, 7.0, 8.0, 9.0, 11.0, 14.0,
			]),
		},
		{
			name: "electric guitar",
			expression: 0.2,
			samples: centerAndNormalizeWave([
				11.0, 12.0, 12.0, 10.0, 6.0, 6.0, 8.0, 0.0, 2.0, 4.0, 8.0, 10.0, 9.0, 10.0, 1.0,
				7.0, 11.0, 3.0, 6.0, 6.0, 8.0, 13.0, 14.0, 2.0, 0.0, 12.0, 8.0, 4.0, 13.0, 11.0,
				10.0, 13.0,
			]),
		},
		{
			name: "organ",
			expression: 0.2,
			samples: centerAndNormalizeWave([
				11.0, 10.0, 12.0, 11.0, 14.0, 7.0, 5.0, 5.0, 12.0, 10.0, 10.0, 9.0, 12.0, 6.0, 4.0,
				5.0, 13.0, 12.0, 12.0, 10.0, 12.0, 5.0, 2.0, 2.0, 8.0, 6.0, 6.0, 5.0, 8.0, 3.0, 2.0,
				1.0,
			]),
		},
		{
			name: "pan flute",
			expression: 0.35,
			samples: centerAndNormalizeWave([
				1.0, 4.0, 7.0, 6.0, 7.0, 9.0, 7.0, 7.0, 11.0, 12.0, 13.0, 15.0, 13.0, 11.0, 11.0,
				12.0, 13.0, 10.0, 7.0, 5.0, 3.0, 6.0, 10.0, 7.0, 3.0, 3.0, 1.0, 0.0, 1.0, 0.0, 1.0,
				0.0,
			]),
		},
		{
			name: "glitch",
			expression: 0.5,
			samples: centerWave([
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, -1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0, -1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0, -1.0, -1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, -1.0, -1.0, -1.0, -1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, -1.0, -1.0, -1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, -1.0, -1.0,
			]),
		},
		{
			name: "trapezoid",
			expression: 1.0,
			samples: centerWave([
				1.0 / 15.0,
				6.0 / 15.0,
				10.0 / 15.0,
				14.0 / 15.0,
				15.0 / 15.0,
				15.0 / 15.0,
				15.0 / 15.0,
				15.0 / 15.0,
				15.0 / 15.0,
				15.0 / 15.0,
				15.0 / 15.0,
				15.0 / 15.0,
				14.0 / 15.0,
				10.0 / 15.0,
				6.0 / 15.0,
				1.0 / 15.0,
				-1.0 / 15.0,
				-6.0 / 15.0,
				-10.0 / 15.0,
				-14.0 / 15.0,
				-15.0 / 15.0,
				-15.0 / 15.0,
				-15.0 / 15.0,
				-15.0 / 15.0,
				-15.0 / 15.0,
				-15.0 / 15.0,
				-15.0 / 15.0,
				-15.0 / 15.0,
				-14.0 / 15.0,
				-10.0 / 15.0,
				-6.0 / 15.0,
				-1.0 / 15.0,
			]),
		},
		// modbox
		{
			name: "modbox 10% pulse",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
			]),
		},
		{
			name: "modbox sunsoft bass",
			expression: 1.0,
			samples: centerAndNormalizeWave([
				0.0, 0.1875, 0.3125, 0.5625, 0.5, 0.75, 0.875, 1.0, 1.0, 0.6875, 0.5, 0.625, 0.625,
				0.5, 0.375, 0.5625, 0.4375, 0.5625, 0.4375, 0.4375, 0.3125, 0.1875, 0.1875, 0.375,
				0.5625, 0.5625, 0.5625, 0.5625, 0.5625, 0.4375, 0.25, 0.0,
			]),
		},
		{
			name: "modbox loud pulse",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				1.0, 0.7, 0.1, 0.1, 0, 0, 0, 0, 0, 0.1, 0.2, 0.15, 0.25, 0.125, 0.215, 0.345, 4.0,
			]),
		},
		{
			name: "modbox sax",
			expression: 0.5,
			samples: centerAndNormalizeWave([1.0 / 15.0, 3.0 / 15.0, 5.0 / 15.0, 9.0, 0.06]),
		},
		{
			name: "modbox guitar",
			expression: 0.5,
			samples: centerAndNormalizeWave([-0.5, 3.5, 3.0, -0.5, -0.25, -1.0]),
		},
		{
			name: "modbox sine",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				0.0, 0.05, 0.125, 0.2, 0.25, 0.3, 0.425, 0.475, 0.525, 0.625, 0.675, 0.725, 0.775,
				0.8, 0.825, 0.875, 0.9, 0.925, 0.95, 0.975, 0.98, 0.99, 0.995, 1, 0.995, 0.99, 0.98,
				0.975, 0.95, 0.925, 0.9, 0.875, 0.825, 0.8, 0.775, 0.725, 0.675, 0.625, 0.525,
				0.475, 0.425, 0.3, 0.25, 0.2, 0.125, 0.05, 0.0, -0.05, -0.125, -0.2, -0.25, -0.3,
				-0.425, -0.475, -0.525, -0.625, -0.675, -0.725, -0.775, -0.8, -0.825, -0.875, -0.9,
				-0.925, -0.95, -0.975, -0.98, -0.99, -0.995, -1, -0.995, -0.99, -0.98, -0.975,
				-0.95, -0.925, -0.9, -0.875, -0.825, -0.8, -0.775, -0.725, -0.675, -0.625, -0.525,
				-0.475, -0.425, -0.3, -0.25, -0.2, -0.125, -0.05,
			]),
		},
		{
			name: "modbox atari bass",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				1.0, 1.0, 1.0, 1.0, 0.0, 1.0, 0.0, 1.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 0.0,
			]),
		},
		{
			name: "modbox atari pulse",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				1.0, 0.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
			]),
		},
		{
			name: "modbox 1% pulse",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0,
			]),
		},
		{
			name: "modbox curved sawtooth",
			expression: 0.5,
			samples: centerAndNormalizeWave([1.0, 1.0 / 2.0, 1.0 / 3.0, 1.0 / 4.0]),
		},
		{
			name: "modbox viola",
			expression: 0.45,
			samples: centerAndNormalizeWave([
				-0.9, -1.0, -0.85, -0.775, -0.7, -0.6, -0.5, -0.4, -0.325, -0.225, -0.2, -0.125,
				-0.1, -0.11, -0.125, -0.15, -0.175, -0.18, -0.2, -0.21, -0.22, -0.21, -0.2, -0.175,
				-0.15, -0.1, -0.5, 0.75, 0.11, 0.175, 0.2, 0.25, 0.26, 0.275, 0.26, 0.25, 0.225,
				0.2, 0.19, 0.18, 0.19, 0.2, 0.21, 0.22, 0.23, 0.24, 0.25, 0.26, 0.275, 0.28, 0.29,
				0.3, 0.29, 0.28, 0.27, 0.26, 0.25, 0.225, 0.2, 0.175, 0.15, 0.1, 0.075, 0.0, -0.01,
				-0.025, 0.025, 0.075, 0.2, 0.3, 0.475, 0.6, 0.75, 0.85, 0.85, 1.0, 0.99, 0.95, 0.8,
				0.675, 0.475, 0.275, 0.01, -0.15, -0.3, -0.475, -0.5, -0.6, -0.71, -0.81, -0.9,
				-1.0, -0.9,
			]),
		},
		{
			name: "modbox brass",
			expression: 0.45,
			samples: centerAndNormalizeWave([
				-1.0, -0.95, -0.975, -0.9, -0.85, -0.8, -0.775, -0.65, -0.6, -0.5, -0.475, -0.35,
				-0.275, -0.2, -0.125, -0.05, 0.0, 0.075, 0.125, 0.15, 0.2, 0.21, 0.225, 0.25, 0.225,
				0.21, 0.2, 0.19, 0.175, 0.125, 0.1, 0.075, 0.06, 0.05, 0.04, 0.025, 0.04, 0.05, 0.1,
				0.15, 0.225, 0.325, 0.425, 0.575, 0.7, 0.85, 0.95, 1.0, 0.9, 0.675, 0.375, 0.2,
				0.275, 0.4, 0.5, 0.55, 0.6, 0.625, 0.65, 0.65, 0.65, 0.65, 0.64, 0.6, 0.55, 0.5,
				0.4, 0.325, 0.25, 0.15, 0.05, -0.05, -0.15, -0.275, -0.35, -0.45, -0.55, -0.65,
				-0.7, -0.78, -0.825, -0.9, -0.925, -0.95, -0.975,
			]),
		},
		{
			name: "modbox acoustic bass",
			expression: 0.5,
			samples: centerAndNormalizeWave([1.0, 0.0, 0.1, -0.1, -0.2, -0.4, -0.3, -1.0]),
		},
		{
			name: "modbox lyre",
			expression: 0.45,
			samples: centerAndNormalizeWave([1.0, -1.0, 4.0, 2.15, 4.13, 5.15, 0.0, -0.05, 1.0]),
		},
		{
			name: "modbox ramp pulse",
			expression: 0.5,
			samples: centerAndNormalizeWave([6.1, -2.9, 1.4, -2.9]),
		},
		{
			name: "modbox piccolo",
			expression: 0.5,
			samples: centerAndNormalizeWave([1, 4, 2, 1, -0.1, -1, -0.12]),
		},
		{
			name: "modbox squaretooth",
			expression: 0.5,
			samples: centerAndNormalizeWave([0.2, 1.0, 2.6, 1.0, 0.0, -2.4]),
		},
		{
			name: "modbox flatline",
			expression: 1.0,
			samples: centerAndNormalizeWave([
				1.0, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1, 0.0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6,
				0.7, 0.8, 0.9,
			]),
		},
		{
			name: "modbox pnryshk a (u5)",
			expression: 0.4,
			samples: centerAndNormalizeWave([
				1.0, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1, 0.0,
			]),
		},
		{
			name: "modbox pnryshk b (riff)",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				1.0, -0.9, 0.8, -0.7, 0.6, -0.5, 0.4, -0.3, 0.2, -0.1, 0.0, -0.1, 0.2, -0.3, 0.4,
				-0.5, 0.6, -0.7, 0.8, -0.9, 1.0,
			]),
		},
		// sandbox
		{
			name: "sandbox shrill lute",
			expression: 0.94,
			samples: centerAndNormalizeWave([1.0, 1.5, 1.25, 1.2, 1.3, 1.5]),
		},
		{
			name: "sandbox bassoon",
			expression: 0.5,
			samples: centerAndNormalizeWave([1.0, -1.0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0]),
		},
		{
			name: "sandbox shrill bass",
			expression: 0.5,
			samples: centerAndNormalizeWave([0, 1, 0, 0, 1, 0, 1, 0, 0, 0]),
		},
		{
			name: "sandbox nes pulse",
			expression: 0.4,
			samples: centerAndNormalizeWave([2.1, -2.2, 1.2, 3]),
		},
		{
			name: "sandbox saw bass",
			expression: 0.25,
			samples: centerAndNormalizeWave([
				1, 1, 1, 1, 0, 2, 1, 2, 3, 1, -2, 1, 4, 1, 4, 2, 1, 6, -3, 4, 2, 1, 5, 1, 4, 1, 5,
				6, 7, 1, 6, 1, 4, 1, 9,
			]),
		},
		{
			name: "sandbox euphonium",
			expression: 0.3,
			samples: centerAndNormalizeWave([
				0, 1, 2, 1, 2, 1, 4, 2, 5, 0, -2, 1, 5, 1, 2, 1, 2, 4, 5, 1, 5, -2, 5, 10, 1,
			]),
		},
		{
			name: "sandbox shrill pulse",
			expression: 0.3,
			samples: centerAndNormalizeWave([4 - 2, 0, 4, 1, 4, 6, 7, 3]),
		},
		{
			name: "sandbox r-sawtooth",
			expression: 0.2,
			samples: centerAndNormalizeWave([6.1, -2.9, 1.4, -2.9]),
		},
		{
			name: "sandbox recorder",
			expression: 0.2,
			samples: centerAndNormalizeWave([
				5.0, -5.1, 4.0, -4.1, 3.0, -3.1, 2.0, -2.1, 1.0, -1.1, 6.0,
			]),
		},
		{
			name: "sandbox narrow saw",
			expression: 1.2,
			samples: centerAndNormalizeWave([
				0.1,
				0.13 / -0.1,
				0.13 / -0.3,
				0.13 / -0.5,
				0.13 / -0.7,
				0.13 / -0.9,
				0.13 / -0.11,
				0.13 / -0.31,
				0.13 / -0.51,
				0.13 / -0.71,
				0.13 / -0.91,
				0.13 / -0.12,
				0.13 / -0.32,
				0.13 / -0.52,
				0.13 / -0.72,
				0.13 / -0.92,
				0.13 / -0.13,
				0.13 / 0.13,
				0.13 / 0.92,
				0.13 / 0.72,
				0.13 / 0.52,
				0.13 / 0.32,
				0.13 / 0.12,
				0.13 / 0.91,
				0.13 / 0.71,
				0.13 / 0.51,
				0.13 / 0.31,
				0.13 / 0.11,
				0.13 / 0.9,
				0.13 / 0.7,
				0.13 / 0.5,
				0.13 / 0.3,
				0.13,
			]),
		},
		{
			name: "sandbox deep square",
			expression: 1.0,
			samples: centerAndNormalizeWave([1.0, 2.25, 1.0, -1.0, -2.25, -1.0]),
		},
		{
			name: "sandbox ring pulse",
			expression: 1.0,
			samples: centerAndNormalizeWave([
				1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, 1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, 1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, 1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, 1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, 1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, 1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, 1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0,
			]),
		},
		{
			name: "sandbox double sine",
			expression: 1.0,
			samples: centerAndNormalizeWave([
				1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 1.8, 1.7, 1.6, 1.5, 1.4, 1.3, 1.2,
				1.1, 1.0, 0.0, -1.0, -1.1, -1.2, -1.3, -1.4, -1.5, -1.6, -1.7, -1.8, -1.9, -1.8,
				-1.7, -1.6, -1.5, -1.4, -1.3, -1.2, -1.1, -1.0,
			]),
		},
		{
			name: "sandbox contrabass",
			expression: 0.5,
			samples: centerAndNormalizeWave([4.2, 6.9, 1.337, 6.66]),
		},
		{
			name: "sandbox double bass",
			expression: 0.4,
			samples: centerAndNormalizeWave([
				0.0, 0.1875, 0.3125, 0.5625, 0.5, 0.75, 0.875, 1.0, -1.0, -0.6875, -0.5, -0.625,
				-0.625, -0.5, -0.375, -0.5625, -0.4375, -0.5625, -0.4375, -0.4375, -0.3125, -0.1875,
				0.1875, 0.375, 0.5625, -0.5625, 0.5625, 0.5625, 0.5625, 0.4375, 0.25, 0.0,
			]),
		},
		// haileybox
		{
			name: "haileybox test1",
			expression: 0.5,
			samples: centerAndNormalizeWave([1.0, 0.5, -1.0]),
		},
		// brucebox
		{
			name: "brucebox pokey 4bit lfsr",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				1.0, -1.0, -1.0, -1.0, 1.0, 1.0, 1.0, 1.0, -1.0, 1.0, -1.0, 1.0, 1.0, -1.0, -1.0,
			]),
		},
		{
			name: "brucebox pokey 5step bass",
			expression: 0.5,
			samples: centerAndNormalizeWave([1.0, -1.0, 1.0, -1.0, 1.0]),
		},
		{
			name: "brucebox isolated spiky",
			expression: 0.5,
			samples: centerAndNormalizeWave([1.0, -1.0, 1.0, -1.0, 1.0, -1.0]),
		},
		// nerdbox
		{
			name: "nerdbox unnamed 1",
			expression: 0.5,
			samples: centerAndNormalizeWave([0.2, 0.8 / 0.2, 0.7, -0.4, -1.0, 0.5, -0.5 / 0.6]),
		},
		{
			name: "nerdbox unnamed 2",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				2.0,
				5.0 / 55.0,
				-9.0,
				6.5 / 6.5,
				-55.0,
				18.5 / -26.0,
			]),
		},
		// zefbox
		{
			name: "zefbox semi-square",
			expression: 1.0,
			samples: centerAndNormalizeWave([1.0, 1.5, 2.0, 2.5, 2.5, 2.5, 2.0, 1.5, 1.0]),
		},
		{
			name: "zefbox deep square",
			expression: 1.0,
			samples: centerAndNormalizeWave([1.0, 2.25, 1.0, -1.0, -2.25, -1.0]),
		},
		{
			name: "zefbox squaretal",
			expression: 0.7,
			samples: centerAndNormalizeWave([1.5, 1.0, 1.5, -1.5, -1.0, -1.5]),
		},
		{
			name: "zefbox saw wide",
			expression: 0.65,
			samples: centerAndNormalizeWave([
				0.0, -0.4, -0.8, -1.2, -1.6, -2.0, 0.0, -0.4, -0.8, -1.2, -1.6,
			]),
		},
		{
			name: "zefbox saw narrow",
			expression: 0.65,
			samples: centerAndNormalizeWave([1, 0.5, 1, 0.5, 1, 0.5, 1, 2, 1, 2, 1]),
		},
		{
			name: "zefbox deep sawtooth",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				0, 2, 3, 4, 4.5, 5, 5.5, 6, 6.25, 6.5, 6.75, 7, 6.75, 6.5, 6.25, 6, 5.5, 5, 4.5, 4,
				3, 2, 1,
			]),
		},
		{
			name: "zefbox sawtal",
			expression: 0.3,
			samples: centerAndNormalizeWave([
				1.5, 1.0, 1.25, -0.5, 1.5, -0.5, 0.0, -1.5, 1.5, 0.0, 0.5, -1.5, 0.5, 1.25, -1.0,
				-1.5,
			]),
		},
		{
			name: "zefbox deep sawtal",
			expression: 0.7,
			samples: centerAndNormalizeWave([0.75, 0.25, 0.5, -0.5, 0.5, -0.5, -0.25, -0.75]),
		},
		{
			name: "zefbox pulse",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				1.0, -2.0, -2.0, -1.5, -1.5, -1.25, -1.25, -1.0, -1.0,
			]),
		},
		{
			name: "zefbox triple pulse",
			expression: 0.4,
			samples: centerAndNormalizeWave([
				1.0, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0, 1.5, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0, -1.0, 1.5,
			]),
		},
		{
			name: "zefbox high pulse",
			expression: 0.2,
			samples: centerAndNormalizeWave([1, -2, 2, -3, 3, -4, 5, -4, 3, -3, 2, -2, 1]),
		},
		{
			name: "zefbox deep pulse",
			expression: 0.2,
			samples: centerAndNormalizeWave([
				1, 2, 2, -2, -2, -3, -4, -4, -5, -5, -5, -5, 0, -1, -2,
			]),
		},
		// wackybox
		{
			name: "wackybox guitar string",
			expression: 0.6,
			samples: centerAndNormalizeWave([
				0, 63, 63, 63, 63, 19, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 11, 63, 63, 63, 63,
				63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 27,
				63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 63, 34, 63, 63, 63,
				63,
			]),
		},
		{
			name: "wackybox intense",
			expression: 0.6,
			samples: centerAndNormalizeWave([
				36, 25, 33, 35, 18, 51, 22, 40, 27, 37, 31, 33, 25, 29, 41, 23, 31, 31, 45, 20, 37,
				23, 29, 26, 42, 29, 33, 26, 31, 27, 40, 25, 40, 26, 37, 24, 41, 32, 0, 32, 33, 29,
				32, 31, 31, 31, 31, 31, 31, 31, 31, 31, 31, 31, 31, 31, 31, 31, 31, 31, 31, 31, 31,
				31,
			]),
		},
		{
			name: "wackybox buzz wave",
			expression: 0.6,
			samples: centerAndNormalizeWave([
				0, 1, 1, 2, 4, 4, 4, 4, 5, 5, 6, 6, 6, 7, 8, 8, 8, 9, 9, 9, 9, 9, 9, 8, 8, 8, 11,
				15, 23, 62, 61, 60, 58, 56, 56, 54, 53, 52, 50, 49, 48, 47, 47, 45, 45, 45, 44, 44,
				43, 43, 42, 42, 42, 42, 42, 42, 42, 42, 42, 42, 42, 43, 43, 53,
			]),
		},
		// todbox
		{ name: "todbox 1/3 pulse", expression: 0.5, samples: centerWave([1.0, -1.0, -1.0]) },
		{
			name: "todbox 1/5 pulse",
			expression: 0.5,
			samples: centerWave([1.0, -1.0, -1.0, -1.0, -1.0]),
		},
		{
			name: "todbox slap bass",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				1, 0.5, 0, 0.5, 1.25, 0.5, -0.25, 0.1, -0.1, 0.1, 1.1, 2.1, 3, 3.5, 2.9, 3.3, 2.7,
				2.9, 2.3, 2, 1.9, 1.8, 1, 0.7, 0.9, 0.8, 0.4, 0.1, 0.0, 0.2, 0.4, 0.6, 0.5, 0.8,
			]),
		},
		{
			name: "todbox harsh wave",
			expression: 0.45,
			samples: centerAndNormalizeWave([
				1.0, -1.0, -1.0, -1.0, 0.5, 0.5, 0.5, 0.7, 0.39, 1.3, 0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
			]),
		},
		{
			name: "todbox accordian",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				0, 1, 1, 2, 2, 1.5, 1.5, 0.8, 0, -2, -3.25, -4, -4.5, -5.5, -6, -5.75, -5.5, -5, -5,
				-5, -6, -6, -6, -5, -4, -3, -2, -1, 0.75, 1, 2, 3, 4, 5, 6, 6.5, 7.5, 8, 7.75, 6,
				5.25, 5, 5, 5, 5, 5, 4.25, 3.75, 3.25, 2.75, 1.25, -0.75, -2, -0.75, 1.25, 1.25, 2,
				2, 2, 2, 1.5, -1, -2, -1, 1.5, 2, 2.75, 2.75, 2.75, 3, 2.75, -1, -2, -2.5, -2, -1,
				-2.25, -2.75, -2, -3, -1.75, 1, 2, 3.5, 4, 5.25, 6, 8, 9.75, 10, 9.5, 9, 8.5, 7.5,
				6.5, 5.25, 5, 4.5, 4, 4, 4, 3.25, 2.5, 2, 1, -0.5, -2, -3.5, -4, -4, -4, -3.75, -3,
				-2, -1,
			]),
		},
		// todbox beta
		{
			name: "todbox beta banana wave",
			expression: 0.8,
			samples: centerAndNormalizeWave([
				0.0, 0.2, 0.4, 0.5, 0.6, 0.7, 0.8, 0.85, 0.9, 0.95, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 0.95, 0.9, 0.85, 0.8, 0.7, 0.6, 0.5,
				0.4, 0.2, 0.0,
			]),
		},
		{
			name: "todbox beta test wave",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				56, 0, -52, 16, 3, 3, 2, -35, 20, 147, -53, 0, 0, 5, -6,
			]),
		},
		{
			name: "todbox beta real snare",
			expression: 1.0,
			samples: centerAndNormalizeWave([
				0.0, -0.01208, -0.02997, -0.04382, -0.06042, -0.07529, -0.09116, -0.10654, -0.12189,
				-0.13751, -0.15289, -0.16849, -0.18387, -0.19974, -0.21484, -0.23071, -0.24557,
				-0.26144, -0.27731, -0.29141, -0.3035, -0.32416, -0.34406, -0.32947, -0.31158,
				-0.33725, -0.37579, -0.39746, -0.40201, -0.40906, -0.4418, -0.47229, -0.47379,
				-0.47733, -0.45239, -0.33954, -0.22894, -0.22443, -0.32138, -0.46371, -0.57178,
				-0.61081, -0.59998, -0.61459, -0.62189, -0.43979, -0.19217, -0.12643, -0.17252,
				-0.20956, -0.20981, -0.19217, -0.22845, -0.34332, -0.50629, -0.64307, -0.72922,
				-0.81384, -0.87857, -0.90149, -0.88687, -0.86169, -0.87781, -0.80478, -0.52493,
				-0.31308, -0.33249, -0.39395, -0.39017, -0.30301, -0.19949, -0.13071, -0.02493,
				0.14307, 0.34961, 0.52542, 0.63223, 0.68613, 0.7471, 0.87305, 0.98184, 0.98889,
				0.97052, 0.99066, 0.99747, 0.99344, 0.99469, 0.99393, 0.9957, 0.99393, 0.99521,
				0.99469, 0.9942, 0.99521, 0.9942, 0.99521, 0.99469, 0.99469, 0.99521, 0.9942,
				0.99545, 0.99445, 0.99469, 0.99493, 0.9942, 0.99521, 0.99393, 0.99493, 0.99469,
				0.99445, 0.9957, 0.99445, 0.99521, 0.99469, 0.99469, 0.99521, 0.9942, 0.99545,
				0.99445, 0.99445, 0.99493, 0.9942, 0.99545, 0.9942, 0.99493, 0.99493, 0.9942,
				0.99545, 0.99445, 0.99521, 0.99469, 0.99445, 0.99545, 0.99368, 0.99393, 0.99445,
				0.99268, 0.97983, 0.97229, 0.95944, 0.88486, 0.76773, 0.64481, 0.53098, 0.39847,
				0.19318, -0.03827, -0.20325, -0.39319, -0.68765, -0.88461, -0.93448, -0.96069,
				-0.97681, -0.98715, -0.99042, -0.99142, -0.99091, -0.99142, -0.99219, -0.99091,
				-0.99219, -0.99066, -0.99142, -0.99142, -0.99118, -0.99191, -0.99066, -0.99191,
				-0.99142, -0.99142, -0.99191, -0.99091, -0.99219, -0.99118, -0.99142, -0.99167,
				-0.99091, -0.99219, -0.99091, -0.99167, -0.99142, -0.99091, -0.99191, -0.99091,
				-0.99191, -0.99142, -0.99118, -0.99191, -0.99066, -0.99191, -0.99118, -0.99142,
				-0.99191, -0.99066, -0.99191, -0.99091, -0.99167, -0.99191, -0.99118, -0.99219,
				-0.99091, -0.99191, -0.99142, -0.99142, -0.99243, -0.98865, -0.98764, -0.99219,
				-0.98083, -0.92517, -0.9277, -0.91486, -0.59042, -0.15189, 0.02945, 0.05667,
				0.06195, 0.00629, -0.18008, -0.56497, -0.8801, -0.9277, -0.92871, -0.97705,
				-0.99167, -0.98663, -0.99118, -0.99042, -0.99219, -0.99142, -0.99118, -0.98941,
				-0.99219, -1.0, -0.9758, -0.95993, -0.99948, -0.98236, -0.84659, -0.7486, -0.70679,
				-0.59747, -0.48035, -0.41687, -0.36826, -0.29745, -0.18185, -0.06219, 0.02164,
				0.07907, 0.13123, 0.18033, 0.1962, 0.15692, 0.14053, 0.20251, 0.2753, 0.30905,
				0.29092, 0.27252, 0.30402, 0.32416, 0.32214, 0.35239, 0.3967, 0.43198, 0.4942,
				0.58487, 0.64154, 0.65967, 0.6705, 0.67026, 0.66522, 0.6554, 0.66119, 0.70627,
				0.75842, 0.78738, 0.7894, 0.78763, 0.80402, 0.85944, 0.94559, 0.9899, 0.9816,
				0.98007, 0.99368, 0.99393, 0.98538, 0.9758, 0.97101, 0.93802, 0.81812, 0.64633,
				0.46649, 0.28613, 0.14685, 0.08966, 0.12543, 0.20325, 0.24557, 0.18866, 0.02795,
				-0.20175, -0.44205, -0.58713, -0.57629, -0.41385, -0.14255, 0.18033, 0.47882,
				0.68311, 0.72314, 0.62064, 0.48309, 0.43073, 0.53577, 0.72794, 0.9025, 0.97354,
				0.97, 0.98083, 0.99191, 0.99319, 0.99493, 0.99393, 0.99521, 0.99393, 0.99545,
				0.9942, 0.99493, 0.99493, 0.99445, 0.99545, 0.9942, 0.99545, 0.99243, 0.98917,
				0.98386, 0.97781, 0.95844, 0.89066, 0.81561, 0.78134, 0.77277, 0.75995, 0.73022,
				0.67126, 0.57178, 0.47, 0.38361, 0.29419, 0.20703, 0.14734, 0.15866, 0.25162,
				0.35818, 0.45062, 0.5675, 0.69748, 0.81232, 0.89697, 0.95062, 0.97656, 0.98615,
				0.99191, 0.99219, 0.99243, 0.99368, 0.99368, 0.97028, 0.95566, 0.94559, 0.82617,
				0.59973, 0.38361, 0.23901, 0.15338, 0.12921, 0.11206, 0.04382, -0.12946, -0.43552,
				-0.72644, -0.89847, -0.95465, -0.95541, -0.97229, -0.99268, -0.99319, -0.9884,
				-0.99142, -0.99167, -0.99091, -0.9884, -0.98965, -0.99368, -0.97455, -0.9501,
				-0.94684, -0.96219, -0.98514, -0.99243, -0.98889, -0.98917, -0.99142, -0.99219,
				-0.99091, -0.99191, -0.99142, -0.99142, -0.99191, -0.99066, -0.99167, -0.99091,
				-0.99142, -0.99191, -0.99091, -0.99191, -0.99091, -0.99167, -0.99167, -0.99091,
				-0.99219, -0.99091, -0.99191, -0.99142, -0.99118, -0.99191, -0.99066, -0.99191,
				-0.99091, -0.99118, -0.99243, -0.98941, -0.98462, -0.96976, -0.9632, -0.96194,
				-0.87305, -0.66196, -0.44809, -0.29495, -0.18085, -0.11813, -0.11334, -0.18564,
				-0.34885, -0.58237, -0.8045, -0.93726, -0.97806, -0.97354, -0.97531, -0.9899,
				-0.99368, -0.98941, -0.99219, -0.99091, -0.99142, -0.99167, -0.99091, -0.99191,
				-0.99118, -0.99219, -0.98236, -0.97781, -0.97656, -0.95135, -0.87204, -0.71335,
				-0.52139, -0.34232, -0.17783, -0.00906, 0.14886, 0.3045, 0.48889, 0.67404, 0.8403,
				0.94128, 0.97681, 0.98462, 0.98337, 0.99142, 0.99521, 0.99493, 0.9942, 0.99445,
				0.99521, 0.99393, 0.99545, 0.99445, 0.99521, 0.99521, 0.99445, 0.9957, 0.99445,
				0.99521, 0.99469, 0.99445, 0.99521, 0.9942, 0.99521, 0.99445, 0.99445, 0.99521,
				0.99445, 0.99545, 0.99445, 0.99469, 0.99493, 0.99393, 0.99493, 0.99445, 0.99393,
				0.98285, 0.97781, 0.97479, 0.92844, 0.82114, 0.66095, 0.52417, 0.46826, 0.46722,
				0.47934, 0.47379, 0.47076, 0.48209, 0.42014, 0.25439, 0.10074, -0.00302, -0.08966,
				-0.16068, -0.21436, -0.2204, -0.15137, -0.00476, 0.18536, 0.37631, 0.52292, 0.62164,
				0.70425, 0.74835, 0.72366, 0.63928, 0.52567, 0.40805, 0.35666, 0.42896, 0.60175,
				0.802, 0.92743, 0.96548, 0.97632, 0.98337, 0.99066, 0.99521, 0.9942, 0.99368,
				0.99292, 0.9884, 0.98083, 0.96774, 0.93323, 0.8544, 0.6947, 0.47202, 0.20425,
				-0.0889, -0.36423, -0.60025, -0.77481, -0.90173, -0.96017, -0.97028, -0.98108,
				-0.9884, -0.99219, -0.9899, -0.99219, -0.99142, -0.99142, -0.99219, -0.99091,
				-0.99243, -0.99066, -0.99142, -0.99142, -0.99118, -0.99191, -0.99066, -0.99167,
				-0.99142, -0.99142, -0.99219, -0.99091, -0.99191, -0.99118, -0.99142, -0.99191,
				-0.99091, -0.99191, -0.99091, -0.99167, -0.99191, -0.99118, -0.99219, -0.99091,
				-0.99167, -0.99142, -0.99142, -0.99219, -0.99091, -0.99191, -0.99142, -0.99118,
				-0.98917, -0.99042, -0.99445, -0.9733, -0.9559, -0.96219, -0.8967, -0.72241,
				-0.55112, -0.44809, -0.39319, -0.37833, -0.35641, -0.2627, -0.1423, -0.11282,
				-0.13525, -0.11536, -0.09671, -0.11511, -0.1806, -0.26874, -0.33374, -0.42215,
				-0.51358, -0.44785, -0.3045, -0.28613, -0.30527, -0.25037, -0.1539, -0.08286,
				-0.11157, -0.12592, -0.00327, 0.13803, 0.19141, 0.1282, 0.01788, -0.03952, -0.12592,
				-0.26773, -0.34634, -0.31384, -0.1806, -0.0108, 0.13574, 0.2612, 0.36975, 0.46573,
				0.55087, 0.63626, 0.73022, 0.83072, 0.92014, 0.97177, 0.98587, 0.98413, 0.99167,
				0.99445, 0.99292, 0.99219, 0.9874, 0.98007, 0.96472, 0.92239, 0.82166, 0.69067,
				0.57959, 0.54962, 0.59695, 0.64255, 0.64633, 0.60629, 0.55942, 0.5491, 0.58966,
				0.61887, 0.56952, 0.54181, 0.59518, 0.63248, 0.63876, 0.65463, 0.73398, 0.88312,
				0.96927, 0.97101, 0.97958, 0.99344, 0.9942, 0.99268, 0.99493, 0.99469, 0.99445,
				0.99521, 0.99445, 0.99545, 0.9942, 0.99493, 0.99493, 0.9942, 0.99545, 0.9942,
				0.99493, 0.9942, 0.99393, 0.9942, 0.9884, 0.98309, 0.98309, 0.96069, 0.88461,
				0.7937, 0.72064, 0.65765, 0.59998, 0.53247, 0.49268, 0.48615, 0.44205, 0.38034,
				0.36447, 0.38715, 0.39294, 0.32645, 0.19595, 0.07782, -0.05893, -0.27832, -0.48309,
				-0.62619, -0.72995, -0.79999, -0.84583, -0.82166, -0.73575, -0.67227, -0.65491,
				-0.6496, -0.66397, -0.70175, -0.72894, -0.74658, -0.76724, -0.7952, -0.82846,
				-0.86523, -0.90527, -0.94382, -0.89948, -0.69849, -0.47479, -0.31662, -0.15414,
				-0.00729, 0.07077, 0.08237, 0.04431, -0.02292, -0.11761, -0.24307, -0.36926,
				-0.45087, -0.4617, -0.4025, -0.30679, -0.17529, 0.0, 0.14331, 0.24179, 0.36774,
				0.49545, 0.56522, 0.57907, 0.56775, 0.53851, 0.51132, 0.48688, 0.41913, 0.26044,
				0.00955, -0.26297, -0.46396, -0.62341, -0.82214, -0.94684, -0.96774, -0.97531,
				-0.98413, -0.99017, -0.9899, -0.99219, -0.99066, -0.99142, -0.99167, -0.99118,
				-0.99219, -0.9899, -0.99118, -0.99368, -0.99142, -0.97757, -0.97403, -0.98007,
				-0.9617, -0.86826, -0.67783, -0.52719, -0.48788, -0.4549, -0.43146, -0.47681,
				-0.54105, -0.57983, -0.60904, -0.62317, -0.59949, -0.55566, -0.52063, -0.52115,
				-0.55112, -0.56244, -0.58337, -0.6554, -0.73373, -0.77228, -0.74759, -0.6889,
				-0.64609, -0.61887, -0.5806, -0.50351, -0.40729, -0.33929, -0.3511, -0.42944,
				-0.47028, -0.42267, -0.32718, -0.20224, -0.0564, 0.04556, 0.10529, 0.1763, 0.26169,
				0.33197, 0.32138, 0.23776, 0.20956, 0.23148, 0.20352, 0.23325, 0.39267, 0.52719,
				0.58438, 0.62289, 0.66345, 0.70023, 0.66296, 0.5433, 0.42618, 0.33475, 0.24533,
				0.14105, 0.03851, 0.01358, 0.09143, 0.22845, 0.34961, 0.41711, 0.4874, 0.58914,
				0.69519, 0.78186, 0.84357, 0.89822, 0.95389, 0.98135, 0.98615, 0.99167, 0.99243,
				0.99445, 0.9942, 0.99469, 0.99493, 0.99393, 0.99545, 0.99445, 0.99521, 0.99469,
				0.99445, 0.99521, 0.9942, 0.99469, 0.98965, 0.98715, 0.98563, 0.96295, 0.91736,
				0.86624, 0.82367, 0.77554, 0.68411, 0.53549, 0.38916, 0.2612, 0.11435, -0.04053,
				-0.18161, -0.23172, -0.19394, -0.15237, -0.1073, -0.02997, 0.08588, 0.2262, 0.34305,
				0.44104, 0.5574, 0.65765, 0.71259, 0.69217, 0.65363, 0.69748, 0.79572, 0.89368,
				0.95514, 0.97733, 0.98413, 0.98816, 0.99243, 0.99445, 0.99243, 0.97302, 0.96674,
				0.97983, 0.90378, 0.71005, 0.51056, 0.40451, 0.40982, 0.41559, 0.32996, 0.24356,
				0.18866, 0.11411, 0.05365, 0.01157, -0.03247, -0.09216, -0.16095, -0.23248,
				-0.31662, -0.39771, -0.48663, -0.59647, -0.71536, -0.82013, -0.85287, -0.82947,
				-0.84937, -0.92215, -0.97177, -0.98663, -0.98816, -0.98438, -0.99091, -0.99219,
				-0.99091, -0.99191, -0.99042, -0.99191, -0.99091, -0.99142, -0.99191, -0.99091,
				-0.99191, -0.99091, -0.99167, -0.99142,
			]),
		},
		// based off an old mp3 in #modded-beepbox where someone tried to shorten the overdrive guitar into the size of other chip waves
		// search "normie alert" in beepcord
		{
			name: "ultrabox shortened od guitar",
			expression: 0.5,
			samples: centerAndNormalizeWave([
				-0.82785, -0.67621, -0.40268, -0.43817, -0.45468, -0.22531, -0.18329, 0.2475,
				0.71246, 0.52155, 0.56082, 0.48395, 0.3399, 0.46957, 0.27744, 0.42313, 0.47104,
				0.18796, 0.1293, -0.13901, -0.07431, -0.16348, -0.74857, -0.73206, -0.35181,
				-0.26227, -0.41882, -0.27786, -0.19806, -0.19867, 0.18643, 0.24808, 0.08847,
				-0.06964, 0.06912, 0.20474, -0.05304, 0.29416, 0.31967, 0.14243, 0.27521, -0.23932,
				-0.14752, 0.1236, -0.26123, -0.26111, 0.06616, 0.2652, 0.0809, 0.1524, 0.16254,
				-0.12061, 0.04562, 0.00131, 0.0405, 0.08182, -0.21729, -0.17041, -0.16312, -0.08563,
				0.0639, 0.05099, 0.05627, 0.02728, 0.00726, -0.13028, -0.05673, -0.14969, -0.17645,
				0.35492, 0.16766, -0.00897, 0.24326, -0.00461, -0.04456, 0.01776, -0.0495, -0.01221,
				0.02039, 0.07684, 0.13397, 0.3985, 0.35962, 0.13754, 0.4231, 0.27161, -0.17609,
				0.03659, 0.10635, -0.21909, -0.22046, -0.20258, -0.40973, -0.4028, -0.40521,
				-0.66284,
			]),
		},
		// LemmBox wave types
		{
			name: "lemmbox HD triangle",
			expression: 1.0,
			samples: centerWave([
				1.0, 0.996, 0.992, 0.988, 0.984, 0.98, 0.976, 0.972, 0.968, 0.964, 0.96, 0.956,
				0.952, 0.948, 0.944, 0.94, 0.9359999999999999, 0.9319999999999999,
				0.9279999999999999, 0.9239999999999999, 0.9199999999999999, 0.9159999999999999,
				0.9119999999999999, 0.9079999999999999, 0.9039999999999999, 0.8999999999999999,
				0.8959999999999999, 0.8919999999999999, 0.8879999999999999, 0.8839999999999999,
				0.8799999999999999, 0.8759999999999999, 0.8719999999999999, 0.8679999999999999,
				0.8639999999999999, 0.8599999999999999, 0.8560000000000001, 0.8520000000000001,
				0.8480000000000001, 0.8440000000000001, 0.8400000000000001, 0.8360000000000001,
				0.8320000000000001, 0.8280000000000001, 0.8240000000000001, 0.8200000000000001,
				0.8160000000000001, 0.812, 0.808, 0.804, 0.8, 0.796, 0.792, 0.788, 0.784, 0.78,
				0.776, 0.772, 0.768, 0.764, 0.76, 0.756, 0.752, 0.748, 0.744, 0.74, 0.736, 0.732,
				0.728, 0.724, 0.72, 0.716, 0.712, 0.708, 0.704, 0.7, 0.696, 0.692, 0.688,
				0.6839999999999999, 0.6799999999999999, 0.6759999999999999, 0.6719999999999999,
				0.6679999999999999, 0.6639999999999999, 0.6599999999999999, 0.6560000000000001,
				0.6520000000000001, 0.6480000000000001, 0.6440000000000001, 0.6400000000000001,
				0.6360000000000001, 0.6320000000000001, 0.6280000000000001, 0.6240000000000001,
				0.6200000000000001, 0.6160000000000001, 0.6120000000000001, 0.6080000000000001,
				0.6040000000000001, 0.6000000000000001, 0.5960000000000001, 0.5920000000000001,
				0.5880000000000001, 0.5840000000000001, 0.5800000000000001, 0.5760000000000001,
				0.5720000000000001, 0.5680000000000001, 0.5640000000000001, 0.56, 0.556, 0.552,
				0.548, 0.544, 0.54, 0.536, 0.532, 0.528, 0.524, 0.52, 0.516, 0.512, 0.508, 0.504,
				0.5, 0.496, 0.492, 0.488, 0.484, 0.48, 0.476, 0.472, 0.46799999999999997,
				0.46399999999999997, 0.45999999999999996, 0.45599999999999996, 0.45199999999999996,
				0.44799999999999995, 0.44399999999999995, 0.43999999999999995, 0.43599999999999994,
				0.43199999999999994, 0.42799999999999994, 0.42399999999999993, 0.41999999999999993,
				0.4159999999999999, 0.4119999999999999, 0.4079999999999999, 0.4039999999999999,
				0.3999999999999999, 0.3959999999999999, 0.3919999999999999, 0.3879999999999999,
				0.3839999999999999, 0.3799999999999999, 0.3759999999999999, 0.3719999999999999,
				0.3679999999999999, 0.3639999999999999, 0.3599999999999999, 0.35599999999999987,
				0.35199999999999987, 0.34799999999999986, 0.34399999999999986, 0.33999999999999986,
				0.33599999999999985, 0.33199999999999985, 0.32799999999999985, 0.32399999999999984,
				0.31999999999999984, 0.31599999999999984, 0.31200000000000006, 0.30800000000000005,
				0.30400000000000005, 0.30000000000000004, 0.29600000000000004, 0.29200000000000004,
				0.28800000000000003, 0.28400000000000003, 0.28, 0.276, 0.272, 0.268, 0.264, 0.26,
				0.256, 0.252, 0.248, 0.244, 0.24, 0.236, 0.23199999999999998, 0.22799999999999998,
				0.22399999999999998, 0.21999999999999997, 0.21599999999999997, 0.21199999999999997,
				0.20799999999999996, 0.20399999999999996, 0.19999999999999996, 0.19599999999999995,
				0.19199999999999995, 0.18799999999999994, 0.18400000000000016, 0.18000000000000016,
				0.17600000000000016, 0.17200000000000015, 0.16800000000000015, 0.16400000000000015,
				0.16000000000000014, 0.15600000000000014, 0.15200000000000014, 0.14800000000000013,
				0.14400000000000013, 0.14000000000000012, 0.13600000000000012, 0.13200000000000012,
				0.1280000000000001, 0.12400000000000011, 0.1200000000000001, 0.1160000000000001,
				0.1120000000000001, 0.1080000000000001, 0.10400000000000009, 0.10000000000000009,
				0.09600000000000009, 0.09200000000000008, 0.08800000000000008, 0.08400000000000007,
				0.08000000000000007, 0.07600000000000007, 0.07200000000000006, 0.06800000000000006,
				0.06400000000000006, 0.06000000000000005, 0.05600000000000005, 0.052000000000000046,
				0.04800000000000004, 0.04400000000000004, 0.040000000000000036, 0.03600000000000003,
				0.03200000000000003, 0.028000000000000025, 0.02400000000000002,
				0.020000000000000018, 0.016000000000000014, 0.01200000000000001,
				0.008000000000000007, 0.0040000000000000036, 0.0, -0.0040000000000000036,
				-0.008000000000000007, -0.01200000000000001, -0.016000000000000014,
				-0.020000000000000018, -0.02400000000000002, -0.028000000000000025,
				-0.03200000000000003, -0.03600000000000003, -0.040000000000000036,
				-0.04400000000000004, -0.04800000000000004, -0.052000000000000046,
				-0.05600000000000005, -0.06000000000000005, -0.06400000000000006,
				-0.06800000000000006, -0.07200000000000006, -0.07600000000000007,
				-0.08000000000000007, -0.08400000000000007, -0.08800000000000008,
				-0.09200000000000008, -0.09600000000000009, -0.10000000000000009,
				-0.10400000000000009, -0.1080000000000001, -0.1120000000000001, -0.1160000000000001,
				-0.1200000000000001, -0.12400000000000011, -0.1279999999999999, -0.1319999999999999,
				-0.1359999999999999, -0.1399999999999999, -0.1439999999999999, -0.1479999999999999,
				-0.1519999999999999, -0.15599999999999992, -0.15999999999999992,
				-0.16399999999999992, -0.16799999999999993, -0.17199999999999993,
				-0.17599999999999993, -0.17999999999999994, -0.18399999999999994,
				-0.18799999999999994, -0.19199999999999995, -0.19599999999999995,
				-0.19999999999999996, -0.20399999999999996, -0.20799999999999996,
				-0.21199999999999997, -0.21599999999999997, -0.21999999999999997,
				-0.22399999999999998, -0.22799999999999998, -0.23199999999999998, -0.236, -0.24,
				-0.244, -0.248, -0.252, -0.256, -0.26, -0.264, -0.268, -0.272, -0.276, -0.28,
				-0.28400000000000003, -0.28800000000000003, -0.29200000000000004,
				-0.29600000000000004, -0.30000000000000004, -0.30400000000000005,
				-0.30800000000000005, -0.31200000000000006, -0.31600000000000006,
				-0.32000000000000006, -0.32400000000000007, -0.32800000000000007,
				-0.3320000000000001, -0.3360000000000001, -0.3400000000000001, -0.3440000000000001,
				-0.3480000000000001, -0.3520000000000001, -0.3560000000000001, -0.3600000000000001,
				-0.3640000000000001, -0.3680000000000001, -0.3720000000000001, -0.3759999999999999,
				-0.3799999999999999, -0.3839999999999999, -0.3879999999999999, -0.3919999999999999,
				-0.3959999999999999, -0.3999999999999999, -0.4039999999999999, -0.4079999999999999,
				-0.4119999999999999, -0.4159999999999999, -0.41999999999999993,
				-0.42399999999999993, -0.42799999999999994, -0.43199999999999994,
				-0.43599999999999994, -0.43999999999999995, -0.44399999999999995,
				-0.44799999999999995, -0.45199999999999996, -0.45599999999999996,
				-0.45999999999999996, -0.46399999999999997, -0.46799999999999997, -0.472, -0.476,
				-0.48, -0.484, -0.488, -0.492, -0.496, -0.5, -0.504, -0.508, -0.512, -0.516, -0.52,
				-0.524, -0.528, -0.532, -0.536, -0.54, -0.544, -0.548, -0.552, -0.556, -0.56,
				-0.5640000000000001, -0.5680000000000001, -0.5720000000000001, -0.5760000000000001,
				-0.5800000000000001, -0.5840000000000001, -0.5880000000000001, -0.5920000000000001,
				-0.5960000000000001, -0.6000000000000001, -0.6040000000000001, -0.6080000000000001,
				-0.6120000000000001, -0.6160000000000001, -0.6200000000000001, -0.6240000000000001,
				-0.6279999999999999, -0.6319999999999999, -0.6359999999999999, -0.6399999999999999,
				-0.6439999999999999, -0.6479999999999999, -0.6519999999999999, -0.6559999999999999,
				-0.6599999999999999, -0.6639999999999999, -0.6679999999999999, -0.6719999999999999,
				-0.6759999999999999, -0.6799999999999999, -0.6839999999999999, -0.688, -0.692,
				-0.696, -0.7, -0.704, -0.708, -0.712, -0.716, -0.72, -0.724, -0.728, -0.732, -0.736,
				-0.74, -0.744, -0.748, -0.752, -0.756, -0.76, -0.764, -0.768, -0.772, -0.776, -0.78,
				-0.784, -0.788, -0.792, -0.796, -0.8, -0.804, -0.808, -0.812, -0.8160000000000001,
				-0.8200000000000001, -0.8240000000000001, -0.8280000000000001, -0.8320000000000001,
				-0.8360000000000001, -0.8400000000000001, -0.8440000000000001, -0.8480000000000001,
				-0.8520000000000001, -0.8560000000000001, -0.8600000000000001, -0.8640000000000001,
				-0.8680000000000001, -0.8720000000000001, -0.8759999999999999, -0.8799999999999999,
				-0.8839999999999999, -0.8879999999999999, -0.8919999999999999, -0.8959999999999999,
				-0.8999999999999999, -0.9039999999999999, -0.9079999999999999, -0.9119999999999999,
				-0.9159999999999999, -0.9199999999999999, -0.9239999999999999, -0.9279999999999999,
				-0.9319999999999999, -0.9359999999999999, -0.94, -0.944, -0.948, -0.952, -0.956,
				-0.96, -0.964, -0.968, -0.972, -0.976, -0.98, -0.984, -0.988, -0.992, -0.996, -1.0,
				-0.996, -0.992, -0.988, -0.984, -0.98, -0.976, -0.972, -0.968, -0.964, -0.96,
				-0.956, -0.952, -0.948, -0.944, -0.94, -0.9359999999999999, -0.9319999999999999,
				-0.9279999999999999, -0.9239999999999999, -0.9199999999999999, -0.9159999999999999,
				-0.9119999999999999, -0.9079999999999999, -0.9039999999999999, -0.8999999999999999,
				-0.8959999999999999, -0.8919999999999999, -0.8879999999999999, -0.8839999999999999,
				-0.8799999999999999, -0.8759999999999999, -0.8719999999999999, -0.8679999999999999,
				-0.8639999999999999, -0.8599999999999999, -0.8559999999999999, -0.8519999999999999,
				-0.8479999999999999, -0.8439999999999999, -0.8399999999999999, -0.8359999999999999,
				-0.8319999999999999, -0.8279999999999998, -0.8239999999999998, -0.8199999999999998,
				-0.8159999999999998, -0.8119999999999998, -0.8079999999999998, -0.8039999999999998,
				-0.7999999999999998, -0.7959999999999998, -0.7919999999999998, -0.7879999999999998,
				-0.7839999999999998, -0.7799999999999998, -0.7759999999999998, -0.7719999999999998,
				-0.7679999999999998, -0.7639999999999998, -0.7599999999999998, -0.7559999999999998,
				-0.7519999999999998, -0.7480000000000002, -0.7440000000000002, -0.7400000000000002,
				-0.7360000000000002, -0.7320000000000002, -0.7280000000000002, -0.7240000000000002,
				-0.7200000000000002, -0.7160000000000002, -0.7120000000000002, -0.7080000000000002,
				-0.7040000000000002, -0.7000000000000002, -0.6960000000000002, -0.6920000000000002,
				-0.6880000000000002, -0.6840000000000002, -0.6800000000000002, -0.6760000000000002,
				-0.6720000000000002, -0.6680000000000001, -0.6640000000000001, -0.6600000000000001,
				-0.6560000000000001, -0.6520000000000001, -0.6480000000000001, -0.6440000000000001,
				-0.6400000000000001, -0.6360000000000001, -0.6320000000000001, -0.6280000000000001,
				-0.6240000000000001, -0.6200000000000001, -0.6160000000000001, -0.6120000000000001,
				-0.6080000000000001, -0.6040000000000001, -0.6000000000000001, -0.5960000000000001,
				-0.5920000000000001, -0.5880000000000001, -0.5840000000000001, -0.5800000000000001,
				-0.5760000000000001, -0.5720000000000001, -0.5680000000000001, -0.5640000000000001,
				-0.56, -0.556, -0.552, -0.548, -0.544, -0.54, -0.536, -0.532, -0.528, -0.524, -0.52,
				-0.516, -0.512, -0.508, -0.504, -0.5, -0.496, -0.492, -0.488, -0.484, -0.48, -0.476,
				-0.472, -0.46799999999999997, -0.46399999999999997, -0.45999999999999996,
				-0.45599999999999996, -0.45199999999999996, -0.44799999999999995,
				-0.44399999999999995, -0.43999999999999995, -0.43599999999999994,
				-0.43199999999999994, -0.42799999999999994, -0.42399999999999993,
				-0.41999999999999993, -0.4159999999999999, -0.4119999999999999, -0.4079999999999999,
				-0.4039999999999999, -0.3999999999999999, -0.3959999999999999, -0.3919999999999999,
				-0.3879999999999999, -0.3839999999999999, -0.3799999999999999, -0.3759999999999999,
				-0.3719999999999999, -0.3679999999999999, -0.3639999999999999, -0.3599999999999999,
				-0.35599999999999987, -0.35199999999999987, -0.34799999999999986,
				-0.34399999999999986, -0.33999999999999986, -0.33599999999999985,
				-0.33199999999999985, -0.32799999999999985, -0.32399999999999984,
				-0.31999999999999984, -0.31599999999999984, -0.31199999999999983,
				-0.30799999999999983, -0.3039999999999998, -0.2999999999999998, -0.2959999999999998,
				-0.2919999999999998, -0.2879999999999998, -0.2839999999999998, -0.2799999999999998,
				-0.2759999999999998, -0.2719999999999998, -0.2679999999999998, -0.2639999999999998,
				-0.2599999999999998, -0.2559999999999998, -0.2519999999999998, -0.24800000000000022,
				-0.24400000000000022, -0.2400000000000002, -0.2360000000000002, -0.2320000000000002,
				-0.2280000000000002, -0.2240000000000002, -0.2200000000000002, -0.2160000000000002,
				-0.2120000000000002, -0.20800000000000018, -0.20400000000000018,
				-0.20000000000000018, -0.19600000000000017, -0.19200000000000017,
				-0.18800000000000017, -0.18400000000000016, -0.18000000000000016,
				-0.17600000000000016, -0.17200000000000015, -0.16800000000000015,
				-0.16400000000000015, -0.16000000000000014, -0.15600000000000014,
				-0.15200000000000014, -0.14800000000000013, -0.14400000000000013,
				-0.14000000000000012, -0.13600000000000012, -0.13200000000000012,
				-0.1280000000000001, -0.12400000000000011, -0.1200000000000001, -0.1160000000000001,
				-0.1120000000000001, -0.1080000000000001, -0.10400000000000009,
				-0.10000000000000009, -0.09600000000000009, -0.09200000000000008,
				-0.08800000000000008, -0.08400000000000007, -0.08000000000000007,
				-0.07600000000000007, -0.07200000000000006, -0.06800000000000006,
				-0.06400000000000006, -0.06000000000000005, -0.05600000000000005,
				-0.052000000000000046, -0.04800000000000004, -0.04400000000000004,
				-0.040000000000000036, -0.03600000000000003, -0.03200000000000003,
				-0.028000000000000025, -0.02400000000000002, -0.020000000000000018,
				-0.016000000000000014, -0.01200000000000001, -0.008000000000000007,
				-0.0040000000000000036, 0.0, 0.0040000000000000036, 0.008000000000000007,
				0.01200000000000001, 0.016000000000000014, 0.020000000000000018,
				0.02400000000000002, 0.028000000000000025, 0.03200000000000003, 0.03600000000000003,
				0.040000000000000036, 0.04400000000000004, 0.04800000000000004,
				0.052000000000000046, 0.05600000000000005, 0.06000000000000005, 0.06400000000000006,
				0.06800000000000006, 0.07200000000000006, 0.07600000000000007, 0.08000000000000007,
				0.08400000000000007, 0.08800000000000008, 0.09200000000000008, 0.09600000000000009,
				0.10000000000000009, 0.10400000000000009, 0.1080000000000001, 0.1120000000000001,
				0.1160000000000001, 0.1200000000000001, 0.12400000000000011, 0.1280000000000001,
				0.13200000000000012, 0.13600000000000012, 0.14000000000000012, 0.14400000000000013,
				0.14800000000000013, 0.15200000000000014, 0.15600000000000014, 0.16000000000000014,
				0.16400000000000015, 0.16800000000000015, 0.17200000000000015, 0.17600000000000016,
				0.18000000000000016, 0.18400000000000016, 0.18800000000000017, 0.19200000000000017,
				0.19600000000000017, 0.20000000000000018, 0.20400000000000018, 0.20800000000000018,
				0.2120000000000002, 0.2160000000000002, 0.2200000000000002, 0.2240000000000002,
				0.2280000000000002, 0.2320000000000002, 0.2360000000000002, 0.2400000000000002,
				0.24400000000000022, 0.24800000000000022, 0.2519999999999998, 0.2559999999999998,
				0.2599999999999998, 0.2639999999999998, 0.2679999999999998, 0.2719999999999998,
				0.2759999999999998, 0.2799999999999998, 0.2839999999999998, 0.2879999999999998,
				0.2919999999999998, 0.2959999999999998, 0.2999999999999998, 0.3039999999999998,
				0.30799999999999983, 0.31199999999999983, 0.31599999999999984, 0.31999999999999984,
				0.32399999999999984, 0.32799999999999985, 0.33199999999999985, 0.33599999999999985,
				0.33999999999999986, 0.34399999999999986, 0.34799999999999986, 0.35199999999999987,
				0.35599999999999987, 0.3599999999999999, 0.3639999999999999, 0.3679999999999999,
				0.3719999999999999, 0.3759999999999999, 0.3799999999999999, 0.3839999999999999,
				0.3879999999999999, 0.3919999999999999, 0.3959999999999999, 0.3999999999999999,
				0.4039999999999999, 0.4079999999999999, 0.4119999999999999, 0.4159999999999999,
				0.41999999999999993, 0.42399999999999993, 0.42799999999999994, 0.43199999999999994,
				0.43599999999999994, 0.43999999999999995, 0.44399999999999995, 0.44799999999999995,
				0.45199999999999996, 0.45599999999999996, 0.45999999999999996, 0.46399999999999997,
				0.46799999999999997, 0.472, 0.476, 0.48, 0.484, 0.488, 0.492, 0.496, 0.5, 0.504,
				0.508, 0.512, 0.516, 0.52, 0.524, 0.528, 0.532, 0.536, 0.54, 0.544, 0.548, 0.552,
				0.556, 0.56, 0.5640000000000001, 0.5680000000000001, 0.5720000000000001,
				0.5760000000000001, 0.5800000000000001, 0.5840000000000001, 0.5880000000000001,
				0.5920000000000001, 0.5960000000000001, 0.6000000000000001, 0.6040000000000001,
				0.6080000000000001, 0.6120000000000001, 0.6160000000000001, 0.6200000000000001,
				0.6240000000000001, 0.6280000000000001, 0.6320000000000001, 0.6360000000000001,
				0.6400000000000001, 0.6440000000000001, 0.6480000000000001, 0.6520000000000001,
				0.6560000000000001, 0.6600000000000001, 0.6640000000000001, 0.6680000000000001,
				0.6720000000000002, 0.6760000000000002, 0.6800000000000002, 0.6840000000000002,
				0.6880000000000002, 0.6920000000000002, 0.6960000000000002, 0.7000000000000002,
				0.7040000000000002, 0.7080000000000002, 0.7120000000000002, 0.7160000000000002,
				0.7200000000000002, 0.7240000000000002, 0.7280000000000002, 0.7320000000000002,
				0.7360000000000002, 0.7400000000000002, 0.7440000000000002, 0.7480000000000002,
				0.7519999999999998, 0.7559999999999998, 0.7599999999999998, 0.7639999999999998,
				0.7679999999999998, 0.7719999999999998, 0.7759999999999998, 0.7799999999999998,
				0.7839999999999998, 0.7879999999999998, 0.7919999999999998, 0.7959999999999998,
				0.7999999999999998, 0.8039999999999998, 0.8079999999999998, 0.8119999999999998,
				0.8159999999999998, 0.8199999999999998, 0.8239999999999998, 0.8279999999999998,
				0.8319999999999999, 0.8359999999999999, 0.8399999999999999, 0.8439999999999999,
				0.8479999999999999, 0.8519999999999999, 0.8559999999999999, 0.8599999999999999,
				0.8639999999999999, 0.8679999999999999, 0.8719999999999999, 0.8759999999999999,
				0.8799999999999999, 0.8839999999999999, 0.8879999999999999, 0.8919999999999999,
				0.8959999999999999, 0.8999999999999999, 0.9039999999999999, 0.9079999999999999,
				0.9119999999999999, 0.9159999999999999, 0.9199999999999999, 0.9239999999999999,
				0.9279999999999999, 0.9319999999999999, 0.9359999999999999, 0.94, 0.944, 0.948,
				0.952, 0.956, 0.96, 0.964, 0.968, 0.972, 0.976, 0.98, 0.984, 0.988, 0.992, 0.996,
			]),
		},
		{
			name: "lemmbox HD sawtooth",
			expression: 1.0,
			samples: centerWave([
				0.0, 0.00390625, 0.0078125, 0.01171875, 0.015625, 0.01953125, 0.0234375, 0.02734375,
				0.03125, 0.03515625, 0.0390625, 0.04296875, 0.046875, 0.05078125, 0.0546875,
				0.05859375, 0.0625, 0.06640625, 0.0703125, 0.07421875, 0.078125, 0.08203125,
				0.0859375, 0.08984375, 0.09375, 0.09765625, 0.1015625, 0.10546875, 0.109375,
				0.11328125, 0.1171875, 0.12109375, 0.125, 0.12890625, 0.1328125, 0.13671875,
				0.140625, 0.14453125, 0.1484375, 0.15234375, 0.15625, 0.16015625, 0.1640625,
				0.16796875, 0.171875, 0.17578125, 0.1796875, 0.18359375, 0.1875, 0.19140625,
				0.1953125, 0.19921875, 0.203125, 0.20703125, 0.2109375, 0.21484375, 0.21875,
				0.22265625, 0.2265625, 0.23046875, 0.234375, 0.23828125, 0.2421875, 0.24609375,
				0.25, 0.25390625, 0.2578125, 0.26171875, 0.265625, 0.26953125, 0.2734375,
				0.27734375, 0.28125, 0.28515625, 0.2890625, 0.29296875, 0.296875, 0.30078125,
				0.3046875, 0.30859375, 0.3125, 0.31640625, 0.3203125, 0.32421875, 0.328125,
				0.33203125, 0.3359375, 0.33984375, 0.34375, 0.34765625, 0.3515625, 0.35546875,
				0.359375, 0.36328125, 0.3671875, 0.37109375, 0.375, 0.37890625, 0.3828125,
				0.38671875, 0.390625, 0.39453125, 0.3984375, 0.40234375, 0.40625, 0.41015625,
				0.4140625, 0.41796875, 0.421875, 0.42578125, 0.4296875, 0.43359375, 0.4375,
				0.44140625, 0.4453125, 0.44921875, 0.453125, 0.45703125, 0.4609375, 0.46484375,
				0.46875, 0.47265625, 0.4765625, 0.48046875, 0.484375, 0.48828125, 0.4921875,
				0.49609375, 0.5, 0.50390625, 0.5078125, 0.51171875, 0.515625, 0.51953125, 0.5234375,
				0.52734375, 0.53125, 0.53515625, 0.5390625, 0.54296875, 0.546875, 0.55078125,
				0.5546875, 0.55859375, 0.5625, 0.56640625, 0.5703125, 0.57421875, 0.578125,
				0.58203125, 0.5859375, 0.58984375, 0.59375, 0.59765625, 0.6015625, 0.60546875,
				0.609375, 0.61328125, 0.6171875, 0.62109375, 0.625, 0.62890625, 0.6328125,
				0.63671875, 0.640625, 0.64453125, 0.6484375, 0.65234375, 0.65625, 0.66015625,
				0.6640625, 0.66796875, 0.671875, 0.67578125, 0.6796875, 0.68359375, 0.6875,
				0.69140625, 0.6953125, 0.69921875, 0.703125, 0.70703125, 0.7109375, 0.71484375,
				0.71875, 0.72265625, 0.7265625, 0.73046875, 0.734375, 0.73828125, 0.7421875,
				0.74609375, 0.75, 0.75390625, 0.7578125, 0.76171875, 0.765625, 0.76953125,
				0.7734375, 0.77734375, 0.78125, 0.78515625, 0.7890625, 0.79296875, 0.796875,
				0.80078125, 0.8046875, 0.80859375, 0.8125, 0.81640625, 0.8203125, 0.82421875,
				0.828125, 0.83203125, 0.8359375, 0.83984375, 0.84375, 0.84765625, 0.8515625,
				0.85546875, 0.859375, 0.86328125, 0.8671875, 0.87109375, 0.875, 0.87890625,
				0.8828125, 0.88671875, 0.890625, 0.89453125, 0.8984375, 0.90234375, 0.90625,
				0.91015625, 0.9140625, 0.91796875, 0.921875, 0.92578125, 0.9296875, 0.93359375,
				0.9375, 0.94140625, 0.9453125, 0.94921875, 0.953125, 0.95703125, 0.9609375,
				0.96484375, 0.96875, 0.97265625, 0.9765625, 0.98046875, 0.984375, 0.98828125,
				0.9921875, 0.99609375, -1.0, -0.99609375, -0.9921875, -0.98828125, -0.984375,
				-0.98046875, -0.9765625, -0.97265625, -0.96875, -0.96484375, -0.9609375,
				-0.95703125, -0.953125, -0.94921875, -0.9453125, -0.94140625, -0.9375, -0.93359375,
				-0.9296875, -0.92578125, -0.921875, -0.91796875, -0.9140625, -0.91015625, -0.90625,
				-0.90234375, -0.8984375, -0.89453125, -0.890625, -0.88671875, -0.8828125,
				-0.87890625, -0.875, -0.87109375, -0.8671875, -0.86328125, -0.859375, -0.85546875,
				-0.8515625, -0.84765625, -0.84375, -0.83984375, -0.8359375, -0.83203125, -0.828125,
				-0.82421875, -0.8203125, -0.81640625, -0.8125, -0.80859375, -0.8046875, -0.80078125,
				-0.796875, -0.79296875, -0.7890625, -0.78515625, -0.78125, -0.77734375, -0.7734375,
				-0.76953125, -0.765625, -0.76171875, -0.7578125, -0.75390625, -0.75, -0.74609375,
				-0.7421875, -0.73828125, -0.734375, -0.73046875, -0.7265625, -0.72265625, -0.71875,
				-0.71484375, -0.7109375, -0.70703125, -0.703125, -0.69921875, -0.6953125,
				-0.69140625, -0.6875, -0.68359375, -0.6796875, -0.67578125, -0.671875, -0.66796875,
				-0.6640625, -0.66015625, -0.65625, -0.65234375, -0.6484375, -0.64453125, -0.640625,
				-0.63671875, -0.6328125, -0.62890625, -0.625, -0.62109375, -0.6171875, -0.61328125,
				-0.609375, -0.60546875, -0.6015625, -0.59765625, -0.59375, -0.58984375, -0.5859375,
				-0.58203125, -0.578125, -0.57421875, -0.5703125, -0.56640625, -0.5625, -0.55859375,
				-0.5546875, -0.55078125, -0.546875, -0.54296875, -0.5390625, -0.53515625, -0.53125,
				-0.52734375, -0.5234375, -0.51953125, -0.515625, -0.51171875, -0.5078125,
				-0.50390625, -0.5, -0.49609375, -0.4921875, -0.48828125, -0.484375, -0.48046875,
				-0.4765625, -0.47265625, -0.46875, -0.46484375, -0.4609375, -0.45703125, -0.453125,
				-0.44921875, -0.4453125, -0.44140625, -0.4375, -0.43359375, -0.4296875, -0.42578125,
				-0.421875, -0.41796875, -0.4140625, -0.41015625, -0.40625, -0.40234375, -0.3984375,
				-0.39453125, -0.390625, -0.38671875, -0.3828125, -0.37890625, -0.375, -0.37109375,
				-0.3671875, -0.36328125, -0.359375, -0.35546875, -0.3515625, -0.34765625, -0.34375,
				-0.33984375, -0.3359375, -0.33203125, -0.328125, -0.32421875, -0.3203125,
				-0.31640625, -0.3125, -0.30859375, -0.3046875, -0.30078125, -0.296875, -0.29296875,
				-0.2890625, -0.28515625, -0.28125, -0.27734375, -0.2734375, -0.26953125, -0.265625,
				-0.26171875, -0.2578125, -0.25390625, -0.25, -0.24609375, -0.2421875, -0.23828125,
				-0.234375, -0.23046875, -0.2265625, -0.22265625, -0.21875, -0.21484375, -0.2109375,
				-0.20703125, -0.203125, -0.19921875, -0.1953125, -0.19140625, -0.1875, -0.18359375,
				-0.1796875, -0.17578125, -0.171875, -0.16796875, -0.1640625, -0.16015625, -0.15625,
				-0.15234375, -0.1484375, -0.14453125, -0.140625, -0.13671875, -0.1328125,
				-0.12890625, -0.125, -0.12109375, -0.1171875, -0.11328125, -0.109375, -0.10546875,
				-0.1015625, -0.09765625, -0.09375, -0.08984375, -0.0859375, -0.08203125, -0.078125,
				-0.07421875, -0.0703125, -0.06640625, -0.0625, -0.05859375, -0.0546875, -0.05078125,
				-0.046875, -0.04296875, -0.0390625, -0.03515625, -0.03125, -0.02734375, -0.0234375,
				-0.01953125, -0.015625, -0.01171875, -0.0078125, -0.00390625,
			]),
		},
		{
			name: "lemmbox HD rounded",
			expression: 1.0,
			samples: centerWave([
				0.0, 0.01, 0.02, 0.04, 0.05, 0.06, 0.07, 0.09, 0.1, 0.11, 0.12, 0.13, 0.15, 0.16,
				0.17, 0.18, 0.2, 0.21, 0.22, 0.23, 0.24, 0.25, 0.27, 0.28, 0.29, 0.3, 0.31, 0.33,
				0.34, 0.35, 0.36, 0.37, 0.38, 0.39, 0.41, 0.42, 0.43, 0.44, 0.45, 0.46, 0.47, 0.48,
				0.49, 0.5, 0.51, 0.52, 0.53, 0.55, 0.56, 0.57, 0.58, 0.59, 0.6, 0.61, 0.62, 0.62,
				0.63, 0.64, 0.65, 0.66, 0.67, 0.68, 0.69, 0.7, 0.71, 0.72, 0.72, 0.73, 0.74, 0.75,
				0.76, 0.77, 0.77, 0.78, 0.79, 0.8, 0.8, 0.81, 0.82, 0.82, 0.83, 0.84, 0.84, 0.85,
				0.86, 0.86, 0.87, 0.88, 0.88, 0.89, 0.89, 0.9, 0.9, 0.91, 0.91, 0.92, 0.92, 0.93,
				0.93, 0.94, 0.94, 0.95, 0.95, 0.95, 0.96, 0.96, 0.96, 0.97, 0.97, 0.97, 0.98, 0.98,
				0.98, 0.98, 0.99, 0.99, 0.99, 0.99, 0.99, 0.99, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
				1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
				1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
				1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
				1, 1, 1, 1, 1, 1, 0.99, 0.99, 0.99, 0.99, 0.99, 0.99, 0.98, 0.98, 0.98, 0.98, 0.97,
				0.97, 0.97, 0.96, 0.96, 0.96, 0.95, 0.95, 0.95, 0.94, 0.94, 0.93, 0.93, 0.92, 0.92,
				0.91, 0.91, 0.9, 0.9, 0.89, 0.89, 0.88, 0.88, 0.87, 0.86, 0.86, 0.85, 0.84, 0.84,
				0.83, 0.82, 0.82, 0.81, 0.8, 0.8, 0.79, 0.78, 0.77, 0.77, 0.76, 0.75, 0.74, 0.73,
				0.72, 0.72, 0.71, 0.7, 0.69, 0.68, 0.67, 0.66, 0.65, 0.64, 0.63, 0.62, 0.62, 0.61,
				0.6, 0.59, 0.58, 0.57, 0.56, 0.55, 0.53, 0.52, 0.51, 0.5, 0.49, 0.48, 0.47, 0.46,
				0.45, 0.44, 0.43, 0.42, 0.41, 0.39, 0.38, 0.37, 0.36, 0.35, 0.34, 0.33, 0.31, 0.3,
				0.29, 0.28, 0.27, 0.25, 0.24, 0.23, 0.22, 0.21, 0.2, 0.18, 0.17, 0.16, 0.15, 0.13,
				0.12, 0.11, 0.1, 0.09, 0.07, 0.06, 0.05, 0.04, 0.02, 0.01, 0.0, -0.01, -0.02, -0.04,
				-0.05, -0.06, -0.07, -0.09, -0.1, -0.11, -0.12, -0.13, -0.15, -0.16, -0.17, -0.18,
				-0.2, -0.21, -0.22, -0.23, -0.24, -0.25, -0.27, -0.28, -0.29, -0.3, -0.31, -0.33,
				-0.34, -0.35, -0.36, -0.37, -0.38, -0.39, -0.41, -0.42, -0.43, -0.44, -0.45, -0.46,
				-0.47, -0.48, -0.49, -0.5, -0.51, -0.52, -0.53, -0.55, -0.56, -0.57, -0.58, -0.59,
				-0.6, -0.61, -0.62, -0.62, -0.63, -0.64, -0.65, -0.66, -0.67, -0.68, -0.69, -0.7,
				-0.71, -0.72, -0.72, -0.73, -0.74, -0.75, -0.76, -0.77, -0.77, -0.78, -0.79, -0.8,
				-0.8, -0.81, -0.82, -0.82, -0.83, -0.84, -0.84, -0.85, -0.86, -0.86, -0.87, -0.88,
				-0.88, -0.89, -0.89, -0.9, -0.9, -0.91, -0.91, -0.92, -0.92, -0.93, -0.93, -0.94,
				-0.94, -0.95, -0.95, -0.95, -0.96, -0.96, -0.96, -0.97, -0.97, -0.97, -0.98, -0.98,
				-0.98, -0.98, -0.99, -0.99, -0.99, -0.99, -0.99, -0.99, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -0.99,
				-0.99, -0.99, -0.99, -0.99, -0.99, -0.98, -0.98, -0.98, -0.98, -0.97, -0.97, -0.97,
				-0.96, -0.96, -0.96, -0.95, -0.95, -0.95, -0.94, -0.94, -0.93, -0.93, -0.92, -0.92,
				-0.91, -0.91, -0.9, -0.9, -0.89, -0.89, -0.88, -0.88, -0.87, -0.86, -0.86, -0.85,
				-0.84, -0.84, -0.83, -0.82, -0.82, -0.81, -0.8, -0.8, -0.79, -0.78, -0.77, -0.77,
				-0.76, -0.75, -0.74, -0.73, -0.72, -0.72, -0.71, -0.7, -0.69, -0.68, -0.67, -0.66,
				-0.65, -0.64, -0.63, -0.62, -0.62, -0.61, -0.6, -0.59, -0.58, -0.57, -0.56, -0.55,
				-0.53, -0.52, -0.51, -0.5, -0.49, -0.48, -0.47, -0.46, -0.45, -0.44, -0.43, -0.42,
				-0.41, -0.39, -0.38, -0.37, -0.36, -0.35, -0.34, -0.33, -0.31, -0.3, -0.29, -0.28,
				-0.27, -0.25, -0.24, -0.23, -0.22, -0.21, -0.2, -0.18, -0.17, -0.16, -0.15, -0.13,
				-0.12, -0.11, -0.1, -0.09, -0.07, -0.06, -0.05, -0.04, -0.02, -0.01,
			]),
		},
		{
			name: "lemmbox trapasquare",
			expression: 1.0,
			samples: centerWave([
				0.0, 0.012271538285719925, 0.024541228522912288, 0.03680722294135883,
				0.049067674327418015, 0.06132073630220858, 0.07356456359966743, 0.0857973123444399,
				0.0980171403295606, 0.11022220729388306, 0.1224106751992162, 0.13458070850712617,
				0.14673047445536175, 0.15885814333386145, 0.17096188876030122, 0.18303988795514095,
				0.19509032201612825, 0.20711137619221856, 0.2191012401568698, 0.2310581082806711,
				0.24298017990326387, 0.25486565960451457, 0.26671275747489837, 0.27851968938505306,
				0.29028467725446233, 0.3020059493192281, 0.3136817403988915, 0.3253102921622629,
				0.33688985339222005, 0.34841868024943456, 0.3598950365349881, 0.3713171939518375,
				0.3826834323650898, 0.3939920400610481, 0.40524131400498986, 0.41642956009763715,
				0.4275550934302821, 0.43861623853852766, 0.44961132965460654, 0.46053871095824,
				0.47139673682599764, 0.4821837720791227, 0.49289819222978404, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
				0.49289819222978415, 0.4821837720791229, 0.47139673682599786, 0.4605387109582402,
				0.4496113296546069, 0.43861623853852755, 0.42755509343028203, 0.41642956009763715,
				0.4052413140049899, 0.39399204006104815, 0.3826834323650899, 0.3713171939518377,
				0.35989503653498833, 0.3484186802494348, 0.33688985339222033, 0.32531029216226326,
				0.3136817403988914, 0.30200594931922803, 0.2902846772544624, 0.27851968938505317,
				0.2667127574748985, 0.2548656596045147, 0.24298017990326407, 0.23105810828067133,
				0.21910124015687005, 0.20711137619221884, 0.1950903220161286, 0.1830398879551409,
				0.17096188876030122, 0.15885814333386147, 0.1467304744553618, 0.13458070850712628,
				0.12241067519921635, 0.11022220729388324, 0.09801714032956083, 0.08579731234444016,
				0.07356456359966773, 0.06132073630220849, 0.049067674327417966, 0.03680722294135883,
				0.024541228522912326, 0.012271538285720007, 1.2246467991473532e-16,
				-0.012271538285719762, -0.02454122852291208, -0.03680722294135858,
				-0.049067674327417724, -0.061320736302208245, -0.0735645635996675,
				-0.08579731234443992, -0.09801714032956059, -0.110222207293883, -0.1224106751992161,
				-0.13458070850712606, -0.14673047445536158, -0.15885814333386122,
				-0.17096188876030097, -0.18303988795514065, -0.19509032201612836,
				-0.2071113761922186, -0.2191012401568698, -0.23105810828067108,
				-0.24298017990326382, -0.25486565960451446, -0.26671275747489825,
				-0.2785196893850529, -0.2902846772544621, -0.3020059493192278, -0.3136817403988912,
				-0.325310292162263, -0.33688985339222005, -0.34841868024943456, -0.3598950365349881,
				-0.37131719395183743, -0.38268343236508967, -0.39399204006104793,
				-0.4052413140049897, -0.41642956009763693, -0.4275550934302818, -0.4386162385385273,
				-0.44961132965460665, -0.46053871095824006, -0.47139673682599764,
				-0.48218377207912266, -0.4928981922297839, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0,
				-1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -1.0, -0.49289819222978426,
				-0.4821837720791226, -0.4713967368259979, -0.46053871095823995, -0.449611329654607,
				-0.43861623853852766, -0.42755509343028253, -0.41642956009763726,
				-0.4052413140049904, -0.39399204006104827, -0.3826834323650904, -0.3713171939518378,
				-0.359895036534988, -0.3484186802494349, -0.33688985339222, -0.32531029216226337,
				-0.3136817403988915, -0.3020059493192286, -0.2902846772544625, -0.27851968938505367,
				-0.2667127574748986, -0.2548656596045144, -0.24298017990326418, -0.231058108280671,
				-0.21910124015687016, -0.20711137619221853, -0.19509032201612872,
				-0.183039887955141, -0.17096188876030177, -0.15885814333386158, -0.1467304744553624,
				-0.13458070850712642, -0.12241067519921603, -0.11022220729388336,
				-0.0980171403295605, -0.08579731234444028, -0.07356456359966741,
				-0.06132073630220906, -0.04906767432741809, -0.036807222941359394,
				-0.024541228522912448, -0.012271538285720572,
			]),
		},
	]);
	public static chipWaves: DictionaryArray<ChipWave> = rawChipToIntegrated(Config.rawChipWaves);
	public static rawRawChipWaves: DictionaryArray<ChipWave> = Config.rawChipWaves;

	public static firstIndexForSamplesInChipWaveList: number = Config.chipWaves.length;

	// Noise waves have too many samples to write by hand, they're generated on-demand by getDrumWave instead.
	public static readonly chipNoises: DictionaryArray<ChipNoise> = toNameMap([
		{
			name: "retro",
			expression: 0.25,
			basePitch: 69,
			pitchFilterMult: 1024.0,
			isSoft: false,
			samples: null,
		},
		{
			name: "white",
			expression: 1.0,
			basePitch: 69,
			pitchFilterMult: 8.0,
			isSoft: true,
			samples: null,
		},
		// The "clang" and "buzz" noises are based on similar noises in the modded beepbox.
		{
			name: "clang",
			expression: 0.4,
			basePitch: 69,
			pitchFilterMult: 1024.0,
			isSoft: false,
			samples: null,
		},
		{
			name: "buzz",
			expression: 0.3,
			basePitch: 69,
			pitchFilterMult: 1024.0,
			isSoft: false,
			samples: null,
		},
		{
			name: "hollow",
			expression: 1.5,
			basePitch: 96,
			pitchFilterMult: 1.0,
			isSoft: true,
			samples: null,
		},
		{
			name: "shine",
			expression: 1.0,
			basePitch: 69,
			pitchFilterMult: 1024.0,
			isSoft: false,
			samples: null,
		}, // Identical to buzz but louder.
		{
			name: "deep",
			expression: 1.5,
			basePitch: 120,
			pitchFilterMult: 1024.0,
			isSoft: true,
			samples: null,
		},
		{
			name: "cutter",
			expression: 0.005,
			basePitch: 96,
			pitchFilterMult: 1024.0,
			isSoft: false,
			samples: null,
		},
		{
			name: "metallic",
			expression: 1.0,
			basePitch: 96,
			pitchFilterMult: 1024.0,
			isSoft: false,
			samples: null,
		},
		{
			name: "static",
			expression: 1.0,
			basePitch: 96,
			pitchFilterMult: 1024.0,
			isSoft: false,
			samples: null,
		},
		// technically these are from the pandorasbox beta but whatever
		{
			name: "1-bit white",
			expression: 0.5,
			basePitch: 74.41,
			pitchFilterMult: 1024.0,
			isSoft: false,
			samples: null,
		},
		{
			name: "1-bit metallic",
			expression: 0.5,
			basePitch: 86.41,
			pitchFilterMult: 1024.0,
			isSoft: false,
			samples: null,
		},
		// ultrabox noises
		{
			name: "crackling",
			expression: 0.9,
			basePitch: 69,
			pitchFilterMult: 1024.0,
			isSoft: false,
			samples: null,
		},
		{
			name: "pink",
			expression: 1.0,
			basePitch: 69,
			pitchFilterMult: 8.0,
			isSoft: true,
			samples: null,
		},
		{
			name: "brownian",
			expression: 1.0,
			basePitch: 69,
			pitchFilterMult: 8.0,
			isSoft: true,
			samples: null,
		},
	]);

	public static readonly filterFreqStep: number = 1.0 / 4.0;
	public static readonly filterFreqRange: number = 34;
	public static readonly filterFreqReferenceSetting: number = 28;
	public static readonly filterFreqReferenceHz: number = 8000.0;
	public static readonly filterFreqMaxHz: number =
		Config.filterFreqReferenceHz *
		2.0 **
			(Config.filterFreqStep *
				(Config.filterFreqRange - 1 - Config.filterFreqReferenceSetting)); // ~19khz
	public static readonly filterFreqMinHz: number = 8.0;
	public static readonly filterGainRange: number = 15;
	public static readonly filterGainCenter: number = 7;
	public static readonly filterGainStep: number = 1.0 / 2.0;
	public static readonly filterMaxPoints: number = 12;
	public static readonly filterTypeNames: ReadonlyArray<string> = [
		"low-pass",
		"high-pass",
		"peak",
	]; // See FilterType enum above.
	public static readonly filterMorphCount: number = 10; // Number of filter shapes allowed for modulating between. Counts the 0/default position.

	public static readonly filterSimpleCutRange: number = 11;
	public static readonly filterSimplePeakRange: number = 8;

	public static readonly fadeInRange: number = 10;
	public static readonly fadeOutTicks: ReadonlyArray<number> = [
		-24, -12, -6, -3, -1, 6, 12, 24, 48, 72, 96,
	];
	public static readonly fadeOutNeutral: number = 4;
	public static readonly drumsetFadeOutTicks: number = 48;
	public static readonly transitions: DictionaryArray<Transition> = toNameMap([
		{
			name: "normal",
			isSeamless: false,
			continues: false,
			slides: false,
			slideTicks: 3,
			includeAdjacentPatterns: false,
		},
		{
			name: "interrupt",
			isSeamless: true,
			continues: false,
			slides: false,
			slideTicks: 3,
			includeAdjacentPatterns: true,
		},
		{
			name: "continue",
			isSeamless: true,
			continues: true,
			slides: false,
			slideTicks: 3,
			includeAdjacentPatterns: true,
		},
		{
			name: "slide",
			isSeamless: true,
			continues: false,
			slides: true,
			slideTicks: 3,
			includeAdjacentPatterns: true,
		},
		{
			name: "slide in pattern",
			isSeamless: true,
			continues: false,
			slides: true,
			slideTicks: 3,
			includeAdjacentPatterns: false,
		},
	]);
	public static readonly vibratos: DictionaryArray<Vibrato> = toNameMap([
		{ name: "none", amplitude: 0.0, type: 0, delayTicks: 0 },
		{ name: "light", amplitude: 0.15, type: 0, delayTicks: 0 },
		{ name: "delayed", amplitude: 0.3, type: 0, delayTicks: 37 }, // It will fade in over the previous two ticks.
		{ name: "heavy", amplitude: 0.45, type: 0, delayTicks: 0 },
		{ name: "shaky", amplitude: 0.1, type: 1, delayTicks: 0 },
		//    { name: "very shaky", amplitude: 1, type: 0, delayTicks: 0 },
		// { name: "insane", amplitude: 10, type: 1, delayTicks: 0 },
		// todbox vibratos
		// 	{ name: "super insane", amplitude: 30, type: 1, delayTicks: 1 },
		// wackybox
		// 	 { name: "quiver", amplitude: 0.001, type: 0, delayTicks: 0 },
		//  { name: "wub-wub", amplitude: 10.0, type: 0, delayTicks: 0 },
		//     { name: "quiver delayed", amplitude: 0.001, type: 0, delayTicks: 18 },
		//  { name: "vibrate", amplitude: 0.08, type: 0, delayTicks: 0 },
		// { name: "too much wub ⚠", amplitude: 30.0, type: 0, delayTicks: 18 },
		// too much wub breaks things just a little bit at it's original amplitude
		// sandbox
	]);
	public static readonly vibratoTypes: DictionaryArray<VibratoType> = toNameMap([
		{ name: "normal", periodsSeconds: [0.14], period: 0.14 },
		{ name: "shaky", periodsSeconds: [0.11, 1.618 * 0.11, 3 * 0.11], period: 266.97 }, // LCM of all periods
	]);
	// This array is more or less a linear step by 0.1 but there's a bit of range added at the start to hit specific ratios, and the end starts to grow faster.
	//                                                             0       1      2    3     4      5    6    7      8     9   10   11 12   13   14   15   16   17   18   19   20   21 22   23   24   25   26   27   28   29   30   31 32   33   34   35   36   37   38    39  40   41 42    43   44   45   46 47   48 49 50
	public static readonly arpSpeedScale: ReadonlyArray<number> = [
		0,
		0.0625,
		0.125,
		0.2,
		0.25,
		1 / 3,
		0.4,
		0.5,
		2 / 3,
		0.75,
		0.8,
		0.9,
		1,
		1.1,
		1.2,
		1.3,
		1.4,
		1.5,
		1.6,
		1.7,
		1.8,
		1.9,
		2,
		2.1,
		2.2,
		2.3,
		2.4,
		2.5,
		2.6,
		2.7,
		2.8,
		2.9,
		3,
		3.1,
		3.2,
		3.3,
		3.4,
		3.5,
		3.6,
		3.7,
		3.8,
		3.9,
		4,
		4.15,
		4.3,
		4.5,
		4.8,
		5,
		5.5,
		6,
		8,
	];
	public static readonly unisons: DictionaryArray<Unison> = toNameMap([
		{ name: "none", voices: 1, spread: 0.0, offset: 0.0, expression: 1.4, sign: 1.0 },
		{ name: "shimmer", voices: 2, spread: 0.018, offset: 0.0, expression: 0.8, sign: 1.0 },
		{ name: "hum", voices: 2, spread: 0.045, offset: 0.0, expression: 1.0, sign: 1.0 },
		{ name: "honky tonk", voices: 2, spread: 0.09, offset: 0.0, expression: 1.0, sign: 1.0 },
		{ name: "dissonant", voices: 2, spread: 0.25, offset: 0.0, expression: 0.9, sign: 1.0 },

		{ name: "piano", voices: 2, spread: 0.01, offset: 0.0, expression: 1.0, sign: 0.7 },
		{ name: "spinner", voices: 2, spread: 0.02, offset: 0.0, expression: 1.0, sign: 1.0 },
		{ name: "recurve", voices: 2, spread: 0.005, offset: 0.0, expression: 1.0, sign: 1.0 },
		{ name: "vary", voices: 2, spread: 0.002, offset: 0.0, expression: 0.85, sign: 1.6 },

		{ name: "bowed", voices: 2, spread: 0.02, offset: 0.0, expression: 1.0, sign: -1.0 },
		{ name: "hold", voices: 2, spread: 0.003, offset: 0.0, expression: 0.8, sign: -2.5 },

		{ name: "chorus", voices: 9, spread: 0.22, offset: 0, expression: 0.15, sign: 1.0 },
		{ name: "bow", voices: 9, spread: 0.006, offset: 0, expression: 0.15, sign: 0.5 },

		{ name: "octave", voices: 2, spread: 6.0, offset: 6.0, expression: 0.8, sign: 1.0 },
		{
			name: "double octave",
			voices: 3,
			spread: 12.0,
			offset: 0.0,
			expression: 0.63,
			sign: 1.0,
		},
		{ name: "triple octave", voices: 4, spread: 18.0, offset: 6.0, expression: 0.5, sign: 1.0 },
		{
			name: "weird octave",
			voices: 2,
			spread: 5.85,
			offset: 5.85,
			expression: 0.75,
			sign: 1.0,
		},
		{ name: "fluctuate", voices: 2, spread: 12, offset: 0.0, expression: 1.0, sign: 1.0 },

		{ name: "fifth", voices: 2, spread: 3.5, offset: 3.5, expression: 0.9, sign: 1.0 },
		{
			name: "alternate fifth",
			voices: 2,
			spread: 2.5,
			offset: -2.5,
			expression: 0.9,
			sign: 1.0,
		},
		{ name: "lone fifth", voices: 1, spread: 0.0, offset: 7.0, expression: 1.4, sign: 1.0 },

		{ name: "fourth", voices: 2, spread: 4, offset: 4, expression: 0.95, sign: 1.0 },

		{ name: "warbled", voices: 2, spread: 0.25, offset: 0.05, expression: 0.9, sign: -0.8 },
		{
			name: "hecking gosh",
			voices: 2,
			spread: 6.25,
			offset: -6.0,
			expression: 0.8,
			sign: -0.7,
		},
		{ name: "detune", voices: 1, spread: 0.0, offset: 0.25, expression: 1.0, sign: 1.0 },
		{ name: "rising", voices: 2, spread: 1.0, offset: 0.7, expression: 0.95, sign: 1.0 },
		{ name: "vibrate", voices: 2, spread: 3.5, offset: 7, expression: 0.975, sign: 1.0 },
		{ name: "bass", voices: 1, spread: 0, offset: -7, expression: 1.0, sign: 1.0 },
		{ name: "dirty", voices: 2, spread: 0, offset: 0.1, expression: 0.975, sign: 1.0 },
		{ name: "stationary", voices: 2, spread: 3.5, offset: 0.0, expression: 0.9, sign: 1.0 },
		{ name: "voiced", voices: 2, spread: 9.5, offset: 0.0, expression: 1.0, sign: 1.0 },
		{ name: "inject", voices: 2, spread: 6.0, offset: 0.4, expression: 1.0, sign: 1.0 },
		{ name: "askewed", voices: 2, spread: 0.0, offset: 0.42, expression: 0.7, sign: 1.0 },
		{ name: "resonance", voices: 2, spread: 0.0025, offset: 0.1, expression: 0.8, sign: -1.5 },
		{ name: "augmented", voices: 4, spread: 6, offset: 6, expression: 0.5, sign: 1.0 },
		{ name: "diminished", voices: 5, spread: 6, offset: 6, expression: 0.4, sign: 1.0 },
		{ name: "block", voices: 9, spread: 6, offset: 6, expression: 0.15, sign: 0.8 },
		{
			name: "extraterrestrial",
			voices: 6,
			spread: 15.2,
			offset: -6,
			expression: 0.35,
			sign: 0.7,
		},
		{ name: "hyper", voices: 2, spread: 0.03, offset: -0.02, expression: 0.85, sign: 0.7 },
		{ name: "broke", voices: 2, spread: 0.0, offset: -0.3, expression: 0.8, sign: 1.0 },
		{ name: "energetic", voices: 2, spread: 6.15, offset: 6.435, expression: 0.85, sign: 0.9 },
		{ name: "bent", voices: 2, spread: 9.5, offset: 4.5, expression: 0.8, sign: -0.6 },
		{ name: "offtune", voices: 2, spread: 0.4, offset: 0.4, expression: 0.9, sign: 1.0 },

		{ name: "peak", voices: 2, spread: 12.038, offset: 12.01, expression: 0.85, sign: 0.9 },
		{
			name: "deep shift",
			voices: 2,
			spread: 12.03,
			offset: -17.01,
			expression: 0.85,
			sign: 1.2,
		},
		{ name: "buried", voices: 2, spread: 0.036, offset: -36.0, expression: 1.4, sign: 1.0 },
		{ name: "corrupt", voices: 2, spread: 18.0, offset: 48.0, expression: 0.7, sign: 0.7 },
		{ name: "thin", voices: 1, spread: 0.0, offset: 50.0, expression: 1.0, sign: 1.0 },

		{ name: "FART", voices: 2, spread: 13, offset: -5, expression: 1.0, sign: -3 },
		// for modbox; voices = riffapp, spread = intervals, offset = offsets, expression = volume, and sign = signs
	]);
	public static readonly effectNames: ReadonlyArray<string> = [
		"reverb",
		"chorus",
		"panning",
		"distortion",
		"bitcrusher",
		"note filter",
		"echo",
		"pitch shift",
		"detune",
		"vibrato",
		"transition type",
		"chord type",
		"note range",
		"ring mod",
		"granular",
		"phaser",
		"",
		"invert wave",
	];
	public static readonly effectOrder: ReadonlyArray<EffectType> = [
		EffectType.panning,
		EffectType.transition,
		EffectType.chord,
		EffectType.pitchShift,
		EffectType.detune,
		EffectType.vibrato,
		EffectType.noteFilter,
		EffectType.granular,
		EffectType.distortion,
		EffectType.bitcrusher,
		EffectType.chorus,
		EffectType.echo,
		EffectType.reverb,
		EffectType.ringModulation,
		EffectType.phaser,
		EffectType.invertWave,
		EffectType.noteRange,
	];
	public static readonly noteSizeMax: number = 6;
	public static readonly volumeRange: number = 50;
	// Beepbox's old volume scale used factor -0.5 and was [0~7] had roughly value 6 = 0.125 power. This new value is chosen to have -21 be the same,
	// given that the new scale is [-25~25]. This is such that conversion between the scales is roughly equivalent by satisfying (0.5*6 = 0.1428*21)
	public static readonly volumeLogScale: number = 0.1428;
	public static readonly panCenter: number = 50;
	public static readonly panMax: number = Config.panCenter * 2;
	public static readonly panDelaySecondsMax: number = 0.001;
	public static readonly ringModRange: number = 8;
	public static readonly ringModHzRange: number = 64;
	public static readonly ringModMinHz: number = 20;
	public static readonly ringModMaxHz: number = 4400;
	public static readonly rmHzOffsetCenter: number = 200;
	public static readonly rmHzOffsetMax: number = 400;
	public static readonly rmHzOffsetMin: number = 0;
	public static readonly granularRange: number = 10;
	public static readonly grainSizeMin: number = 40;
	public static readonly grainSizeMax: number = 2000;
	public static readonly grainSizeStep: number = 40;
	public static readonly grainRangeMax: number = 1600;
	public static readonly grainAmountsMax: number = 10; // 2^grainAmountsMax is what is actually used
	public static readonly granularEnvelopeType: number = GranularEnvelopeType.parabolic; // here you can change which envelope implementation is used for grains (RaisedCosineBell still needs work)
	public static readonly chorusRange: number = 8;
	public static readonly chorusPeriodSeconds: number = 2.0;
	public static readonly chorusDelayRange: number = 0.0034;
	public static readonly chorusDelayOffsets: ReadonlyArray<ReadonlyArray<number>> = [
		[1.51, 2.1, 3.35],
		[1.47, 2.15, 3.25],
	];
	public static readonly chorusPhaseOffsets: ReadonlyArray<ReadonlyArray<number>> = [
		[0.0, 2.1, 4.2],
		[3.2, 5.3, 1.0],
	];
	public static readonly chorusMaxDelay: number =
		Config.chorusDelayRange *
		(1.0 +
			Config.chorusDelayOffsets[0]
				.concat(Config.chorusDelayOffsets[1])
				.reduce((x, y) => Math.max(x, y)));
	public static readonly chords: DictionaryArray<Chord> = toNameMap([
		{
			name: "simultaneous",
			customInterval: false,
			arpeggiates: false,
			strumParts: 0,
			singleTone: false,
		},
		{
			name: "strum",
			customInterval: false,
			arpeggiates: false,
			strumParts: 1,
			singleTone: false,
		},
		{
			name: "arpeggio",
			customInterval: false,
			arpeggiates: true,
			strumParts: 0,
			singleTone: true,
		},
		{
			name: "custom interval",
			customInterval: true,
			arpeggiates: false,
			strumParts: 0,
			singleTone: true,
		},
		{
			name: "monophonic",
			customInterval: false,
			arpeggiates: false,
			strumParts: 0,
			singleTone: true,
		},
	]);
	public static readonly maxChordSize: number = 9;
	public static readonly operatorCount: number = 4;
	public static readonly maxPitchOrOperatorCount: number = Math.max(
		Config.maxChordSize,
		Config.operatorCount + 2,
	);
	public static readonly algorithms: DictionaryArray<Algorithm> = toNameMap([
		{
			name: "1←(2 3 4)",
			carrierCount: 1,
			associatedCarrier: [1, 1, 1, 1],
			modulatedBy: [[2, 3, 4], [], [], []],
		},
		{
			name: "1←(2 3←4)",
			carrierCount: 1,
			associatedCarrier: [1, 1, 1, 1],
			modulatedBy: [[2, 3], [], [4], []],
		},
		{
			name: "1←2←(3 4)",
			carrierCount: 1,
			associatedCarrier: [1, 1, 1, 1],
			modulatedBy: [[2], [3, 4], [], []],
		},
		{
			name: "1←(2 3)←4",
			carrierCount: 1,
			associatedCarrier: [1, 1, 1, 1],
			modulatedBy: [[2, 3], [4], [4], []],
		},
		{
			name: "1←2←3←4",
			carrierCount: 1,
			associatedCarrier: [1, 1, 1, 1],
			modulatedBy: [[2], [3], [4], []],
		},
		{
			name: "1←3 2←4",
			carrierCount: 2,
			associatedCarrier: [1, 2, 1, 2],
			modulatedBy: [[3], [4], [], []],
		},
		{
			name: "1 2←(3 4)",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2],
			modulatedBy: [[], [3, 4], [], []],
		},
		{
			name: "1 2←3←4",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2],
			modulatedBy: [[], [3], [4], []],
		},
		{
			name: "(1 2)←3←4",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2],
			modulatedBy: [[3], [3], [4], []],
		},
		{
			name: "(1 2)←(3 4)",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2],
			modulatedBy: [[3, 4], [3, 4], [], []],
		},
		{
			name: "1 2 3←4",
			carrierCount: 3,
			associatedCarrier: [1, 2, 3, 3],
			modulatedBy: [[], [], [4], []],
		},
		{
			name: "(1 2 3)←4",
			carrierCount: 3,
			associatedCarrier: [1, 2, 3, 3],
			modulatedBy: [[4], [4], [4], []],
		},
		{
			name: "1 2 3 4",
			carrierCount: 4,
			associatedCarrier: [1, 2, 3, 4],
			modulatedBy: [[], [], [], []],
		},
		{
			name: "1←(2 3) 2←4",
			carrierCount: 2,
			associatedCarrier: [1, 2, 1, 2],
			modulatedBy: [[2, 3], [4], [], []],
		},
		{
			name: "1←(2 (3 (4",
			carrierCount: 3,
			associatedCarrier: [1, 2, 3, 3],
			modulatedBy: [[2, 3, 4], [3, 4], [4], []],
		},
	]);
	public static readonly algorithmsOpl3: DictionaryArray<Algorithm> = toNameMap([
		// OPL3 4-op algorithms (8 algorithms matching YMF262 4-op mode)
		{
			// All serial: 1←2←3←4
			name: "1←2←3←4",
			carrierCount: 1,
			associatedCarrier: [1, 1, 1, 1],
			modulatedBy: [[2], [3], [4], []],
		},
		{
			// Two parallel pairs: (1←2) || (3←4)
			name: "1←2\u20023←4",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2],
			modulatedBy: [[2], [], [4], []],
		},
		{
			// 1←2←3 + 4 alone
			name: "1←2←3\u20024",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2],
			modulatedBy: [[2], [3], [], []],
		},
		{
			// Parallel pairs cross: (1←2) || (3←4), alt fb
			name: "1←2\u20023→4",
			carrierCount: 2,
			associatedCarrier: [1, 2, 1, 2],
			modulatedBy: [[2], [4], [], []],
		},
		{
			// All independent: 1 2 3←4
			name: "1\u20022\u20023←4",
			carrierCount: 3,
			associatedCarrier: [1, 2, 3, 3],
			modulatedBy: [[], [], [4], []],
		},
		{
			// 1←2 || 3←4 (alt)
			name: "1←2\u20023←4 alt",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2],
			modulatedBy: [[2], [], [4], []],
		},
		{
			// All parallel: 1 2 3 4
			name: "1\u20022\u20023\u20024",
			carrierCount: 4,
			associatedCarrier: [1, 2, 3, 4],
			modulatedBy: [[], [], [], []],
		},
		{
			// Cascade: 1←2←3, 4 alone (alt carrier map)
			name: "1←2←3\u20024 alt",
			carrierCount: 2,
			associatedCarrier: [1, 2, 1, 1],
			modulatedBy: [[2], [3], [], []],
		},
	]);
	public static readonly algorithms6Op: DictionaryArray<Algorithm> = toNameMap([
		// placeholder makes life easier for later
		{
			name: "Custom",
			carrierCount: 1,
			associatedCarrier: [1, 1, 1, 1, 1, 1],
			modulatedBy: [[2, 3, 4, 5, 6], [], [], [], [], []],
		},
		// yoinked from SynthBox
		// algortihm Section 1
		{
			name: "1←2←3←4←5←6",
			carrierCount: 1,
			associatedCarrier: [1, 1, 1, 1, 1, 1],
			modulatedBy: [[2], [3], [4], [5], [6], []],
		},
		{
			name: "1←3 2←4←5←6",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2, 2, 2],
			modulatedBy: [[3], [4], [], [5], [6], []],
		},
		{
			name: "1←3←4 2←5←6",
			carrierCount: 2,
			associatedCarrier: [1, 1, 1, 2, 2, 2],
			modulatedBy: [[3], [5], [4], [], [6], []],
		},
		{
			name: "1←4 2←5 3←6",
			carrierCount: 3,
			associatedCarrier: [1, 2, 3, 1, 2, 3],
			modulatedBy: [[4], [5], [6], [], [], []],
		},
		// Algorithm Section 2
		{
			name: "1←3 2←(4 5←6)",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2, 2, 2],
			modulatedBy: [[3], [4, 5], [], [], [6], []],
		},
		{
			name: "1←(3 4) 2←5←6",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2, 2, 2],
			modulatedBy: [[3, 4], [5], [], [], [6], []],
		},
		{
			name: "1←3 2←(4 5 6)",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2, 2, 2],
			modulatedBy: [[3], [4, 5, 6], [], [], [], []],
		},
		{
			name: "1←3 2←(4 5)←6",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2, 2, 2],
			modulatedBy: [[3], [4, 5], [], [6], [6], []],
		},
		{
			name: "1←3 2←4←(5 6)",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2, 2, 2],
			modulatedBy: [[3], [4], [], [5, 6], [], []],
		},
		{
			name: "1←(2 3 4 5 6)",
			carrierCount: 1,
			associatedCarrier: [1, 1, 1, 1, 1, 1],
			modulatedBy: [[2, 3, 4, 5, 6], [], [], [], [], []],
		},
		{
			name: "1←(2 3←5 4←6)",
			carrierCount: 1,
			associatedCarrier: [1, 1, 1, 1, 1, 1],
			modulatedBy: [[2, 3, 4], [], [5], [6], [], []],
		},
		{
			name: "1←(2 3 4←5←6)",
			carrierCount: 1,
			associatedCarrier: [1, 1, 1, 1, 1, 1],
			modulatedBy: [[2, 3, 4], [], [], [5], [6], []],
		},
		// Algorithm Section 3
		{
			name: "1←4←5 (2 3)←6",
			carrierCount: 3,
			associatedCarrier: [1, 2, 3, 1, 2, 3],
			modulatedBy: [[4], [6], [6], [5], [], []],
		},
		{
			name: "1←(3 4)←5 2←6",
			carrierCount: 2,
			associatedCarrier: [1, 2, 2, 2, 2, 2],
			modulatedBy: [[3, 4], [6], [5], [5], [], []],
		},
		{
			name: "(1 2)←4 3←(5 6)",
			carrierCount: 3,
			associatedCarrier: [1, 2, 3, 1, 2, 3],
			modulatedBy: [[4], [4], [5, 6], [], [], []],
		},
		{
			name: "(1 2)←5 (3 4)←6",
			carrierCount: 4,
			associatedCarrier: [1, 2, 3, 4, 4, 4],
			modulatedBy: [[5], [5], [6], [6], [], []],
		},
		{
			name: "(1 2 3)←(4 5 6)",
			carrierCount: 3,
			associatedCarrier: [1, 2, 3, 1, 2, 3],
			modulatedBy: [[4, 5, 6], [4, 5, 6], [4, 5, 6], [], [], []],
		},
		{
			name: "1←5 (2 3 4)←6",
			carrierCount: 4,
			associatedCarrier: [1, 2, 3, 4, 4, 4],
			modulatedBy: [[5], [6], [6], [6], [], []],
		},
		{
			name: "1 2←5 (3 4)←6",
			carrierCount: 4,
			associatedCarrier: [1, 2, 3, 4, 4, 4],
			modulatedBy: [[], [5], [6], [6], [], []],
		},
		{
			name: "1 2 (3 4 5)←6",
			carrierCount: 5,
			associatedCarrier: [1, 2, 3, 4, 5, 5],
			modulatedBy: [[], [], [6], [6], [6], []],
		},
		{
			name: "1 2 3 (4 5)←6",
			carrierCount: 5,
			associatedCarrier: [1, 2, 3, 4, 5, 5],
			modulatedBy: [[], [], [], [6], [6], []],
		},
		// Algorithm Section 3
		{
			name: "1 2←4 3←(5 6)",
			carrierCount: 3,
			associatedCarrier: [1, 2, 3, 3, 3, 3],
			modulatedBy: [[], [4], [5, 6], [], [], []],
		},
		{
			name: "1←4 2←(5 6) 3",
			carrierCount: 3,
			associatedCarrier: [1, 2, 3, 3, 3, 3],
			modulatedBy: [[4], [5, 6], [], [], [], []],
		},
		{
			name: "1 2 3←5 4←6",
			carrierCount: 4,
			associatedCarrier: [1, 2, 3, 4, 4, 4],
			modulatedBy: [[], [], [5], [6], [], []],
		},
		{
			name: "1 (2 3)←5←6 4",
			carrierCount: 4,
			associatedCarrier: [1, 2, 3, 4, 4, 4],
			modulatedBy: [[], [5], [5], [], [6], []],
		},
		{
			name: "1 2 3←5←6 4",
			carrierCount: 4,
			associatedCarrier: [1, 2, 3, 4, 4, 4],
			modulatedBy: [[], [], [5, 6], [], [], []],
		},
		{
			name: "(1 2 3 4 5)←6",
			carrierCount: 5,
			associatedCarrier: [1, 2, 3, 4, 5, 5],
			modulatedBy: [[6], [6], [6], [6], [6], []],
		},
		{
			name: "1 2 3 4 5←6",
			carrierCount: 5,
			associatedCarrier: [1, 2, 3, 4, 5, 5],
			modulatedBy: [[], [], [], [], [6], []],
		},
		{
			name: "1 2 3 4 5 6",
			carrierCount: 6,
			associatedCarrier: [1, 2, 3, 4, 5, 6],
			modulatedBy: [[], [], [], [], [], []],
		},
		// Section 4: own previous operators reused for 4op.
		{
			name: "1←(2 (3 (4 (5 (6",
			carrierCount: 5,
			associatedCarrier: [1, 2, 3, 4, 5, 5],
			modulatedBy: [[2, 3, 4, 5, 6], [3, 4, 5, 6], [4, 5, 6], [5, 6], [6], []],
		},
		{
			name: "1←(2(3(4(5(6",
			carrierCount: 1,
			associatedCarrier: [1, 1, 1, 1, 1, 1],
			modulatedBy: [[2, 3, 4, 5, 6], [3, 4, 5, 6], [4, 5, 6], [5, 6], [6], []],
		},
		{
			name: "1←4(2←5(3←6",
			carrierCount: 3,
			associatedCarrier: [1, 2, 3, 1, 2, 3],
			modulatedBy: [[2, 3, 4], [3, 5], [6], [], [], []],
		},
		{
			name: "1←4(2←5 3←6",
			carrierCount: 3,
			associatedCarrier: [1, 2, 3, 1, 2, 3],
			modulatedBy: [[2, 3, 4], [5], [6], [], [], []],
		},
	]);
	public static readonly operatorCarrierInterval: ReadonlyArray<number> = [
		0.0, 0.04, -0.073, 0.091, 0.061, 0.024,
	];
	public static readonly operatorAmplitudeMax: number = 15;
	public static readonly operatorFrequencies: DictionaryArray<OperatorFrequency> = toNameMap([
		{ name: "0.12×", mult: 0.125, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "0.25×", mult: 0.25, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "0.5×", mult: 0.5, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "0.75×", mult: 0.75, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "1×", mult: 1.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "~1×", mult: 1.0, hzOffset: 1.5, amplitudeSign: -1.0 },
		{ name: "2×", mult: 2.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "~2×", mult: 2.0, hzOffset: -1.3, amplitudeSign: -1.0 },
		{ name: "3×", mult: 3.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "3.5×", mult: 3.5, hzOffset: -0.05, amplitudeSign: 1.0 },
		{ name: "4×", mult: 4.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "~4×", mult: 4.0, hzOffset: -2.4, amplitudeSign: -1.0 },
		{ name: "5×", mult: 5.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "6×", mult: 6.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "7×", mult: 7.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "8×", mult: 8.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "9×", mult: 9.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "10×", mult: 10.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "11×", mult: 11.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "12×", mult: 12.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "13×", mult: 13.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "14×", mult: 14.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "15×", mult: 15.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		// ultrabox
		{ name: "16×", mult: 16.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "17×", mult: 17.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		// ultrabox
		{ name: "18×", mult: 18.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "19×", mult: 19.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		// ultrabox
		{ name: "20×", mult: 20.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "~20×", mult: 20.0, hzOffset: -5.0, amplitudeSign: -1.0 },
		// dogebox (also present in other mods)
		{ name: "25×", mult: 25.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "50×", mult: 50.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "75×", mult: 75.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "100×", mult: 100.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		// 50 and 100 are from dogebox
		// 128 and 256 from slarmoo's box
		{ name: "128×", mult: 128.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		// 256× was mistakenly 250×. 250× has been left for preservationof old songs
		{ name: "250×", mult: 250.0, hzOffset: 0.0, amplitudeSign: 1.0 },
		{ name: "256×", mult: 256.0, hzOffset: 0.0, amplitudeSign: 1.0 },
	]);

	// still used for drumsets
	public static readonly envelopes: DictionaryArray<Envelope> = toNameMap([
		{ name: "none", type: EnvelopeType.none, speed: 1.0 },
		{ name: "note size", type: EnvelopeType.noteSize, speed: 1.0 },
		{ name: "pitch", type: EnvelopeType.pitch, speed: 1.0 }, // Slarmoo's box (fairly useless on drumsets)
		{ name: "punch", type: EnvelopeType.punch, speed: 1.0 },
		{ name: "flare -1", type: EnvelopeType.flare, speed: 128.0 },
		{ name: "flare 1", type: EnvelopeType.flare, speed: 32.0 },
		{ name: "flare 2", type: EnvelopeType.flare, speed: 8.0 },
		{ name: "flare 3", type: EnvelopeType.flare, speed: 2.0 },
		{ name: "twang -1", type: EnvelopeType.twang, speed: 128.0 },
		{ name: "twang 1", type: EnvelopeType.twang, speed: 32.0 },
		{ name: "twang 2", type: EnvelopeType.twang, speed: 8.0 },
		{ name: "twang 3", type: EnvelopeType.twang, speed: 2.0 },
		{ name: "swell -1", type: EnvelopeType.swell, speed: 128.0 },
		{ name: "swell 1", type: EnvelopeType.swell, speed: 32.0 },
		{ name: "swell 2", type: EnvelopeType.swell, speed: 8.0 },
		{ name: "swell 3", type: EnvelopeType.swell, speed: 2.0 },
		{ name: "tremolo0", type: EnvelopeType.lfo, speed: 8.0 },
		{ name: "tremolo1", type: EnvelopeType.lfo, speed: 4.0 },
		{ name: "tremolo2", type: EnvelopeType.lfo, speed: 2.0 },
		{ name: "tremolo3", type: EnvelopeType.lfo, speed: 1.0 },
		{ name: "tremolo4", type: EnvelopeType.tremolo2, speed: 4.0 },
		{ name: "tremolo5", type: EnvelopeType.tremolo2, speed: 2.0 },
		{ name: "tremolo6", type: EnvelopeType.tremolo2, speed: 1.0 },
		{ name: "decay -1", type: EnvelopeType.decay, speed: 40.0 },
		{ name: "decay 1", type: EnvelopeType.decay, speed: 10.0 },
		{ name: "decay 2", type: EnvelopeType.decay, speed: 7.0 },
		{ name: "decay 3", type: EnvelopeType.decay, speed: 4.0 },
		{ name: "wibble-1", type: EnvelopeType.wibble, speed: 128.0 },
		// Speed changed from 96 to 128 (96 was omitted, now too late to add).
		{ name: "wibble 1", type: EnvelopeType.wibble, speed: 24.0 },
		{ name: "wibble 2", type: EnvelopeType.wibble, speed: 12.0 },
		{ name: "wibble 3", type: EnvelopeType.wibble, speed: 4.0 },
		{ name: "linear-2", type: EnvelopeType.linear, speed: 256.0 },
		{ name: "linear-1", type: EnvelopeType.linear, speed: 128.0 },
		{ name: "linear 1", type: EnvelopeType.linear, speed: 32.0 },
		{ name: "linear 2", type: EnvelopeType.linear, speed: 8.0 },
		{ name: "linear 3", type: EnvelopeType.linear, speed: 2.0 },
		{ name: "rise -2", type: EnvelopeType.rise, speed: 256.0 },
		{ name: "rise -1", type: EnvelopeType.rise, speed: 128.0 },
		{ name: "rise 1", type: EnvelopeType.rise, speed: 32.0 },
		{ name: "rise 2", type: EnvelopeType.rise, speed: 8.0 },
		{ name: "rise 3", type: EnvelopeType.rise, speed: 2.0 },
		// modbox
		{ name: "flute 1", type: EnvelopeType.wibble, speed: 16.0 },
		{ name: "flute 2", type: EnvelopeType.wibble, speed: 8.0 },
		{ name: "flute 3", type: EnvelopeType.wibble, speed: 4.0 },
		// sandbox
		{ name: "tripolo1", type: EnvelopeType.lfo, speed: 9.0 },
		{ name: "tripolo2", type: EnvelopeType.lfo, speed: 6.0 },
		{ name: "tripolo3", type: EnvelopeType.lfo, speed: 3.0 },
		{ name: "tripolo4", type: EnvelopeType.tremolo2, speed: 9.0 },
		{ name: "tripolo5", type: EnvelopeType.tremolo2, speed: 6.0 },
		{ name: "tripolo6", type: EnvelopeType.tremolo2, speed: 3.0 },
		{ name: "pentolo1", type: EnvelopeType.lfo, speed: 10.0 },
		{ name: "pentolo2", type: EnvelopeType.lfo, speed: 5.0 },
		{ name: "pentolo3", type: EnvelopeType.lfo, speed: 2.5 },
		{ name: "pentolo4", type: EnvelopeType.tremolo2, speed: 10.0 },
		{ name: "pentolo5", type: EnvelopeType.tremolo2, speed: 5.0 },
		{ name: "pentolo6", type: EnvelopeType.tremolo2, speed: 2.5 },
		// todbox
		{ name: "flutter 1", type: EnvelopeType.lfo, speed: 14.0 },
		{ name: "flutter 2", type: EnvelopeType.tremolo2, speed: 11.0 },
		{ name: "water-y flutter", type: EnvelopeType.lfo, speed: 9.0 },
		// new jummbox
		{ name: "blip 1", type: EnvelopeType.blip, speed: 6.0 },
		{ name: "blip 2", type: EnvelopeType.blip, speed: 16.0 },
		{ name: "blip 3", type: EnvelopeType.blip, speed: 32.0 },
		// Slarmoo's Box
		{ name: "fall 1", type: EnvelopeType.fall, speed: 8.0 },
		{ name: "fall 2", type: EnvelopeType.fall, speed: 4.0 },
		{ name: "fall 3", type: EnvelopeType.fall, speed: 2.0 },
	]);

	public static readonly newEnvelopes: DictionaryArray<Envelope> = toNameMap([
		{ name: "none", type: EnvelopeType.none, speed: 1.0 },
		{ name: "note size", type: EnvelopeType.noteSize, speed: 1.0 },
		{ name: "pitch", type: EnvelopeType.pitch, speed: 1.0 },
		{ name: "random", type: EnvelopeType.pseudorandom, speed: 4.0 }, // Slarmoo's box 1.3
		{ name: "punch", type: EnvelopeType.punch, speed: 1.0 },
		{ name: "flare", type: EnvelopeType.flare, speed: 32.0 },
		{ name: "twang", type: EnvelopeType.twang, speed: 32.0 },
		{ name: "swell", type: EnvelopeType.swell, speed: 32.0 },
		{ name: "lfo", type: EnvelopeType.lfo, speed: 4.0 }, // replaced tremolo and tremolo2 Slarmoo's Box 1.3
		{ name: "decay", type: EnvelopeType.decay, speed: 10.0 },
		{ name: "wibble", type: EnvelopeType.wibble, speed: 24.0 },
		{ name: "linear", type: EnvelopeType.linear, speed: 32.0 },
		{ name: "rise", type: EnvelopeType.rise, speed: 32.0 },
		{ name: "blip", type: EnvelopeType.blip, speed: 6.0 },
		{ name: "fall", type: EnvelopeType.fall, speed: 2.0 },
	]);

	public static readonly feedbacks: DictionaryArray<Feedback> = toNameMap([
		{ name: "1⟲", indices: [[1], [], [], []] },
		{ name: "2⟲", indices: [[], [2], [], []] },
		{ name: "3⟲", indices: [[], [], [3], []] },
		{ name: "4⟲", indices: [[], [], [], [4]] },
		{ name: "1⟲ 2⟲", indices: [[1], [2], [], []] },
		{ name: "3⟲ 4⟲", indices: [[], [], [3], [4]] },
		{ name: "1⟲ 2⟲ 3⟲", indices: [[1], [2], [3], []] },
		{ name: "2⟲ 3⟲ 4⟲", indices: [[], [2], [3], [4]] },
		{ name: "1⟲ 2⟲ 3⟲ 4⟲", indices: [[1], [2], [3], [4]] },
		{ name: "1→2", indices: [[], [1], [], []] },
		{ name: "1→3", indices: [[], [], [1], []] },
		{ name: "1→4", indices: [[], [], [], [1]] },
		{ name: "2→3", indices: [[], [], [2], []] },
		{ name: "2→4", indices: [[], [], [], [2]] },
		{ name: "3→4", indices: [[], [], [], [3]] },
		{ name: "1→3 2→4", indices: [[], [], [1], [2]] },
		{ name: "1→4 2→3", indices: [[], [], [2], [1]] },
		{ name: "1→2→3→4", indices: [[], [1], [2], [3]] },
		{ name: "1↔2 3↔4", indices: [[2], [1], [4], [3]] },
		{ name: "1↔4 2↔3", indices: [[4], [3], [2], [1]] },
		{ name: "2→1→4→3→2", indices: [[2], [3], [4], [1]] },
		{ name: "1→2→3→4→1", indices: [[4], [1], [2], [3]] },
		{ name: "(1 2 3)→4", indices: [[], [], [], [1, 2, 3]] },
		{
			name: "ALL",
			indices: [
				[1, 2, 3, 4],
				[1, 2, 3, 4],
				[1, 2, 3, 4],
				[1, 2, 3, 4],
			],
		},
	]);
	public static readonly feedbacks6Op: DictionaryArray<Feedback> = toNameMap([
		// placeholder makes life easier for later
		{ name: "Custom", indices: [[2, 3, 4, 5, 6], [], [], [], [], []] },

		{ name: "1⟲", indices: [[1], [], [], [], [], []] },
		{ name: "2⟲", indices: [[], [2], [], [], [], []] },
		{ name: "3⟲", indices: [[], [], [3], [], [], []] },
		{ name: "4⟲", indices: [[], [], [], [4], [], []] },
		{ name: "5⟲", indices: [[], [], [], [], [5], []] },
		{ name: "6⟲", indices: [[], [], [], [], [], [6]] },
		{ name: "1⟲ 2⟲", indices: [[1], [2], [], [], [], []] },
		{ name: "3⟲ 4⟲", indices: [[], [], [3], [4], [], []] },
		{ name: "1⟲ 2⟲ 3⟲", indices: [[1], [2], [3], [], [], []] },
		{ name: "2⟲ 3⟲ 4⟲", indices: [[], [2], [3], [4], [], []] },
		{ name: "1⟲ 2⟲ 3⟲ 4⟲", indices: [[1], [2], [3], [4], [], []] },
		{ name: "1⟲ 2⟲ 3⟲ 4⟲ 5⟲", indices: [[1], [2], [3], [4], [5], []] },
		{ name: "1⟲ 2⟲ 3⟲ 4⟲ 5⟲ 6⟲", indices: [[1], [2], [3], [4], [5], [6]] },
		{ name: "1→2", indices: [[], [1], [], [], [], []] },
		{ name: "1→3", indices: [[], [], [1], [], [], []] },
		{ name: "1→4", indices: [[], [], [], [1], [], []] },
		{ name: "1→5", indices: [[], [], [], [], [1], []] },
		{ name: "1→6", indices: [[], [], [], [], [], [1]] },
		{ name: "2→3", indices: [[], [], [2], [], [], []] },
		{ name: "2→4", indices: [[], [], [], [2], [], []] },
		{ name: "3→4", indices: [[], [], [], [3], [], []] },
		{ name: "4→5", indices: [[], [], [], [], [4], []] },
		{ name: "1→4 2→5 3→6", indices: [[], [], [], [1], [2], [3]] },
		{ name: "1→5 2→6 3→4", indices: [[], [], [], [3], [1], [2]] },
		{ name: "1→2→3→4→5→6", indices: [[], [1], [2], [3], [4], [5]] },
		{ name: "2→1→6→5→4→3→2", indices: [[2], [3], [4], [5], [6], [1]] },
		{ name: "1→2→3→4→5→6→1", indices: [[6], [1], [2], [3], [4], [5]] },
		{ name: "1↔2 3↔4 5↔6", indices: [[2], [1], [4], [3], [6], [5]] },
		{ name: "1↔4 2↔5 3↔6", indices: [[4], [5], [6], [1], [2], [3]] },
		{ name: "(1,2,3,4,5)→6", indices: [[], [], [], [], [], [1, 2, 3, 4, 5]] },
		{
			name: "ALL",
			indices: [
				[1, 2, 3, 4, 5, 6],
				[1, 2, 3, 4, 5, 6],
				[1, 2, 3, 4, 5, 6],
				[1, 2, 3, 4, 5, 6],
				[1, 2, 3, 4, 5, 6],
				[1, 2, 3, 4, 5, 6],
			],
		},
	]);
	public static readonly chipNoiseLength: number = 1 << 15; // 32768
	public static readonly spectrumNoiseLength: number = 1 << 15; // 32768
	public static readonly spectrumBasePitch: number = 24;
	public static readonly spectrumControlPoints: number = 30;
	public static readonly spectrumControlPointsPerOctave: number = 7;
	public static readonly spectrumControlPointBits: number = 3;
	public static readonly spectrumMax: number = (1 << Config.spectrumControlPointBits) - 1;
	public static readonly harmonicsControlPoints: number = 28;
	public static readonly harmonicsRendered: number = 64;
	public static readonly harmonicsRenderedForPickedString: number = 1 << 8; // 256
	public static readonly harmonicsControlPointBits: number = 3;
	public static readonly harmonicsMax: number = (1 << Config.harmonicsControlPointBits) - 1;
	public static readonly harmonicsWavelength: number = 1 << 11; // 2048
	public static readonly pulseWidthRange: number = 50;
	public static readonly pulseWidthStepPower: number = 0.5;
	public static readonly supersawVoiceCount: number = 7;
	public static readonly supersawDynamismMax: number = 6;
	public static readonly supersawSpreadMax: number = 12;
	public static readonly supersawShapeMax: number = 6;
	public static readonly pitchChannelCountMin: number = 1;
	public static readonly pitchChannelCountMax: number = 60; // slarmoo: 60
	public static readonly noiseChannelCountMin: number = 0;
	public static readonly noiseChannelCountMax: number = 60; // slarmoo: 60
	public static readonly modChannelCountMin: number = 0;
	public static readonly modChannelCountMax: number = 60; // slarmoo: 60
	public static readonly noiseInterval: number = 6;
	public static readonly pitchesPerOctave: number = 12;
	public static readonly drumCount: number = 12;
	public static readonly pitchOctaves: number = 8;
	public static readonly modCount: number = 6;
	public static readonly maxPitch: number = Config.pitchOctaves * Config.pitchesPerOctave;
	public static readonly maximumTonesPerChannel: number = Config.maxChordSize * 2;
	public static readonly justIntonationSemitones: number[] = [
		1.0 / 2.0,
		8.0 / 15.0,
		9.0 / 16.0,
		3.0 / 5.0,
		5.0 / 8.0,
		2.0 / 3.0,
		32.0 / 45.0,
		3.0 / 4.0,
		4.0 / 5.0,
		5.0 / 6.0,
		8.0 / 9.0,
		15.0 / 16.0,
		1.0,
		16.0 / 15.0,
		9.0 / 8.0,
		6.0 / 5.0,
		5.0 / 4.0,
		4.0 / 3.0,
		45.0 / 32.0,
		3.0 / 2.0,
		8.0 / 5.0,
		5.0 / 3.0,
		16.0 / 9.0,
		15.0 / 8.0,
		2.0,
	].map((x) => Math.log2(x) * Config.pitchesPerOctave);
	public static readonly pitchShiftRange: number = Config.justIntonationSemitones.length;
	public static readonly pitchShiftCenter: number = Config.pitchShiftRange >> 1;
	public static readonly detuneCenter: number = 200;
	public static readonly detuneMax: number = 400;
	public static readonly detuneMin: number = 0;
	public static readonly songDetuneMin: number = 0;
	public static readonly songDetuneMax: number = 500;
	public static readonly unisonVoicesMin: number = 1;
	public static readonly unisonVoicesMax: number = 9;
	public static readonly unisonSpreadMin: number = -96;
	public static readonly unisonSpreadMax: number = 96;
	public static readonly unisonOffsetMin: number = -96;
	public static readonly unisonOffsetMax: number = 96;
	public static readonly unisonExpressionMin: number = -2;
	public static readonly unisonExpressionMax: number = 2;
	public static readonly unisonSignMin: number = -2;
	public static readonly unisonSignMax: number = 2;
	public static readonly sineWaveLength: number = 1 << 8; // 256
	public static readonly sineWaveMask: number = Config.sineWaveLength - 1;

	public static generateSineWave(): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			wave[i] = Math.sin((i * Math.PI * 2.0) / Config.sineWaveLength);
		}
		return wave;
	}

	public static generateTriWave(): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			wave[i] =
				Math.asin(Math.sin((i * Math.PI * 2.0) / Config.sineWaveLength)) / (Math.PI / 2);
		}
		return wave;
	}

	public static generateTrapezoidWave(drive: number = 2): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			wave[i] = Math.max(
				-1.0,
				Math.min(
					1.0,
					Math.asin(Math.sin((i * Math.PI * 2.0) / Config.sineWaveLength)) * drive,
				),
			);
		}
		return wave;
	}

	public static generateSquareWave(phaseWidth: number = 0): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		const centerPoint: number = Config.sineWaveLength / 4;
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			wave[i] =
				+(
					Math.abs(i - centerPoint) < (phaseWidth * Config.sineWaveLength) / 2 ||
					Math.abs(i - Config.sineWaveLength - centerPoint) <
						(phaseWidth * Config.sineWaveLength) / 2
				) *
					2 -
				1;
		}
		return wave;
	}

	public static generateSawWave(inverse: boolean = false): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			wave[i] = ((((i + Config.sineWaveLength / 4.0) * 2.0) / Config.sineWaveLength) % 2) - 1;
			wave[i] = inverse ? -wave[i] : wave[i];
		}
		return wave;
	}

	public static generateSemisineWave() {
		const wave = new Float32Array(Config.sineWaveLength + 1);
		for (let i = 0; i < Config.sineWaveLength + 1; i++) {
			wave[i] = Math.max(Math.sin((i * Math.PI * 2.0) / Config.sineWaveLength), 0);
		}
		return wave;
	}

	public static generateWhiteNoiseFmWave() {
		const wave = new Float32Array(Config.sineWaveLength + 1);
		for (let i = 0; i < Config.sineWaveLength + 1; i++) {
			wave[i] = Math.random() * 2.0 - 1.0;
		}
		return wave;
	}

	public static generateQuasiSineWave() {
		const wave = new Float32Array(Config.sineWaveLength + 1);
		for (let i = 0; i < Config.sineWaveLength + 1; i++) {
			wave[i] = Math.round(Math.sin((i * Math.PI * 2.0) / Config.sineWaveLength));
		}
		return wave;
	}

	// AcBox
	public static generateAbsSineWave(): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			wave[i] = Math.abs(Math.sin((i * Math.PI * 2.0) / Config.sineWaveLength));
		}
		return wave;
	}

	public static generateQuarterSineWave(): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			const q = Math.floor((i * 4) / Config.sineWaveLength);

			if (q === 0 || q === 2) {
				wave[i] = Math.abs(Math.sin((i * Math.PI * 2.0) / Config.sineWaveLength));
			} else {
				wave[i] = 0;
			}
		}
		return wave;
	}

	public static generateSquishedSineWave(): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			if (i < Config.sineWaveLength / 2) {
				wave[i] = Math.sin((2 * i * Math.PI * 2.0) / Config.sineWaveLength);
			} else {
				wave[i] = 0;
			}
		}
		return wave;
	}

	public static generateSquishedAbsSineWave(): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			if (i < Config.sineWaveLength / 2) {
				wave[i] = Math.abs(Math.sin((2 * i * Math.PI * 2.0) / Config.sineWaveLength));
			} else {
				wave[i] = 0;
			}
		}
		return wave;
	}

	// OPL3-style operator waveforms (appended)
	// Pseudo-sine: positive half full amplitude, negative half at 50%
	public static generatePseudoSineWave(): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			const s: number = Math.sin((i * Math.PI * 2.0) / Config.sineWaveLength);
			wave[i] = s >= 0 ? s : s * 0.5;
		}
		return wave;
	}

	// Square-ish: sign of sine, produces odd harmonics
	public static generateSquareishSineWave(): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			const s: number = Math.sin((i * Math.PI * 2.0) / Config.sineWaveLength);
			wave[i] = s >= 0 ? 1.0 : -1.0;
		}
		return wave;
	}

	// Neg half-sine: only negative half (inverted half-sine)
	public static generateNegHalfSineWave(): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			const s: number = Math.sin((i * Math.PI * 2.0) / Config.sineWaveLength);
			wave[i] = s < 0 ? s : 0;
		}
		return wave;
	}

	// Stretched sine: boosted peaks, compressed troughs
	public static generateStretchedSineWave(): Float32Array {
		const wave: Float32Array = new Float32Array(Config.sineWaveLength + 1);
		for (let i: number = 0; i < Config.sineWaveLength + 1; i++) {
			const s: number = Math.sin((i * Math.PI * 2.0) / Config.sineWaveLength);
			// Boost positive peaks by squaring, negative by square then negate
			wave[i] = s >= 0 ? s * s : -(s * s);
		}
		return wave;
	}

	public static readonly sineWave: Float32Array = Config.generateSineWave();

	public static readonly perEnvelopeSpeedIndices: number[] = [
		0, 0.01, 0.02, 0.03, 0.04, 0.05, 0.06, 0.07, 0.08, 0.09, 0.1, 0.2, 0.25, 0.3, 0.3333, 0.4,
		0.5, 0.6, 0.6667, 0.7, 0.75, 0.8, 0.9, 1, 1.25, 1.3333, 1.5, 1.6667, 1.75, 2, 2.25, 2.5,
		2.75, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 11, 12, 13, 14, 15, 16,
		17, 18, 19, 20, 24, 32, 40, 64, 128, 256,
	];
	public static readonly perEnvelopeSpeedToIndices: Dictionary<number> = {
		// used to convert speeds back into indices
		0: 0,
		0.01: 1,
		0.02: 2,
		0.03: 3,
		0.04: 4,
		0.05: 5,
		0.06: 6,
		0.07: 7,
		0.08: 8,
		0.09: 9,
		0.1: 10,
		0.2: 11,
		0.25: 12,
		0.3: 13,
		0.3333: 14,
		0.4: 15,
		0.5: 16,
		0.6: 17,
		0.6667: 18,
		0.7: 19,
		0.75: 20,
		0.8: 21,
		0.9: 22,
		1: 23,
		1.25: 24,
		1.3333: 25,
		1.5: 26,
		1.6667: 27,
		1.75: 28,
		2: 29,
		2.25: 30,
		2.5: 31,
		2.75: 32,
		3: 33,
		3.5: 34,
		4: 35,
		4.5: 36,
		5: 37,
		5.5: 38,
		6: 39,
		6.5: 40,
		7: 41,
		7.5: 42,
		8: 43,
		8.5: 44,
		9: 45,
		9.5: 46,
		10: 47,
		11: 48,
		12: 49,
		13: 50,
		14: 51,
		15: 52,
		16: 53,
		17: 54,
		18: 55,
		19: 56,
		20: 57,
		24: 58,
		32: 59,
		40: 60,
		64: 61,
		128: 62,
		256: 63,
	};

	public static readonly perEnvelopeBoundMin: number = 0; // probably should leave at 0. Negative envelopes are problematic right now
	public static readonly perEnvelopeBoundMax: number = 2; // max of 6.3 unless you update url
	public static readonly randomEnvelopeSeedMax: number = 63; // if you increase this you'll need to update the url to support it
	public static readonly randomEnvelopeStepsMax: number = 32;

	// Picked strings have an all-pass filter with a corner frequency based on the tone fundamental frequency, in order to add a slight inharmonicity. (Which is important for distortion.)
	public static readonly pickedStringDispersionCenterFreq: number = 6000.0; // The tone fundamental freq is pulled toward this freq for computing the all-pass corner freq.
	public static readonly pickedStringDispersionFreqScale: number = 0.3; // The tone fundamental freq freq moves this much toward the center freq for computing the all-pass corner freq.
	public static readonly pickedStringDispersionFreqMult: number = 4.0; // The all-pass corner freq is based on this times the adjusted tone fundamental freq.
	public static readonly pickedStringShelfHz: number = 4000.0; // The cutoff freq of the shelf filter that is used to decay the high frequency energy in the picked string.

	public static readonly distortionRange: number = 8;
	public static readonly stringSustainRange: number = 15;
	public static readonly stringDecayRate: number = 0.12;
	public static readonly enableAcousticSustain: boolean = false;
	public static readonly sustainTypeNames: ReadonlyArray<string> = ["bright", "acoustic"]; // See SustainType enum above.

	public static readonly bitcrusherFreqRange: number = 14;
	public static readonly bitcrusherOctaveStep: number = 0.5;
	public static readonly bitcrusherQuantizationRange: number = 8;

	public static readonly maxEnvelopeCount: number = 16;
	public static readonly defaultAutomationRange: number = 13;
	public static readonly instrumentAutomationTargets: DictionaryArray<AutomationTarget> =
		toNameMap([
			{
				name: "none",
				computeIndex: null,
				displayName: "none",
				perNote: false,
				interleave: false,
				isFilter: false,
				/*range: 0,                              */ maxCount: 1,
				effect: null,
				compatibleInstruments: null,
			},
			{
				name: "noteVolume",
				computeIndex: EnvelopeComputeIndex.noteVolume,
				displayName: "note volume",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.volumeRange,             */ maxCount: 1,
				effect: null,
				compatibleInstruments: null,
			},
			{
				name: "pulseWidth",
				computeIndex: EnvelopeComputeIndex.pulseWidth,
				displayName: "pulse width",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.pulseWidthRange,         */ maxCount: 1,
				effect: null,
				compatibleInstruments: [InstrumentType.pwm, InstrumentType.supersaw],
			},
			{
				name: "stringSustain",
				computeIndex: EnvelopeComputeIndex.stringSustain,
				displayName: "sustain",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.stringSustainRange,      */ maxCount: 1,
				effect: null,
				compatibleInstruments: [InstrumentType.pickedString],
			},
			{
				name: "unison",
				computeIndex: EnvelopeComputeIndex.unison,
				displayName: "unison",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.defaultAutomationRange,  */ maxCount: 1,
				effect: null,
				compatibleInstruments: [
					InstrumentType.chip,
					InstrumentType.harmonics,
					InstrumentType.pickedString,
					InstrumentType.customChipWave,
					InstrumentType.pwm,
					InstrumentType.noise,
					InstrumentType.spectrum,
					InstrumentType.drumset,
					InstrumentType.fm,
					InstrumentType.fm6op,
					InstrumentType.opl3,
				],
			},
			{
				name: "operatorFrequency",
				computeIndex: EnvelopeComputeIndex.operatorFrequency0,
				displayName: "fm# freq",
				perNote: true,
				interleave: true,
				isFilter: false,
				/*range: Config.defaultAutomationRange,  */ maxCount: Config.operatorCount + 2,
				effect: null,
				compatibleInstruments: [
					InstrumentType.fm,
					InstrumentType.fm6op,
					InstrumentType.opl3,
				],
			},
			{
				name: "operatorAmplitude",
				computeIndex: EnvelopeComputeIndex.operatorAmplitude0,
				displayName: "fm# volume",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.operatorAmplitudeMax + 1,*/ maxCount: Config.operatorCount + 2,
				effect: null,
				compatibleInstruments: [
					InstrumentType.fm,
					InstrumentType.fm6op,
					InstrumentType.opl3,
				],
			},
			{
				name: "feedbackAmplitude",
				computeIndex: EnvelopeComputeIndex.feedbackAmplitude,
				displayName: "fm feedback",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.operatorAmplitudeMax + 1,*/ maxCount: 1,
				effect: null,
				compatibleInstruments: [
					InstrumentType.fm,
					InstrumentType.fm6op,
					InstrumentType.opl3,
				],
			},
			{
				name: "pitchShift",
				computeIndex: EnvelopeComputeIndex.pitchShift,
				displayName: "pitch shift",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.pitchShiftRange,         */ maxCount: 1,
				effect: EffectType.pitchShift,
				compatibleInstruments: null,
			},
			{
				name: "detune",
				computeIndex: EnvelopeComputeIndex.detune,
				displayName: "detune",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.detuneMax + 1,           */ maxCount: 1,
				effect: EffectType.detune,
				compatibleInstruments: null,
			},
			{
				name: "vibratoDepth",
				computeIndex: EnvelopeComputeIndex.vibratoDepth,
				displayName: "vibrato depth",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.defaultAutomationRange,  */ maxCount: 1,
				effect: EffectType.vibrato,
				compatibleInstruments: null,
			},
			// { name: "vibratoSpeed",         computeIndex: EnvelopeComputeIndex.vibratoSpeed,              displayName: "vibrato speed",  /*perNote: true,*/ interleave: false, isFilter: false, /*range: Config.defaultAutomationRange,  */    maxCount: 1, effect: EffectType.vibrato, compatibleInstruments: null },
			{
				name: "noteFilterAllFreqs",
				computeIndex: EnvelopeComputeIndex.noteFilterAllFreqs,
				displayName: "n. filter freqs",
				perNote: true,
				interleave: false,
				isFilter: true,
				/*range: null,                           */ maxCount: 1,
				effect: EffectType.noteFilter,
				compatibleInstruments: null,
			},
			{
				name: "noteFilterFreq",
				computeIndex: EnvelopeComputeIndex.noteFilterFreq0,
				displayName: "n. filter # freq",
				perNote: true,
				interleave: false /*true*/,
				isFilter: true,
				/*range: Config.filterFreqRange,     */ maxCount: Config.filterMaxPoints,
				effect: EffectType.noteFilter,
				compatibleInstruments: null,
			},
			{
				name: "decimalOffset",
				computeIndex: EnvelopeComputeIndex.decimalOffset,
				displayName: "decimal offset",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.pulseWidthRange,         */ maxCount: 1,
				effect: null,
				compatibleInstruments: [InstrumentType.pwm, InstrumentType.supersaw],
			},
			{
				name: "supersawDynamism",
				computeIndex: EnvelopeComputeIndex.supersawDynamism,
				displayName: "dynamism",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.supersawDynamismMax + 1, */ maxCount: 1,
				effect: null,
				compatibleInstruments: [InstrumentType.supersaw],
			},
			{
				name: "supersawSpread",
				computeIndex: EnvelopeComputeIndex.supersawSpread,
				displayName: "spread",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.supersawSpreadMax + 1,   */ maxCount: 1,
				effect: null,
				compatibleInstruments: [InstrumentType.supersaw],
			},
			{
				name: "supersawShape",
				computeIndex: EnvelopeComputeIndex.supersawShape,
				displayName: "saw↔pulse",
				perNote: true,
				interleave: false,
				isFilter: false,
				/*range: Config.supersawShapeMax + 1,    */ maxCount: 1,
				effect: null,
				compatibleInstruments: [InstrumentType.supersaw],
			},
			{
				name: "panning",
				computeIndex: EnvelopeComputeIndex.panning,
				displayName: "panning",
				perNote: false,
				interleave: false,
				isFilter: false,
				/*range: Config.chorusRange,    */ maxCount: 1,
				effect: EffectType.panning,
				compatibleInstruments: null,
			},
			{
				name: "distortion",
				computeIndex: EnvelopeComputeIndex.distortion,
				displayName: "distortion",
				perNote: false,
				interleave: false,
				isFilter: false,
				/*range: Config.defaultAutomationRange,  */ maxCount: 1,
				effect: EffectType.distortion,
				compatibleInstruments: null,
			},
			{
				name: "bitcrusherQuantization",
				computeIndex: EnvelopeComputeIndex.bitcrusherQuantization,
				displayName: "bitcrush",
				perNote: false,
				interleave: false,
				isFilter: false,
				/*range: Config.defaultAutomationRange,  */ maxCount: 1,
				effect: EffectType.bitcrusher,
				compatibleInstruments: null,
			},
			{
				name: "bitcrusherFrequency",
				computeIndex: EnvelopeComputeIndex.bitcrusherFrequency,
				displayName: "freq crush",
				perNote: false,
				interleave: false,
				isFilter: false,
				/*range: Config.defaultAutomationRange,  */ maxCount: 1,
				effect: EffectType.bitcrusher,
				compatibleInstruments: null,
			},
			{
				name: "chorus",
				computeIndex: EnvelopeComputeIndex.chorus,
				displayName: "chorus",
				perNote: false,
				interleave: false,
				isFilter: false,
				/*range: Config.chorusRange,    */ maxCount: 1,
				effect: EffectType.chorus,
				compatibleInstruments: null,
			},
			{
				name: "echoSustain",
				computeIndex: EnvelopeComputeIndex.echoSustain,
				displayName: "echo",
				perNote: false,
				interleave: false,
				isFilter: false,
				/*range: Config.chorusRange,    */ maxCount: 1,
				effect: EffectType.echo,
				compatibleInstruments: null,
			},
			{
				name: "reverb",
				computeIndex: EnvelopeComputeIndex.reverb,
				displayName: "reverb",
				perNote: false,
				interleave: false,
				isFilter: false,
				/*range: Config.chorusRange,    */ maxCount: 1,
				effect: EffectType.reverb,
				compatibleInstruments: null,
			},
			{
				name: "arpeggioSpeed",
				computeIndex: EnvelopeComputeIndex.arpeggioSpeed,
				displayName: "arpeggio speed",
				perNote: false,
				interleave: false,
				isFilter: false,
				/*range: Config.chorusRange,    */ maxCount: 1,
				effect: EffectType.chord,
				compatibleInstruments: null,
			},
			{
				name: "ringModulation",
				computeIndex: EnvelopeComputeIndex.ringModulation,
				displayName: "ring mod",
				perNote: false,
				interleave: false,
				isFilter: false,
				maxCount: 1,
				effect: EffectType.ringModulation,
				compatibleInstruments: null,
			},
			{
				name: "ringModulationHz",
				computeIndex: EnvelopeComputeIndex.ringModulationHz,
				displayName: "ring mod hz",
				perNote: false,
				interleave: false,
				isFilter: false,
				maxCount: 1,
				effect: EffectType.ringModulation,
				compatibleInstruments: null,
			},
			{
				name: "granular",
				computeIndex: EnvelopeComputeIndex.granular,
				displayName: "granular",
				perNote: false,
				interleave: false,
				isFilter: false,
				maxCount: 1,
				effect: EffectType.granular,
				compatibleInstruments: null,
			},
			{
				name: "grainFreq",
				computeIndex: EnvelopeComputeIndex.grainAmount,
				displayName: "grain freq",
				perNote: false,
				interleave: false,
				isFilter: false,
				maxCount: 1,
				effect: EffectType.granular,
				compatibleInstruments: null,
			},
			{
				name: "grainSize",
				computeIndex: EnvelopeComputeIndex.grainSize,
				displayName: "grain size",
				perNote: false,
				interleave: false,
				isFilter: false,
				maxCount: 1,
				effect: EffectType.granular,
				compatibleInstruments: null,
			},
			{
				name: "grainRange",
				computeIndex: EnvelopeComputeIndex.grainRange,
				displayName: "grain range",
				perNote: false,
				interleave: false,
				isFilter: false,
				maxCount: 1,
				effect: EffectType.granular,
				compatibleInstruments: null,
			},
			{
				name: "echoDelay",
				computeIndex: EnvelopeComputeIndex.echoDelay,
				displayName: "echo delay",
				perNote: false,
				interleave: false,
				isFilter: false,
				maxCount: 1,
				effect: EffectType.echo,
				compatibleInstruments: null,
			},
			{
				name: "phaserFreq",
				computeIndex: EnvelopeComputeIndex.phaserFreq,
				displayName: "phaser freq",
				perNote: false,
				interleave: false,
				isFilter: false,
				maxCount: 1,
				effect: EffectType.phaser,
				compatibleInstruments: null,
			},
			{
				name: "phaserMix",
				computeIndex: EnvelopeComputeIndex.phaserMix,
				displayName: "phaser",
				perNote: false,
				interleave: false,
				isFilter: false,
				maxCount: 1,
				effect: EffectType.phaser,
				compatibleInstruments: null,
			},
			{
				name: "phaserFeedback",
				computeIndex: EnvelopeComputeIndex.phaserFeedback,
				displayName: "phaser feedback",
				perNote: false,
				interleave: false,
				isFilter: false,
				maxCount: 1,
				effect: EffectType.phaser,
				compatibleInstruments: null,
			},
			{
				name: "phaserStages",
				computeIndex: EnvelopeComputeIndex.phaserStages,
				displayName: "phaser stages",
				perNote: false,
				interleave: false,
				isFilter: false,
				maxCount: 1,
				effect: EffectType.phaser,
				compatibleInstruments: null,
			},
			// note filter gain disabled — controlling filter gain is less intuitive than controlling filter freq.
			// {name: "noteFilterGain",         computeIndex:       EnvelopeComputeIndex.noteFilterGain0,        displayName: "n. filter # vol",  /*perNote:  true,*/ interleave: false, isFilter:  true, range: Config.filterGainRange,             maxCount: Config.filterMaxPoints, effect: EffectType.noteFilter, compatibleInstruments: null},
			/*
        {name: "eqFilterAllFreqs",       computeIndex: InstrumentAutomationIndex.eqFilterAllFreqs,       displayName: "eq filter freqs",  perNote: false, interleave: false, isFilter:  true, range: null,                               maxCount: 1,    effect: null,                    compatibleInstruments: null},
        {name: "eqFilterFreq",           computeIndex: InstrumentAutomationIndex.eqFilterFreq0,          displayName: "eq filter # freq", perNote: false, interleave:  true, isFilter:  true, range: Config.filterFreqRange,             maxCount: Config.filterMaxPoints, effect: null,  compatibleInstruments: null},
        {name: "eqFilterGain",           computeIndex: InstrumentAutomationIndex.eqFilterGain0,          displayName: "eq filter # vol",  perNote: false, interleave: false, isFilter:  true, range: Config.filterGainRange,             maxCount: Config.filterMaxPoints, effect: null,  compatibleInstruments: null},
        {name: "mixVolume",              computeIndex: InstrumentAutomationIndex.mixVolume,              displayName: "mix volume",       perNote: false, interleave: false, isFilter: false, range: Config.volumeRange,                 maxCount: 1,    effect: null,                    compatibleInstruments: null},
        {name: "envelope#",              computeIndex: null,                                             displayName: "envelope",         perNote: false, interleave: false, isFilter: false, range: Config.defaultAutomationRange,      maxCount: Config.maxEnvelopeCount, effect: null, compatibleInstruments: null}, // maxCount special case for envelopes to be allowed to target earlier ones.
        */
		]);
	public static readonly operatorWaves: DictionaryArray<OperatorWave> = toNameMap([
		{ name: "sine", samples: Config.sineWave },
		{ name: "triangle", samples: Config.generateTriWave() },
		{ name: "pulse width", samples: Config.generateSquareWave(0.5) },
		{ name: "sawtooth", samples: Config.generateSawWave() },
		{ name: "ramp", samples: Config.generateSawWave(true) },
		{ name: "trapezoid", samples: Config.generateTrapezoidWave(2) },
		{ name: "quasi-sine", samples: Config.generateQuasiSineWave() },
		{ name: "half-sine", samples: Config.generateSemisineWave() },
		{ name: "white noise", samples: Config.generateWhiteNoiseFmWave() },
		{ name: "absine", samples: Config.generateAbsSineWave() },
		{ name: "sharksine", samples: Config.generateQuarterSineWave() },
		{ name: "fastsine", samples: Config.generateSquishedSineWave() },
		{ name: "camelsine", samples: Config.generateSquishedAbsSineWave() },
		// OPL3-style operator waveforms (appended)
		{ name: "pseudo-sine", samples: Config.generatePseudoSineWave() },
		{ name: "square-ish", samples: Config.generateSquareishSineWave() },
		{ name: "neg half-sine", samples: Config.generateNegHalfSineWave() },
		{ name: "stretched sine", samples: Config.generateStretchedSineWave() },
	]);
	public static readonly pwmOperatorWaves: DictionaryArray<OperatorWave> = toNameMap([
		{ name: "1%", samples: Config.generateSquareWave(0.01) },
		{ name: "5%", samples: Config.generateSquareWave(0.05) },
		{ name: "12.5%", samples: Config.generateSquareWave(0.125) },
		{ name: "25%", samples: Config.generateSquareWave(0.25) },
		{ name: "33%", samples: Config.generateSquareWave(1 / 3) },
		{ name: "50%", samples: Config.generateSquareWave(0.5) },
		{ name: "66%", samples: Config.generateSquareWave(2 / 3) },
		{ name: "75%", samples: Config.generateSquareWave(0.75) },
		{ name: "87.5%", samples: Config.generateSquareWave(0.875) },
		{ name: "95%", samples: Config.generateSquareWave(0.95) },
		{ name: "99%", samples: Config.generateSquareWave(0.99) },
	]);

	// Height of the small editor column for inserting/deleting rows, in pixels.
	public static readonly barEditorHeight: number = 10;

	// Careful about changing index ordering for this. Index is stored in URL/JSON etc.
	public static readonly modulators: DictionaryArray<Modulator> = toNameMap([
		{
			name: "none",
			pianoName: "None",
			maxRawVol: 6,
			newNoteVol: 6,
			forSong: true,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "No Mod Setting",
			promptDesc: [
				"No setting has been chosen yet, so this modulator will have no effect. Try choosing a setting with the dropdown, then click this '?' again for more info.",
				"[$LO - $HI]",
			],
		},
		{
			name: "song volume",
			pianoName: "Volume",
			maxRawVol: 100,
			newNoteVol: 100,
			forSong: true,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Song Volume",
			promptDesc: [
				"This setting affects the overall volume of the song, just like the main volume slider.",
				"At $HI, the volume will be unchanged from default, and it will get gradually quieter down to $LO.",
				"[MULTIPLICATIVE] [$LO - $HI] [%]",
			],
		},
		{
			name: "tempo",
			pianoName: "Tempo",
			maxRawVol: Config.tempoMax - Config.tempoMin,
			newNoteVol: Math.ceil((Config.tempoMax - Config.tempoMin) / 2),
			forSong: true,
			convertRealFactor: Config.tempoMin,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Song Tempo",
			promptDesc: [
				"This setting controls the speed your song plays at, just like the tempo slider.",
				"When you first make a note for this setting, it will default to your current tempo. Raising it speeds up the song, up to $HI BPM, and lowering it slows it down, to a minimum of $LO BPM.",
				"Note that you can make a 'swing' effect by rapidly changing between two tempo values.",
				"[OVERWRITING] [$LO - $HI] [BPM]",
			],
		},
		{
			name: "song reverb",
			pianoName: "Reverb",
			maxRawVol: Config.reverbRange * 2,
			newNoteVol: Config.reverbRange,
			forSong: true,
			convertRealFactor: -Config.reverbRange,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Song Reverb",
			promptDesc: [
				"This setting affects the overall reverb of your song. It works by multiplying existing reverb for instruments, so those with no reverb set will be unaffected.",
				"At $MID, all instruments' reverb will be unchanged from default. This increases up to double the reverb value at $HI, or down to no reverb at $LO.",
				"[MULTIPLICATIVE] [$LO - $HI]",
			],
		},
		{
			name: "next bar",
			pianoName: "Next Bar",
			maxRawVol: 1,
			newNoteVol: 1,
			forSong: true,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Go To Next Bar",
			promptDesc: [
				"This setting functions a little different from most. Wherever a note is placed, the song will jump immediately to the next bar when it is encountered.",
				"This jump happens at the very start of the note, so the length of a next-bar note is irrelevant. Also, the note can be value 0 or 1, but the value is also irrelevant - wherever you place a note, the song will jump.",
				"You can make mixed-meter songs or intro sections by cutting off unneeded beats with a next-bar modulator.",
				"[$LO - $HI]",
			],
		},
		{
			name: "note volume",
			pianoName: "Note Vol.",
			maxRawVol: Config.volumeRange,
			newNoteVol: Math.ceil(Config.volumeRange / 2),
			forSong: false,
			convertRealFactor: Math.ceil(-Config.volumeRange / 2.0),
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Note Volume",
			promptDesc: [
				"This setting affects the volume of your instrument as if its note size had been scaled.",
				"At $MID, an instrument's volume will be unchanged from default. This means you can still use the volume sliders to mix the base volume of instruments. The volume gradually increases up to $HI, or decreases down to mute at $LO.",
				"This setting was the default for volume modulation in JummBox for a long time. Due to some new effects like distortion and bitcrush, note volume doesn't always allow fine volume control. Also, this modulator affects the value of FM modulator waves instead of just carriers. This can distort the sound which may be useful, but also may be undesirable. In those cases, use the 'mix volume' modulator instead, which will always just scale the volume with no added effects.",
				"For display purposes, this mod will show up on the instrument volume slider, as long as there is not also an active 'mix volume' modulator anyhow. However, as mentioned, it works more like changing note volume.",
				"[MULTIPLICATIVE] [$LO - $HI]",
			],
		},
		{
			name: "pan",
			pianoName: "Pan",
			maxRawVol: Config.panMax,
			newNoteVol: Math.ceil(Config.panMax / 2),
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.panning,
			maxIndex: 0,
			promptName: "Instrument Panning",
			promptDesc: [
				"This setting controls the panning of your instrument, just like the panning slider.",
				"At $LO, your instrument will sound like it is coming fully from the left-ear side. At $MID it will be right in the middle, and at $HI, it will sound like it's on the right.",
				"[OVERWRITING] [$LO - $HI] [L-R]",
			],
		},
		{
			name: "reverb",
			pianoName: "Reverb",
			maxRawVol: Config.reverbRange,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.reverb,
			maxIndex: 0,
			promptName: "Instrument Reverb",
			promptDesc: [
				"This setting controls the reverb of your insturment, just like the reverb slider.",
				"At $LO, your instrument will have no reverb. At $HI, it will be at maximum.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "distortion",
			pianoName: "Distortion",
			maxRawVol: Config.distortionRange - 1,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.distortion,
			maxIndex: 0,
			promptName: "Instrument Distortion",
			promptDesc: [
				"This setting controls the amount of distortion for your instrument, just like the distortion slider.",
				"At $LO, your instrument will have no distortion. At $HI, it will be at maximum.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "fm slider 1",
			pianoName: "FM 1",
			maxRawVol: 15,
			newNoteVol: 15,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "FM Slider 1",
			promptDesc: [
				"This setting affects the strength of the first FM slider, just like the corresponding slider on your instrument.",
				"It works in a multiplicative way, so at $HI your slider will sound the same is its default value, and at $LO it will sound like it has been moved all the way to the left.",
				"For the full range of control with this mod, move your underlying slider all the way to the right.",
				"[MULTIPLICATIVE] [$LO - $HI] [%]",
			],
		},
		{
			name: "fm slider 2",
			pianoName: "FM 2",
			maxRawVol: 15,
			newNoteVol: 15,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "FM Slider 2",
			promptDesc: [
				"This setting affects the strength of the second FM slider, just like the corresponding slider on your instrument.",
				"It works in a multiplicative way, so at $HI your slider will sound the same is its default value, and at $LO it will sound like it has been moved all the way to the left.",
				"For the full range of control with this mod, move your underlying slider all the way to the right.",
				"[MULTIPLICATIVE] [$LO - $HI] [%]",
			],
		},
		{
			name: "fm slider 3",
			pianoName: "FM 3",
			maxRawVol: 15,
			newNoteVol: 15,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "FM Slider 3",
			promptDesc: [
				"This setting affects the strength of the third FM slider, just like the corresponding slider on your instrument.",
				"It works in a multiplicative way, so at $HI your slider will sound the same is its default value, and at $LO it will sound like it has been moved all the way to the left.",
				"For the full range of control with this mod, move your underlying slider all the way to the right.",
				"[MULTIPLICATIVE] [$LO - $HI] [%]",
			],
		},
		{
			name: "fm slider 4",
			pianoName: "FM 4",
			maxRawVol: 15,
			newNoteVol: 15,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "FM Slider 4",
			promptDesc: [
				"This setting affects the strength of the fourth FM slider, just like the corresponding slider on your instrument.",
				"It works in a multiplicative way, so at $HI your slider will sound the same is its default value, and at $LO it will sound like it has been moved all the way to the left.",
				"For the full range of control with this mod, move your underlying slider all the way to the right.",
				"[MULTIPLICATIVE] [$LO - $HI] [%]",
			],
		},
		{
			name: "fm feedback",
			pianoName: "FM Feedbck",
			maxRawVol: 15,
			newNoteVol: 15,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "FM Feedback",
			promptDesc: [
				"This setting affects the strength of the FM feedback slider, just like the corresponding slider on your instrument.",
				"It works in a multiplicative way, so at $HI your slider will sound the same is its default value, and at $LO it will sound like it has been moved all the way to the left.",
				"For the full range of control with this mod, move your underlying slider all the way to the right.",
				"[MULTIPLICATIVE] [$LO - $HI] [%]",
			],
		},
		{
			name: "pulse width",
			pianoName: "Pulse Width",
			maxRawVol: Config.pulseWidthRange,
			newNoteVol: Config.pulseWidthRange,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Pulse Width",
			promptDesc: [
				"This setting controls the width of this instrument's pulse wave, just like the pulse width slider.",
				"At $HI, your instrument will sound like a pure square wave (on 50% of the time). It will gradually sound narrower down to $LO, where it will be inaudible (as it is on 0% of the time).",
				"Changing pulse width randomly between a few values is a common strategy in chiptune music to lend some personality to a lead instrument.",
				"[OVERWRITING] [$LO - $HI] [%Duty]",
			],
		},
		{
			name: "detune",
			pianoName: "Detune",
			maxRawVol: Config.detuneMax - Config.detuneMin,
			newNoteVol: Config.detuneCenter,
			forSong: false,
			convertRealFactor: -Config.detuneCenter,
			associatedEffect: EffectType.detune,
			maxIndex: 0,
			promptName: "Instrument Detune",
			promptDesc: [
				"This setting controls the detune for this instrument, just like the detune slider.",
				"At $MID, your instrument will have no detune applied. Each tick corresponds to one cent, or one-hundredth of a pitch. Thus, each change of 100 ticks corresponds to one half-step of detune, up to two half-steps up at $HI, or two half-steps down at $LO.",
				"[OVERWRITING] [$LO - $HI] [cents]",
			],
		},
		{
			name: "vibrato depth",
			pianoName: "Vibrato Depth",
			maxRawVol: 50,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.vibrato,
			maxIndex: 0,
			promptName: "Vibrato Depth",
			promptDesc: [
				"This setting controls the amount that your pitch moves up and down by during vibrato, just like the vibrato depth slider.",
				"At $LO, your instrument will have no vibrato depth so its vibrato would be inaudible. This increases up to $HI, where an extreme pitch change will be noticeable.",
				"[OVERWRITING] [$LO - $HI] [pitch ÷25]",
			],
		},
		{
			name: "song detune",
			pianoName: "Detune",
			maxRawVol: Config.songDetuneMax - Config.songDetuneMin,
			newNoteVol: Math.ceil((Config.songDetuneMax - Config.songDetuneMin) / 2),
			forSong: true,
			convertRealFactor: -250,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Song Detune",
			promptDesc: [
				"This setting controls the overall detune of the entire song. There is no associated slider.",
				"At $MID, your song will have no extra detune applied and sound unchanged from default. Each tick corresponds to four cents, or four hundredths of a pitch. Thus, each change of 25 ticks corresponds to one half-step of detune, up to 10 half-steps up at $HI, or 10 half-steps down at $LO.",
				"[MULTIPLICATIVE] [$LO - $HI] [cents x4]",
			],
		},
		{
			name: "vibrato speed",
			pianoName: "Vibrato Speed",
			maxRawVol: 30,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.vibrato,
			maxIndex: 0,
			promptName: "Vibrato Speed",
			promptDesc: [
				"This setting controls the speed your instrument will vibrato at, just like the slider.",
				"A setting of $LO means there will be no oscillation, and vibrato will be disabled. Higher settings will increase the speed, up to a dramatic trill at the max value, $HI.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "vibrato delay",
			pianoName: "Vibrato Delay",
			maxRawVol: 50,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.vibrato,
			maxIndex: 0,
			promptName: "Vibrato Delay",
			promptDesc: [
				"This setting controls the amount of time vibrato will be held off for before triggering for every new note, just like the slider.",
				"A setting of $LO means there will be no delay. A setting of 24 corresponds to one full beat of delay. As a sole exception to this scale, setting delay to $HI will completely disable vibrato (as if it had infinite delay).",
				"[OVERWRITING] [$LO - $HI] [beats ÷24]",
			],
		},
		{
			name: "arp speed",
			pianoName: "Arp Speed",
			maxRawVol: 50,
			newNoteVol: 12,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.chord,
			maxIndex: 0,
			promptName: "Arpeggio Speed",
			promptDesc: [
				"This setting controls the speed at which your instrument's chords arpeggiate, just like the arpeggio speed slider.",
				"Each setting corresponds to a different speed, from the slowest to the fastest. The speeds are listed below.",
				"[0-4]: x0, x1/16, x⅛, x⅕, x¼,",
				"[5-9]: x⅓, x⅖, x½, x⅔, x¾,",
				"[10-14]: x⅘, x0.9, x1, x1.1, x1.2,",
				"[15-19]: x1.3, x1.4, x1.5, x1.6, x1.7,",
				"[20-24]: x1.8, x1.9, x2, x2.1, x2.2,",
				"[25-29]: x2.3, x2.4, x2.5, x2.6, x2.7,",
				"[30-34]: x2.8, x2.9, x3, x3.1, x3.2,",
				"[35-39]: x3.3, x3.4, x3.5, x3.6, x3.7,",
				"[40-44]: x3.8, x3.9, x4, x4.15, x4.3,",
				"[45-50]: x4.5, x4.8, x5, x5.5, x6, x8",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "pan delay",
			pianoName: "Pan Delay",
			maxRawVol: 20,
			newNoteVol: 10,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.panning,
			maxIndex: 0,
			promptName: "Panning Delay",
			promptDesc: [
				"This setting controls the delay applied to panning for your instrument, just like the pan delay slider.",
				"With more delay, the panning effect will generally be more pronounced. $MID is the default value, whereas $LO will remove any delay at all. No delay can be desirable for chiptune songs.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "reset arp",
			pianoName: "Reset Arp",
			maxRawVol: 1,
			newNoteVol: 1,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.chord,
			maxIndex: 0,
			promptName: "Reset Arpeggio",
			promptDesc: [
				"This setting functions a little different from most. Wherever a note is placed, the arpeggio of this instrument will reset at the very start of that note. This is most noticeable with lower arpeggio speeds. The lengths and values of notes for this setting don't matter, just the note start times.",
				"This mod can be used to sync up your apreggios so that they always sound the same, even if you are using an odd-ratio arpeggio speed or modulating arpeggio speed.",
				"[$LO - $HI]",
			],
		},
		{
			name: "eq filter",
			pianoName: "EQFlt",
			maxRawVol: 10,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "EQ Filter",
			promptDesc: [
				"This setting controls a few separate things for your instrument's EQ filter.",
				"When the option 'morph' is selected, your modulator values will indicate a sub-filter index of your EQ filter to 'morph' to over time. For example, a change from 0 to 1 means your main filter (default) will morph to sub-filter 1 over the specified duration. You can shape the main filter and sub-filters in the large filter editor ('+' button). If your two filters' number, type, and order of filter dots all match up, the morph will happen smoothly and you'll be able to hear them changing. If they do not match up, the filters will simply jump between each other.",
				"Note that filters will morph based on endpoints in the pattern editor. So, if you specify a morph from sub-filter 1 to 4 but do not specifically drag in new endpoints for 2 and 3, it will morph directly between 1 and 4 without going through the others.",
				"If you target Dot X or Dot Y, you can finely tune the coordinates of a single dot for your filter. The number of available dots to choose is dependent on your main filter's dot count.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "note filter",
			pianoName: "N.Flt",
			maxRawVol: 10,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.noteFilter,
			maxIndex: 0,
			promptName: "Note Filter",
			promptDesc: [
				"This setting controls a few separate things for your instrument's note filter.",
				"When the option 'morph' is selected, your modulator values will indicate a sub-filter index of your note filter to 'morph' to over time. For example, a change from 0 to 1 means your main filter (default) will morph to sub-filter 1 over the specified duration. You can shape the main filter and sub-filters in the large filter editor ('+' button). If your two filters' number, type, and order of filter dots all match up, the morph will happen smoothly and you'll be able to hear them changing. If they do not match up, the filters will simply jump between each other.",
				"Note that filters will morph based on endpoints in the pattern editor. So, if you specify a morph from sub-filter 1 to 4 but do not specifically drag in new endpoints for 2 and 3, it will morph directly between 1 and 4 without going through the others.",
				"If you target Dot X or Dot Y, you can finely tune the coordinates of a single dot for your filter. The number of available dots to choose is dependent on your main filter's dot count.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "bit crush",
			pianoName: "Bitcrush",
			maxRawVol: Config.bitcrusherQuantizationRange - 1,
			newNoteVol: Math.round(Config.bitcrusherQuantizationRange / 2),
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.bitcrusher,
			maxIndex: 0,
			promptName: "Instrument Bit Crush",
			promptDesc: [
				"This setting controls the bit crush of your instrument, just like the bit crush slider.",
				"At a value of $LO, no bit crush will be applied. This increases and the bit crush effect gets more noticeable up to the max value, $HI.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "freq crush",
			pianoName: "Freq Crush",
			maxRawVol: Config.bitcrusherFreqRange - 1,
			newNoteVol: Math.round(Config.bitcrusherFreqRange / 2),
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.bitcrusher,
			maxIndex: 0,
			promptName: "Instrument Frequency Crush",
			promptDesc: [
				"This setting controls the frequency crush of your instrument, just like the freq crush slider.",
				"At a value of $LO, no frequency crush will be applied. This increases and the frequency crush effect gets more noticeable up to the max value, $HI.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "echo",
			pianoName: "Echo",
			maxRawVol: Config.echoSustainRange - 1,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.echo,
			maxIndex: 0,
			promptName: "Instrument Echo Sustain",
			promptDesc: [
				"This setting controls the echo sustain (echo loudness) of your instrument, just like the echo slider.",
				"At $LO, your instrument will have no echo sustain and echo will not be audible. Echo sustain increases and the echo effect gets more noticeable up to the max value, $HI.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "echo delay",
			pianoName: "Echo Delay",
			maxRawVol: Config.echoDelayRange,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.echo,
			maxIndex: 0,
			promptName: "Instrument Echo Delay",
			promptDesc: [
				"This setting controls the echo delay of your instrument, just like the echo delay slider.",
				"At $LO, your instrument will have very little echo delay, and this increases up to 2 beats of delay at $HI.",
				"[OVERWRITING] [$LO - $HI] [~beats ÷12]",
			],
		},
		{
			name: "chorus",
			pianoName: "Chorus",
			maxRawVol: Config.chorusRange - 1,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.chorus,
			maxIndex: 0,
			promptName: "Instrument Chorus",
			promptDesc: [
				"This setting controls the chorus strength of your instrument, just like the chorus slider.",
				"At $LO, the chorus effect will be disabled. The strength of the chorus effect increases up to the max value, $HI.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "eq filt cut",
			pianoName: "EQFlt Cut",
			maxRawVol: Config.filterSimpleCutRange - 1,
			newNoteVol: Config.filterSimpleCutRange - 1,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "EQ Filter Cutoff Frequency",
			promptDesc: [
				"This setting controls the filter cut position of your instrument, just like the filter cut slider.",
				"This setting is roughly analagous to the horizontal position of a single low-pass dot on the advanced filter editor. At lower values, a wider range of frequencies is cut off.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "eq filt peak",
			pianoName: "EQFlt Peak",
			maxRawVol: Config.filterSimplePeakRange - 1,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "EQ Filter Peak Gain",
			promptDesc: [
				"This setting controls the filter peak position of your instrument, just like the filter peak slider.",
				"This setting is roughly analagous to the vertical position of a single low-pass dot on the advanced filter editor. At lower values, the cutoff frequency will not be emphasized, and at higher values you will hear emphasis on the cutoff frequency.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "note filt cut",
			pianoName: "N.Flt Cut",
			maxRawVol: Config.filterSimpleCutRange - 1,
			newNoteVol: Config.filterSimpleCutRange - 1,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.noteFilter,
			maxIndex: 0,
			promptName: "Note Filter Cutoff Frequency",
			promptDesc: [
				"This setting controls the filter cut position of your instrument, just like the filter cut slider.",
				"This setting is roughly analagous to the horizontal position of a single low-pass dot on the advanced filter editor. At lower values, a wider range of frequencies is cut off.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "note filt peak",
			pianoName: "N.Flt Peak",
			maxRawVol: Config.filterSimplePeakRange - 1,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.noteFilter,
			maxIndex: 0,
			promptName: "Note Filter Peak Gain",
			promptDesc: [
				"This setting controls the filter peak position of your instrument, just like the filter peak slider.",
				"This setting is roughly analagous to the vertical position of a single low-pass dot on the advanced filter editor. At lower values, the cutoff frequency will not be emphasized, and at higher values you will hear emphasis on the cutoff frequency.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "pitch shift",
			pianoName: "Pitch Shift",
			maxRawVol: Config.pitchShiftRange - 1,
			newNoteVol: Config.pitchShiftCenter,
			forSong: false,
			convertRealFactor: -Config.pitchShiftCenter,
			associatedEffect: EffectType.pitchShift,
			maxIndex: 0,
			promptName: "Pitch Shift",
			promptDesc: [
				"This setting controls the pitch offset of your instrument, just like the pitch shift slider.",
				"At $MID your instrument will have no pitch shift. This increases as you decrease toward $LO pitches (half-steps) at the low end, or increases towards +$HI pitches at the high end.",
				"[OVERWRITING] [$LO - $HI] [pitch]",
			],
		},
		{
			name: "sustain",
			pianoName: "Sustain",
			maxRawVol: Config.stringSustainRange - 1,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Picked String Sustain",
			promptDesc: [
				"This setting controls the sustain of your picked string instrument, just like the sustain slider.",
				"At $LO, your instrument will have minimum sustain and sound 'plucky'. This increases to a more held sound as your modulator approaches the maximum, $HI.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "mix volume",
			pianoName: "Mix Vol.",
			maxRawVol: Config.volumeRange,
			newNoteVol: Math.ceil(Config.volumeRange / 2),
			forSong: false,
			convertRealFactor: Math.ceil(-Config.volumeRange / 2.0),
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Mix Volume",
			promptDesc: [
				"This setting affects the volume of your instrument as if its volume slider had been moved.",
				"At $MID, an instrument's volume will be unchanged from default. This means you can still use the volume sliders to mix the base volume of instruments, since this setting and the default value work multiplicatively. The volume gradually increases up to $HI, or decreases down to mute at $LO.",
				"Unlike the 'note volume' setting, mix volume is very straightforward and simply affects the resultant instrument volume after all effects are applied.",
				"[MULTIPLICATIVE] [$LO - $HI]",
			],
		},
		{
			name: "fm slider 5",
			pianoName: "FM 5",
			maxRawVol: 15,
			newNoteVol: 15,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "FM Slider 5",
			promptDesc: [
				"This setting affects the strength of the fifth FM slider, just like the corresponding slider on your instrument.",
				"It works in a multiplicative way, so at $HI your slider will sound the same is its default value, and at $LO it will sound like it has been moved all the way to the left.",
				"For the full range of control with this mod, move your underlying slider all the way to the right.",
				"[MULTIPLICATIVE] [$LO - $HI] [%]",
			],
		},
		{
			name: "fm slider 6",
			pianoName: "FM 6",
			maxRawVol: 15,
			newNoteVol: 15,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "FM Slider 6",
			promptDesc: [
				"This setting affects the strength of the sixth FM slider, just like the corresponding slider on your instrument.",
				"It works in a multiplicative way, so at $HI your slider will sound the same is its default value, and at $LO it will sound like it has been moved all the way to the left.",
				"For the full range of control with this mod, move your underlying slider all the way to the right.",
				"[MULTIPLICATIVE] [$LO - $HI] [%]",
			],
		},
		{
			name: "decimal offset",
			pianoName: "Decimal Offset",
			maxRawVol: 99,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			invertSliderIndicator: true,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Decimal Offset",
			promptDesc: [
				"This setting controls the decimal offset that is subtracted from the pulse width; use this for creating values like 12.5 or 6.25.",
				"[$LO - $HI]",
			],
		},
		{
			name: "envelope speed",
			pianoName: "EnvelopeSpd",
			maxRawVol: 50,
			newNoteVol: 12,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Envelope Speed",
			promptDesc: [
				"This setting controls how fast all of the envelopes for the instrument play.",
				"At $LO, your instrument's envelopes will be frozen, and at values near there they will change very slowly. At 12, the envelopes will work as usual, performing at normal speed. This increases up to $HI, where the envelopes will change very quickly. The speeds are given below:",
				"[0-4]: x0, x1/16, x⅛, x⅕, x¼,",
				"[5-9]: x⅓, x⅖, x½, x⅔, x¾,",
				"[10-14]: x⅘, x0.9, x1, x1.1, x1.2,",
				"[15-19]: x1.3, x1.4, x1.5, x1.6, x1.7,",
				"[20-24]: x1.8, x1.9, x2, x2.1, x2.2,",
				"[25-29]: x2.3, x2.4, x2.5, x2.6, x2.7,",
				"[30-34]: x2.8, x2.9, x3, x3.1, x3.2,",
				"[35-39]: x3.3, x3.4, x3.5, x3.6, x3.7,",
				"[40-44]: x3.8, x3.9, x4, x4.15, x4.3,",
				"[45-50]: x4.5, x4.8, x5, x5.5, x6, x8",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "dynamism",
			pianoName: "Dynamism",
			maxRawVol: Config.supersawDynamismMax,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Supersaw Dynamism",
			promptDesc: [
				"This setting controls the supersaw dynamism of your instrument, just like the dynamism slider.",
				"At $LO, your instrument will have only a single pulse contributing. Increasing this will raise the contribution of other waves which is similar to a chorus effect. The effect gets more noticeable up to the max value, $HI.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "spread",
			pianoName: "Spread",
			maxRawVol: Config.supersawSpreadMax,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Supersaw Spread",
			promptDesc: [
				"This setting controls the supersaw spread of your instrument, just like the spread slider.",
				"At $LO, all the pulses in your supersaw will be at the same frequency. Increasing this value raises the frequency spread of the contributing waves, up to a dissonant spread at the max value, $HI.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "saw shape",
			pianoName: "Saw Shape",
			maxRawVol: Config.supersawShapeMax,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Supersaw Shape",
			promptDesc: [
				"This setting controls the supersaw shape of your instrument, just like the Saw↔Pulse slider.",
				"As the slider's name implies, this effect will give you a sawtooth wave at $LO, and a full pulse width wave at $HI. Values in between will be a blend of the two.",
				"[OVERWRITING] [$LO - $HI] [%]",
			],
		},
		{
			name: "individual envelope speed",
			pianoName: "IndvEnvSpd",
			maxRawVol: 63,
			newNoteVol: 23,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: this.maxEnvelopeCount - 1,
			promptName: "Individual Envelope Speed",
			promptDesc: [
				"This setting controls how fast the specified envelope of the instrument will play.",
				"At $LO, your the envelope will be frozen, and at values near there they will change very slowly. At 23, the envelope will work as usual, performing at normal speed. This increases up to $HI, where the envelope will change very quickly. The speeds are given below:",
				"[0-4]: x0, x0.01, x0.02, x0.03, x0.04,",
				"[5-9]: x0.05, x0.06, x0.07, x0.08, x0.09,",
				"[10-14]: x0.1, x0.2, x0.25, x0.3, x0.33,",
				"[15-19]: x0.4, x0.5, x0.6, x0.6667, x0.7,",
				"[20-24]: x0.75, x0.8, x0.9, x1, x1.25,",
				"[25-29]: x1.3333, x1.5, x1.6667, x1.75, x2,",
				"[30-34]: x2.25, x2.5, x2.75, x3, x3.5,",
				"[35-39]: x4, x4.5, x5, x5.5, x6,",
				"[40-44]: x6.5, x7, x7.5, x8, x8.5,",
				"[45-49]: x9, x9.5, x10, x11, x12",
				"[50-54]: x13, x14, x15, x16, x17",
				"[55-59]: x18, x19, x20, x24, x32",
				"[60-63]: x40, x64, x128, x256",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "song eq",
			pianoName: "Song EQ",
			maxRawVol: 10,
			newNoteVol: 0,
			forSong: true,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: 0,
			promptName: "Song EQ Filter",
			promptDesc: [
				"This setting overwrites every instrument's eq filter. You can do this in a few separate ways, similar to the per instrument eq filter modulator.",
				"When the option 'morph' is selected, your modulator values will indicate a sub-filter index of your EQ filter to 'morph' to over time. For example, a change from 0 to 1 means your main filter (default) will morph to sub-filter 1 over the specified duration. You can shape the main filter and sub-filters in the large filter editor ('+' button). If your two filters' number, type, and order of filter dots all match up, the morph will happen smoothly and you'll be able to hear them changing. If they do not match up, the filters will simply jump between each other.",
				"Note that filters will morph based on endpoints in the pattern editor. So, if you specify a morph from sub-filter 1 to 4 but do not specifically drag in new endpoints for 2 and 3, it will morph directly between 1 and 4 without going through the others.",
				"If you target Dot X or Dot Y, you can finely tune the coordinates of a single dot for your filter. The number of available dots to choose is dependent on your main filter's dot count.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "reset envelope",
			pianoName: "ResetEnv",
			maxRawVol: 1,
			newNoteVol: 1,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: this.maxEnvelopeCount - 1,
			promptName: "Reset Envelope",
			promptDesc: [
				"This setting functions a lot like the reset arp modulator. Wherever a note is placed, the envelope of this instrument at the specified index will reset at the very start of that note. ",
				"[$LO - $HI]",
			],
		},
		{
			name: "ring modulation",
			pianoName: "Ring Mod",
			maxRawVol: Config.ringModRange,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.ringModulation,
			maxIndex: 0,
			promptName: "Ring Modulation",
			promptDesc: [
				"This setting controls the Ring Modulation effect in your instrument.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "ring mod hertz",
			pianoName: "Ring Mod(Hz)",
			maxRawVol: Config.ringModHzRange,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.ringModulation,
			maxIndex: 0,
			promptName: "Ring Modulation (Hertz)",
			promptDesc: [
				"This setting controls the Hertz (Hz) used in the Ring Modulation effect in your instrument.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "granular",
			pianoName: "Granular",
			maxRawVol: Config.granularRange,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.granular,
			maxIndex: 0,
			promptName: "Granular",
			promptDesc: [
				"This setting controls the granular effect in your instrument.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "grain freq",
			pianoName: "Grain #",
			maxRawVol: Config.grainAmountsMax,
			newNoteVol: 8,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.granular,
			maxIndex: 0,
			promptName: "Grain Count",
			promptDesc: [
				"This setting controls the density of grains for the granular effect on your instrument.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "grain size",
			pianoName: "Grain Size",
			maxRawVol: Config.grainSizeMax / Config.grainSizeStep,
			newNoteVol: Config.grainSizeMin / Config.grainSizeStep,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.granular,
			maxIndex: 0,
			promptName: "Grain Size",
			promptDesc: [
				"This setting controls the grain size of the granular effect in your instrument.",
				`The number shown in the mod channel is multiplied by ${Config.grainSizeStep} to get the actual grain size.`,
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "grain range",
			pianoName: "Grain Range",
			maxRawVol: Config.grainRangeMax / Config.grainSizeStep,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.granular,
			maxIndex: 0,
			promptName: "Grain Range",
			promptDesc: [
				"This setting controls the range of values for your grain size of the granular effect in your instrument, from no variation to a lot",
				`The number shown in the mod channel is multiplied by ${Config.grainSizeStep} to get the actual grain size.`,
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "individual envelope lower bound",
			pianoName: "IndvEnvLow",
			maxRawVol: Config.perEnvelopeBoundMax * 10,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: this.maxEnvelopeCount - 1,
			promptName: "Individual Envelope Lower Bound",
			promptDesc: [
				"This setting controls the envelope lower bound",
				"At $LO, your the envelope will output an upper envelope bound to 0, and at $HI your envelope will output an upper envelope bound to 2.",
				"This settings will not work if your lower envelope bound is higher than your upper envelope bound",
			],
		},
		{
			name: "individual envelope upper bound",
			pianoName: "IndvEnvUp",
			maxRawVol: Config.perEnvelopeBoundMax * 10,
			newNoteVol: 10,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.length,
			maxIndex: this.maxEnvelopeCount - 1,
			promptName: "Individual Envelope Upper Bound",
			promptDesc: [
				"This setting controls the envelope upper bound",
				"At $LO, your the envelope will output a 0 to lower envelope bound, and at $HI your envelope will output a 2 to lower envelope bound.",
				"This settings will not work if your lower envelope bound is higher than your upper envelope bound",
			],
		},
		{
			name: "invert wave",
			pianoName: "Invert Wave",
			maxRawVol: 1,
			newNoteVol: 1,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.invertWave,
			maxIndex: 0,
			promptName: "Invert Wave",
			promptDesc: [
				"Allows you to toggle the Invert Wave effect on instruments. Value must be exactly 1 for this to take effect.",
				"[$LO - $HI]",
			],
		},
		{
			name: "phaser",
			pianoName: "Phaser",
			maxRawVol: Config.phaserMixRange,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.phaser,
			maxIndex: 0,
			promptName: "Instrument Phaser",
			promptDesc: [
				"This setting controls the Phaser Mix of your insturment, just like the Phaser slider.",
				"At $LO, your instrument will have no phaser. At $HI, it will be at maximum.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "phaser frequency",
			pianoName: "Phaser Frequency",
			maxRawVol: Config.phaserFreqRange,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.phaser,
			maxIndex: 0,
			promptName: "Phaser Frequency",
			promptDesc: [
				"This setting controls the phaser frequency of your insturment, just like the phaser freq slider.",
				"At $LO, your instrument will have no phaser freq. At $HI, it will be at maximum.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "phaser feedback",
			pianoName: "Phaser Feedback",
			maxRawVol: Config.phaserFeedbackRange,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.phaser,
			maxIndex: 0,
			promptName: "Phaser Feedback",
			promptDesc: [
				"This setting controls the phaser feedback of your insturment, just like the phaser feedback slider.",
				"At $LO, your instrument will have no phaser feedback. At $HI, it will be at maximum.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
		{
			name: "phaser stages",
			pianoName: "Phaser Stages",
			maxRawVol: Config.phaserMaxStages,
			newNoteVol: 0,
			forSong: false,
			convertRealFactor: 0,
			associatedEffect: EffectType.phaser,
			maxIndex: 0,
			promptName: "Phaser Stages",
			promptDesc: [
				"This setting controls the number of phaser stages in your insturment, just like the phaser stages slider.",
				"At $LO, your instrument will have no phaser stages. At $HI, it will be at maximum.",
				"[OVERWRITING] [$LO - $HI]",
			],
		},
	]);
}

export {
	calculateRingModHertz,
	drawNoiseSpectrum,
	getArpeggioPitchIndex,
	getDrumWave,
	getPulseWidthRatio,
} from "./synth-math-utils";
export { centerAndNormalizeWave, centerWave } from "./utils";
